import React from "react";
import * as ReactDOM from "react-dom";
import { EmptyState, Icon, clickable, useLocalPref, autoSchedule, computeQuoteView, quoteMarginOf, emitAutomation, quotePayments, HubLinkButton, sendBalanceReminder, PurchaseOrderModal, poFromWO, poList, poDerivedStatus, PO_STATUS, LogMaterialUse, notify, confirmDialog, JobTimeline } from "./registry";
import { VerticalChip } from "./registry";   // "Concrete" on a job from the other service; nothing on a fence job
import { locatesRequired, permitRequired, verticalOf, jobTypesOf, brandOf } from "./lib/verticals";   // a concrete job digs no post hole: no 811 ticket, no permit (plan §5.4); the review ask under the job's brand (§5.7)
import { concreteReadinessFor } from "./lib/concrete.steps";   // the concrete job's own readiness list, composed from its job types
import { storeCan } from "./lib/roles";
import { CrewBuildSheet } from "./crewsheet";
import { LiftLogSummary } from "./concretecrew";   // the crew's lift log, read on the job card (plan §5.3)
import { JOB_SHORT as CX_JOB_SHORT, liftTotals } from "./lib/concrete.actuals";   // the warranty the job carries, claims and re-visits (§13 item 19); the lift log's pounds → foam sets drawn from stock (§13 item 18)
import { poSuppliersOf } from "./registry";   // one purchase order per supplier when the recipe names more than one (purchaseorders.tsx)
import { moveJob } from "./registry";   // "Move to <day>" off the weather chip (calendar.tsx; §13 item 13)
import { nextGoodDay, crewBusyOn, weatherFlagLive, dayLabel as wxDayLabel } from "./lib/schedulewx";
import { plansFromJob, mergePlans, planLabel, CARE_LABEL, everyLabel, isCareType } from "./lib/careplans";   // the care plan a finished concrete job starts (§13 item 15)
import { ensureLeaflet } from "./lib/leaflet";
import "./lib/weather";   // registers window.VFP_WEATHER (install-day forecast)
import { localYmd } from "./lib/dates";
import { toCsv, downloadCsv } from "./lib/csv";   // the Jobs Sheet export
import { newId } from "./lib/ids";
import { coHold, confirmCoHold } from "./lib/changeorders";
import { outboxConfigured } from "./lib/fieldsync";   // FW-007: crew updates as queued ops
import { offlineText, opLabel, hhmm, CHECK_LIST, CONCRETE_CHECK_LIST, CROSS_SELL_CHECKS } from "./lib/outbox";
import { WI_COUNTIES, WI_STATEWIDE, permitSearchUrl, countyZoningSearchUrl, detectCountyForWo, permitBlocks, permitBlockMessage } from "./lib/permits";   // FW-025: permitBlocks is the hold scheduling checks
// WO-03: one transition service. Every path that advances a job goes through it,
// so the stage bar can't reach a state the Schedule button would have blocked.
import { blockersFor, gateTransition, confirmCompletion } from "./lib/transitions";
// the supplies list is a plan you can change, and the gap against the job shows
import { supplyLines, supplySummary, withWaste, tidyOverrides } from "./lib/supplies";
import { computeConcreteQuote, rateCardsOf } from "./lib/engine.concrete";   // a concrete job's supply list comes from the rate cards' recipes
// WO-04: permit and materials are states, not checkboxes a file picker can set
import { permitState, permitPatch, permitDone, PERMIT_STATES, materialsState } from "./lib/readiness";
import { uploadPhoto, toggleWeb, docUploadsConfigured, storeDocument, documentUrl } from "./lib/media";   // job photos + permit copies as files
/* ============================================================
   Fence Works — Work Orders (operations pipeline)
   Lifecycle: Estimate → Paid → Needs Scheduling → Scheduled
   (→ Google Calendar) → In Progress → Completed.
   ============================================================ */
const WO_money0 = window.VFP.money0, WO_money = window.VFP.money;

const WO_STAGES = [
  { id: "materials",   label: "Order Supplies",   short: "Order Supplies",  color: "#f0a35e", icon: "box" },
  { id: "unscheduled", label: "Needs Scheduling", short: "Needs Scheduling", color: "#f5c451", icon: "calendar" },
  { id: "scheduled",   label: "Scheduled",        short: "Scheduled",       color: "#4aa8ff", icon: "calendar" },
  { id: "in_progress", label: "In Progress",      short: "In Progress",     color: "#8b7cf6", icon: "bolt" },
  { id: "completed",   label: "Completed",        short: "Completed",       color: "#34d399", icon: "check" },
];
const STAGE = (id) => WO_STAGES.find(s => s.id === id) || WO_STAGES[0];

function crewRate(crew) { return window.VFP.crewLoadedRate(crew); }
function woLaborCost(wo, crews) { return (Number(wo.crewHours) || 0) * crewRate((crews || []).find(c => c.id === wo.crewId)); }
function crewDays(wo, crew) {
  if (!crew || !wo.crewHours) return 0;
  const cap = window.VFP.crewHeadcount(crew) * (Number(crew.hoursPerDay) || 8);
  return cap > 0 ? Math.max(1, Math.ceil((Number(wo.crewHours) || 0) / cap)) : 0;
}

/* ---- time tracking / job-costing actuals --------------------------------
   timeLogs live on the work order as wo.timeLogs: [{ id, hours, date, crewId?, note? }]
   and persist through store.updateWorkOrder (a shallow merge). A work order with
   no timeLogs behaves exactly as before. ---------------------------------- */
function woTimeLogs(wo) { return Array.isArray(wo && wo.timeLogs) ? wo.timeLogs : []; }
function actualHours(wo) { return woTimeLogs(wo).reduce((a, e) => a + (Number(e.hours) || 0), 0); }

/* Estimated labor for a job, preferring the linked quote's engine breakdown
   (crew-hours + cost) and falling back to the work order's own crew-hours
   estimate × the assigned crew's loaded rate. Never recomputes pricing — it
   reads computeQuoteView's existing laborHours/laborCost. */
function woEstLabor(wo, store, crews) {
  const crew = (crews || []).find(c => c.id === wo.crewId);
  const rate = crewRate(crew);
  const q = wo.quoteId ? (store.quotes || []).find(x => x.id === wo.quoteId) : null;
  if (q) {
    try {
      const view = computeQuoteView(q, store.settings || {});
      const qq = view && view.quote;
      if (qq && (Number(qq.laborHours) || 0) > 0) {
        const hours = Number(qq.laborHours) || 0;
        // prefer the quote's own labor cost; if absent, value the hours at this crew's rate
        const cost = (Number(qq.laborCost) || 0) > 0 ? Number(qq.laborCost) : hours * rate;
        return { hours, cost, source: "quote" };
      }
    } catch (e) { /* fall through to the work-order estimate */ }
  }
  const hours = Number(wo.crewHours) || 0;
  return { hours, cost: hours * rate, source: hours > 0 ? "wo" : "none" };
}

/* ---- job expenses (materials receipts, dump fees, rentals, subs …) ----
   Stored on the work order as wo.expenses:[{id,amount,category,vendor,date,by,note}].
   Any team member on the shared workspace can add them; they feed the actual
   side of the job P&L below. Absent/empty behaves exactly as before. */
/* "12 hours, 3 expenses and 8 photos" — plain English for a warning that has to
   land in one read. */
function listWords(xs: string[]): string {
  if (xs.length <= 1) return xs[0] || "";
  return xs.slice(0, -1).join(", ") + " and " + xs[xs.length - 1];
}
function woExpenses(wo) { return Array.isArray(wo && wo.expenses) ? wo.expenses : []; }

/* Not every receipt the crew hands in belongs to the job they were standing on.
   A box of fasteners does. Lunch doesn't — that's overhead. A step ladder
   doesn't either — you keep the ladder. Charging those to the job makes one
   job look unprofitable and hides the real cost of running the business, so an
   expense carries a SCOPE as well as a category:

     job       charged to this job — it moves the job's P&L
     tool      you keep it; tracked here, never charged to one job
     overhead  cost of doing business (lunch, office, fees) — same

   Entries logged before this existed have no scope and count as job cost,
   which is exactly how they were already being counted. */
const EXPENSE_SCOPES: { id: string; label: string; short: string; blurb: string }[] = [
  { id: "job",      label: "This job",   short: "Job cost", blurb: "Materials, dump fees, a sub. Moves this job’s profit." },
  { id: "tool",     label: "A tool",     short: "Tool",     blurb: "You keep it. Never charged to one job." },
  { id: "overhead", label: "Overhead",   short: "Overhead", blurb: "Lunch, office, fees. Cost of doing business." },
];
function expScope(e: any): string { const s = e && e.scope; return s === "tool" || s === "overhead" ? s : "job"; }
function scopeLabel(id: string): string { const f = EXPENSE_SCOPES.find(x => x.id === id); return f ? f.short : "Job cost"; }
function sumExp(xs: any[]) { return xs.reduce((a, e) => a + (Number(e.amount) || 0), 0); }
/* what the job actually cost — scope "job" only. This is the number the P&L uses. */
function expensesTotal(wo) { return sumExp(woExpenses(wo).filter(e => expScope(e) === "job")); }
/* logged on this job but not its cost — shown, never charged */
function offJobTotal(wo) { return sumExp(woExpenses(wo).filter(e => expScope(e) !== "job")); }
/* everything on the ticket, for "here is what you're about to delete" */
function allExpensesTotal(wo) { return sumExp(woExpenses(wo)); }

const EXPENSE_CATS = ["Materials", "Tools", "Equipment / rental", "Dump / disposal", "Subcontractor", "Permits / fees", "Fuel / travel", "Meals / crew", "Other"];
/* picking a category usually settles the scope, so the form follows along and
   the person only corrects it when they mean something unusual */
const CAT_SCOPE: Record<string, string> = { "Tools": "tool", "Meals / crew": "overhead" };

/* ---- the rep's commission, as a cost of the job ----
   The same rule Sales Team → Commission payouts pays on (business.tsx
   CommissionsCard): the rep credited on the quote, matched to the roster in
   settings.salesReps by name (or email), at their commissionPct of the
   quote's total. A rep who isn't on the roster, or has no %, costs nothing
   here — exactly what the payouts card says they're owed. A job with no
   quote (imported history) uses its own rep and value. */
function repCommission(wo, q, settings) {
  const rep = String((q && q.rep) || (wo && wo.rep) || "").trim();
  if (!rep) return { rep: "", pct: 0, amount: 0 };
  const key = rep.toLowerCase();
  const r = ((settings && settings.salesReps) || []).find((x: any) => x && (String(x.name || "").trim().toLowerCase() === key
    || (!!x.email && String(x.email).trim().toLowerCase() === key)));
  const pct = r ? Math.max(0, Number(r.commissionPct) || 0) : 0;
  const base = q ? Number(q.total) || 0 : Number(wo && wo.total) || 0;
  return { rep: (r && r.name) || rep, pct, amount: Math.round(base * pct) / 100 };
}

/* Imported history (Import & export → jobs CSV): a job that ran before Fence
   Works carries what it cost as wo.importedActuals { laborCost?, materialCost?,
   hours? }. Those stand in for time logs and receipts only while the job has
   neither — anything logged in the app wins. null = nothing usable recorded. */
function importedActualsOf(wo) {
  const ia = wo && wo.importedActuals;
  if (!ia || typeof ia !== "object") return null;
  const num = (v: any) => (v == null || v === "" || !isFinite(+v) ? null : Math.max(0, +v));
  const out = { laborCost: num(ia.laborCost), materialCost: num(ia.materialCost), hours: num(ia.hours) };
  return out.laborCost != null || out.materialCost != null || out.hours != null ? out : null;
}

/* ---- per-job profit & loss: quoted (engine) vs actual (logged) ----
   Revenue is the quote's PRE-TAX subtotal less any discount (tax is pass-through, never revenue);
   quoted cost comes straight from the engine breakdown; actual cost = logged
   labor hours × the crew's loaded rate + job-scope expenses — or, for an
   imported job with nothing logged, its recorded costs. The rep's commission
   is a cost on BOTH sides, so quoted and actual profit are after commission
   and the variance between them is still labor + materials. Never recomputes
   pricing — reads computeQuoteView, same as woEstLabor. A job with no quote
   has no quoted side (quotedProfit null), never a made-up one. */
function woPL(wo, store, crews) {
  const settings = store.settings || {};
  const logged = woTimeLogs(wo).length > 0 || woExpenses(wo).length > 0;
  const imported = logged ? null : importedActualsOf(wo);
  // an imported job rarely names a crew: its hours are valued at the default crew's rate
  const crew = (crews || []).find(c => c.id === wo.crewId) || (imported ? (crews || []).find(c => c.id === settings.activeCrewId) : null);
  const rate = crewRate(crew);
  const est = woEstLabor(wo, store, crews);
  let revenue = Number(wo.total) || 0;   // fallback: job value (may include tax)
  let quotedMaterial = 0, quotedCost = 0, quotedProfit = null, quotedMargin = null, grossProfit = null, haveQuote = false;
  const q = wo.quoteId ? (store.quotes || []).find(x => x.id === wo.quoteId) : null;
  if (q) {
    try {
      const view = computeQuoteView(q, settings);
      const qq = view && view.quote;
      if (qq) {
        // pre-tax and after any discount — the quote screen's own margin line
        const m = quoteMarginOf(qq);
        haveQuote = true;
        if (m.revenue > 0) revenue = m.revenue;
        quotedMaterial = Number(qq.materialTotal) || 0;
        quotedCost = m.cost;
        grossProfit = revenue - m.cost;
      }
    } catch (e) { /* fall back to wo.total revenue with no quoted breakdown */ }
  }
  const comm = repCommission(wo, q, settings);
  const commission = comm.amount;
  if (haveQuote) {
    quotedProfit = grossProfit - commission;
    quotedMargin = revenue > 0 ? (quotedProfit / revenue) * 100 : 0;
  }
  let actualHrs, actualLabor, expT;
  if (imported) {
    actualHrs = imported.hours || 0;
    actualLabor = imported.laborCost != null ? imported.laborCost : actualHrs * rate;
    expT = imported.materialCost || 0;
  } else {
    actualHrs = actualHours(wo);
    actualLabor = actualHrs * rate;
    expT = expensesTotal(wo);
  }
  const hasActual = imported ? true : actualHrs > 0 || expT > 0;
  const actualSource = imported ? "imported" : hasActual ? "logged" : "none";
  const actualCost = actualLabor + expT + commission;
  const actualProfit = revenue - actualCost;
  const actualMargin = revenue > 0 ? (actualProfit / revenue) * 100 : 0;
  // everything the quote costed that ISN'T labor (material + gate + disposal +
  // permit + travel) so Labor + Non-labor reconciles to the quoted cost, and
  // lines up with logged expenses on the actual side.
  const quotedNonLabor = haveQuote ? Math.max(0, quotedCost - (Number(est.cost) || 0)) : 0;
  const quotedTotalCost = haveQuote ? quotedCost + commission : null;
  // hours clocked but the material bill never logged reads as a fat margin that
  // isn't real — flag it when what's logged is under half what the quote costed
  const materialsShort = hasActual && !imported && haveQuote && quotedNonLabor > 0 && expT < quotedNonLabor * 0.5;
  return { rate, est, revenue, haveQuote, quotedMaterial, quotedNonLabor, quotedCost, quotedTotalCost, grossProfit, quotedProfit, quotedMargin,
    commission, commissionPct: comm.pct, rep: comm.rep,
    actualHrs, actualLabor, expT, actualCost, actualProfit, actualMargin, hasActual, actualSource, materialsShort };
}

/* Where a job's money stands, from its quote's ledger — the same depositPaid +
   recorded payments every paid/due figure reads (quotePayments):
     paid          nothing left owing
     deposit       money is in and the job isn't finished — the rest isn't due yet
     balance       the job is complete and money is still owed
     unpaid        nothing collected
   null when there is no quote to read a ledger from (imported history, a
   manual work order) — the sheet says "—", never a status it can't know. */
function woPayStatus(wo, store) {
  const q = wo && wo.quoteId ? (store.quotes || []).find(x => x.id === wo.quoteId) : null;
  if (!q) return null;
  const total = Number(q.total) || 0;
  if (!(total > 0)) return null;
  const paid = quotePayments(q).paid;
  const balance = Math.max(0, total - paid);
  const id = balance <= 0.5 ? "paid" : paid <= 0.005 ? "unpaid" : wo.status === "completed" ? "balance" : "deposit";
  const label = ({ paid: "Paid", deposit: "Deposit paid", balance: "Balance due", unpaid: "Unpaid" } as any)[id];
  return { id, label, paid, balance: id === "paid" ? 0 : balance };
}
/* balance still owed on a job's linked quote — the "Unpaid" tail of the
   pipeline. null when there's no quote to read a ledger from. */
function woBalance(wo, store) {
  const q = wo.quoteId ? (store.quotes || []).find(x => x.id === wo.quoteId) : null;
  if (!q) return null;
  return Math.max(0, (Number(q.total) || 0) - quotePayments(q).paid);
}
/* won quotes with no job yet — the "Sold" head of the pipeline (deposit
   hasn't cleared; a paid deposit auto-creates the work order). */
function soldQuotesOf(store) {
  const wos = store.workOrders || [], projects = store.projects || [];
  return (store.quotes || []).filter(q => (q.status === "signed" || q.status === "paid")
    && !wos.some(w => w.quoteId === q.id) && !projects.some(p => p.fromQuote === q.id));
}
const DAY_MS = 86400000;
const daysSince = (ts) => ts ? Math.floor((Date.now() - ts) / DAY_MS) : 0;
const daysUntilStr = (dateStr) => { if (!dateStr) return null; const t0 = new Date(); t0.setHours(0, 0, 0, 0); return Math.round((new Date(dateStr + "T00:00:00").getTime() - t0.getTime()) / DAY_MS); };

/* stalled-job detector — the "action items when work stalls" pass. Pure read
   of what's already on each job; every row carries a jump to fix it. */
function actionItemsOf(store) {
  const items = [];
  soldQuotesOf(store).forEach(q => {
    const age = daysSince(q.lastSentAt || q.createdAt);
    // a pay-on-completion quote (its own deposit is 0) has nothing pending — the app makes its work order on signing (app.tsx; §13 item 16)
    if (age >= 3 && !(q.depositPct != null && Number(q.depositPct) === 0)) items.push({ sev: "warn", icon: "dollar", key: "dep-" + q.id, msg: `${q.customer || "Customer"} signed ${age}d ago — deposit still pending`, btn: "Open quote", go: ["quote", { quoteId: q.id }] });
  });
  (store.workOrders || []).forEach(wo => {
    const who = wo.customer || wo.title || "Job";
    if (wo.status === "materials" && !wo.suppliesOrdered && daysSince(wo.createdAt) >= 3)
      items.push({ sev: "warn", icon: "box", key: "mat-" + wo.id, msg: `${who}: supplies not ordered (${daysSince(wo.createdAt)}d in Order Supplies)`, btn: "Open job", wo });
    if (wo.status === "unscheduled" && wo.scheduleRequest && !wo.scheduledStart)
      items.push({ sev: "crit", icon: "calendar", key: "req-" + wo.id, msg: `${who} picked the week of ${fmtDate(wo.scheduleRequest.start)} ${requestVia(wo.scheduleRequest)} — confirm it`, btn: "Confirm", wo });
    // a phone-captured estimate request whose time we couldn't parse: nothing
    // was booked and nobody checked availability, so it needs a human NOW, not
    // after the five-day nudge below (FW-013 / L08)
    else if (wo.status === "unscheduled" && wo.needsConfirmation && !wo.scheduledStart)
      items.push({ sev: "crit", icon: "phone", key: "req-" + wo.id, msg: `${who} asked for an estimate${wo.requestedWhenText ? ` — "${wo.requestedWhenText}"` : ""} — call them back to set the time`, btn: "Open job", wo });
    else if (wo.status === "unscheduled" && daysSince(wo.createdAt) >= 5)
      items.push({ sev: "warn", icon: "calendar", key: "sch-" + wo.id, msg: `${who}: still needs an install date`, btn: "Open job", wo });
    const du = daysUntilStr(wo.scheduledStart);
    if (locatesRequired(wo) && (wo.status === "scheduled" || wo.status === "in_progress") && du != null && du <= 3 && !((wo.readiness || {}).diggers))
      items.push({ sev: "crit", icon: "phone", key: "811-" + wo.id, msg: `${who}: 811 locate not marked — starts ${du <= 0 ? "today" : "in " + du + "d"}`, btn: "Open job", wo });
    if (wo.status === "in_progress" && wo.scheduledEnd && daysUntilStr(wo.scheduledEnd) <= -2)
      items.push({ sev: "warn", icon: "bolt", key: "run-" + wo.id, msg: `${who}: ${-daysUntilStr(wo.scheduledEnd)}d past its end date and still in progress`, btn: "Open job", wo });
    if (wo.status === "completed") {
      const bal = woBalance(wo, store);
      if (bal != null && bal > 0.5) {
        // auto-chase cadence: first nudge a day after completion, then weekly;
        // after 4 sends the play is a phone call, not another email
        const q = (store.quotes || []).find(x => x.id === wo.quoteId);
        const rem = (q && q.reminders) || [];
        const anchor = rem.length ? Math.max(...rem.map(r => r.at || 0)) : (wo.completedAt || wo.createdAt || 0);
        const due = q && rem.length < 4 && daysSince(anchor) >= (rem.length === 0 ? 1 : 7);
        items.push({ sev: "crit", icon: "dollar", key: "bal-" + wo.id,
          msg: `${who}: complete — collect the ${WO_money0(bal)} balance${rem.length ? ` · ${rem.length} reminder${rem.length > 1 ? "s" : ""} sent` : ""}${rem.length >= 4 ? " — time to CALL" : ""}`,
          btn: "Open quote", go: wo.quoteId ? ["quote", { quoteId: wo.quoteId }] : null, wo: wo.quoteId ? null : wo,
          remindQ: due ? q : null });
      }
    }
  });
  return items.sort((a, b) => (b.sev === "crit" ? 1 : 0) - (a.sev === "crit" ? 1 : 0));
}

function addDaysStr(dateStr, n) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00"); d.setDate(d.getDate() + n);
  return localYmd(d);
}
function fmtDate(s) { if (!s) return ""; const d = new Date(s + "T00:00:00"); return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }); }

/* Where a schedule request came from. The copy used to be hardcoded to "on
   their hub"; the AI phone line now captures requests too (FW-013 / L08), and
   whether a human took the call changes how you follow it up. */
function requestVia(req: any) {
  const via = req && req.via;
  if (via === "ai-call") return "on the phone with the AI line";
  if (via === "call") return "on the phone";
  return "on their hub";
}

/* ---- bill of materials for a work order, computed from its linked quote/project ---- */
function woBOM(wo, store) {
  if (!wo) return [];
  const q = (store.quotes || []).find(x => x.id === wo.quoteId);
  const proj = (store.projects || []).find(p => p.id === wo.projectId);
  const inputs = (q && q.inputs) || (proj && proj.snapshot) || null;
  const productId = (q && q.productId) || (proj && proj.productId);
  // a concrete job: the recipes' supply list, in the units you ORDER — packs
  // when the card says how it's bought (two 100 lb foam sets), else the job's
  // own units rounded up. No product behind it; the rate cards are the catalog.
  if (inputs && inputs.concrete) {
    try {
      const r = computeConcreteQuote(inputs.concrete, rateCardsOf(store.settings), store.settings || {});
      // the SKU, the supplier and the unit cost ride along (§13 item 18): the
      // purchase order lines carry them to the supplier, split per supplier
      const extra = (m: any, unitCost: any) => ({ ...(m.sku ? { sku: String(m.sku) } : {}), ...(m.supplier ? { supplier: String(m.supplier) } : {}), ...(unitCost != null && unitCost > 0 ? { unitCost: Math.round(unitCost * 100) / 100 } : {}) });
      return (r.mat || []).map((m: any) => (m.packs != null && m.packLabel
        ? { label: `${m.label} — ${String(m.packLabel).replace(/^[\d.,]+\s*×\s*/, "")}`, qty: m.packs, ...extra(m, m.packs > 0 && Number(m.packCost) > 0 ? Number(m.packCost) / m.packs : null) }
        : { label: `${m.label} (${m.uom})`, qty: Math.ceil(Number(m.qty) || 0), ...extra(m, Number(m.unit) > 0 ? Number(m.unit) : null) }));
    } catch (e) { return []; }
  }
  if (!inputs || !productId) return [];
  const product = (window.VFP.PRODUCTS || []).find(p => p.id === productId);
  if (!product) return [];
  try {
    if (inputs.fences && inputs.fences.length) {
      const agg = {};
      inputs.fences.forEach(f => {
        const qq = window.VFP.computeQuote(f.snapshot || f, product, store.settings);
        (qq.mat || []).forEach(m => { agg[m.label] = (agg[m.label] || 0) + m.qty; });
      });
      return Object.keys(agg).map(label => ({ label, qty: Math.round(agg[label]) }));
    }
    const qq = window.VFP.computeQuote(inputs, product, store.settings);
    return (qq.mat || []).map(m => ({ label: m.label, qty: Math.round(m.qty) }));
  } catch (e) { return []; }
}

/* ---- self-scheduling: capacity-checked install windows -------------------
   Up to three Monday-start weeks the customer can pick from on their hub.
   A week qualifies when it's after the materials ETA and the crews' booked
   hours leave room (same capacity math as the calendar's CapacityStrip).
   Stamped onto wo.offeredWindows; the customer's pick comes back as
   wo.scheduleRequest for a one-tap confirm. */
function offerWindows(store, wo, jobDays) {
  const crews = (store.settings && store.settings.crews) || [];
  const cap = crews.reduce((a, c) => a + (window.VFP.crewHeadcount ? window.VFP.crewHeadcount(c) : (Number(c.size) || 2)) * (Number(c.hoursPerDay) || 8) * 5, 0);
  const span = Math.max(1, Number(jobDays) || 1);
  const pad2 = (n) => String(n).padStart(2, "0");
  const ymdOf = (d) => d.getFullYear() + "-" + pad2(d.getMonth() + 1) + "-" + pad2(d.getDate());
  const mon = new Date(); mon.setHours(0, 0, 0, 0);
  mon.setDate(mon.getDate() + (((8 - mon.getDay()) % 7) || 7));   // next Monday, never today
  const floor = wo.materialEta ? new Date(wo.materialEta + "T00:00:00") : null;
  const wins = [];
  for (let i = 0; i < 10 && wins.length < 3; i++) {
    const start = new Date(mon); start.setDate(start.getDate() + i * 7);
    if (floor && start.getTime() <= floor.getTime()) continue;
    const wkEnd = new Date(start); wkEnd.setDate(wkEnd.getDate() + 7);
    const booked = (store.workOrders || []).reduce((a, o) => {
      if (!o.scheduledStart || o.status === "completed" || o.id === wo.id) return a;
      const t = new Date(o.scheduledStart + "T00:00:00").getTime();
      return t >= start.getTime() && t < wkEnd.getTime() ? a + (Number(o.crewHours) || 0) : a;
    }, 0);
    if (cap > 0 && booked >= cap * 0.9) continue;   // that week is effectively full
    const end = new Date(start); end.setDate(end.getDate() + (span - 1));
    wins.push({ start: ymdOf(start), end: ymdOf(end) });
  }
  return wins;
}

/* ---- Google Calendar template URL (no OAuth needed) ---- */
function gcalUrl(wo, crew) {
  const ymd = (s) => (s || "").replaceAll("-", "");
  const start = ymd(wo.scheduledStart);
  const endEx = ymd(addDaysStr(wo.scheduledEnd || wo.scheduledStart, 1));
  const text = encodeURIComponent(wo.title || "Fence install");
  const details = encodeURIComponent([
    wo.customer ? "Customer: " + wo.customer : "",
    crew ? "Crew: " + crew.name + (crew.foreman ? " (" + crew.foreman + ")" : "") : "",
    wo.crewHours ? "Estimate: " + wo.crewHours + " crew-hours · " + crewDays(wo, crew) + " day(s)" : "",
    wo.total ? "Job value: " + WO_money0(wo.total) : "",
    wo.notes || "",
  ].filter(Boolean).join("\n"));
  const loc = encodeURIComponent(wo.address || "");
  return "https://calendar.google.com/calendar/render?action=TEMPLATE&text=" + text + "&dates=" + start + "/" + endEx + "&details=" + details + "&location=" + loc;
}
function downloadIcs(wo, crew) {
  const ymd = (s) => (s || "").replaceAll("-", "");
  const ics = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Fence Works//EN", "BEGIN:VEVENT",
    "UID:" + wo.id + "@fenceworks.ai",
    "DTSTART;VALUE=DATE:" + ymd(wo.scheduledStart),
    "DTEND;VALUE=DATE:" + ymd(addDaysStr(wo.scheduledEnd || wo.scheduledStart, 1)),
    "SUMMARY:" + (wo.title || "Fence install"),
    "LOCATION:" + (wo.address || ""),
    "DESCRIPTION:" + ("Customer: " + (wo.customer || "") + (crew ? " | Crew: " + crew.name : "")),
    "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
  const a = document.createElement("a"); a.href = url; a.download = (wo.customer || "job").replace(/\s+/g, "-") + ".ics"; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function WorkOrders({ store, go, params, auth }: any) {
  const userName = (auth && auth.user && (auth.user.name || auth.user.email)) || "";
  const crews = (store.settings && store.settings.crews) || [];
  const orders = store.workOrders || [];
  const focusId = params && params.openWorkOrder;
  const [filter, setFilter] = React.useState<any>("active");   // WO-01: the work that is left, not everything ever
  const [creating, setCreating] = React.useState<any>(false);
  /* WO-01: the default was the Jobs Sheet — up to 13 columns led by a gross
     margin band, on a screen a crew opens to find out where to go. The default
     is the work now; the financial sheet is a view you choose ("Job costing"). */
  const [view, setView] = useLocalPref("fw_wo_view", "pipeline");  // pipeline | sheet | map
  const [query, setQuery] = React.useState<any>("");
  // deep-linked from a quote → make sure the focused job is visible
  /* WO-02: opening a job used to switch the view to Pipeline, clear the filter
     and expand the job inside the full list — so "back" meant re-finding your
     filter, your search and your place. It opens as its own destination now and
     Back to jobs puts everything back. */
  const [detail, setDetail] = React.useState<any>(null);
  const openJob = (wo: any) => {
    setDetail({ id: wo.id, from: { view, filter, query, scrollY: typeof window !== "undefined" ? window.scrollY : 0 } });
    if (typeof window !== "undefined") window.scrollTo({ top: 0 });
  };
  function backToJobs() {
    const from = detail && detail.from;
    setDetail(null);
    if (!from) return;
    setView(from.view, { remember: false });
    setFilter(from.filter);
    setQuery(from.query);
    setTimeout(() => { try { window.scrollTo({ top: from.scrollY }); } catch (e) {} }, 0);
  }
  const detailWo = detail ? orders.find((o: any) => o.id === detail.id) : null;
  // a deep link from a quote opens the job directly
  React.useEffect(() => { if (focusId) { const w = orders.find((o: any) => o.id === focusId); if (w) setDetail({ id: w.id, from: { view, filter, query: "", scrollY: 0 } }); } }, [focusId]);

  /* WO-01: task views. "Needs attention" is whatever actionItemsOf() already
     flags — a blocked, overdue or unlocated job — so the chip and the Action
     Items list can never disagree. */
  const WO_VIEWS = [
    { id: "active", label: "Active", hint: "Everything that isn't finished" },
    { id: "today", label: "Today", hint: "Scheduled to start or run today" },
    { id: "attention", label: "Needs attention", hint: "Blocked, overdue, or missing something" },
    { id: "unscheduled", label: "Unscheduled", hint: "No install date yet" },
    { id: "all", label: "All jobs", hint: "Including completed" },
  ];

  // pipeline head & tail that live OUTSIDE wo.status: Sold (won, deposit
  // pending → no job yet) and Unpaid (complete but the ledger isn't)
  const soldQuotes = React.useMemo(() => soldQuotesOf(store), [store.quotes, store.workOrders, store.projects]);
  const unpaidOrders = React.useMemo(() => (store.workOrders || []).filter(o => o.status === "completed" && (woBalance(o, store) || 0) > 0.5), [store.workOrders, store.quotes]);
  const actionItems = React.useMemo(() => actionItemsOf(store), [store.quotes, store.workOrders, store.projects]);

  const orphanProjects = (store.projects || []).filter(p => !orders.some(w => w.projectId === p.id));
  const counts = WO_STAGES.reduce((m, s) => (m[s.id] = orders.filter(o => o.status === s.id).length, m), {});
  const totalValue = orders.reduce((a, o) => a + (Number(o.total) || 0), 0);
  const totalLabor = orders.reduce((a, o) => a + woLaborCost(o, crews), 0);
  const upcoming = orders.filter(o => o.scheduledStart && o.status !== "completed")
    .sort((a, b) => (a.scheduledStart || "").localeCompare(b.scheduledStart || "")).slice(0, 5);

  // priority orders float to the top within each stage
  const byPriority = (a, b) => (b.priority ? 1 : 0) - (a.priority ? 1 : 0);
  const inWoView = (o: any, id: string) => {
    const today = localYmd(new Date());
    switch (id) {
      case "all": return true;
      case "active": return o.status !== "completed";
      case "today": return o.status !== "completed" && !!o.scheduledStart && o.scheduledStart <= today && (o.scheduledEnd || o.scheduledStart) >= today;
      case "attention": return actionItems.some((a: any) => a.wo && a.wo.id === o.id);
      case "unscheduled": return o.status !== "completed" && !o.scheduledStart;
      default: return o.status === id;
    }
  };
  const woViewCount = (id: string) => orders.filter((o: any) => inWoView(o, id)).length;
  const woNeedle = String(query || "").trim().toLowerCase();
  const list = orders
    .filter((o: any) => inWoView(o, filter))
    .filter((o: any) => !woNeedle || `${o.customer || ""} ${o.title || ""} ${o.address || ""} ${(crews.find((c: any) => c.id === o.crewId) || {}).name || ""}`.toLowerCase().includes(woNeedle))
    .slice().sort(byPriority);

  function createFromProject(pid) {
    const p = (store.projects || []).find(x => x.id === pid); if (!p) return;
    store.addWorkOrder({ id: newId("wo-"), projectId: p.id, quoteId: p.fromQuote, customerId: p.customerId, customer: p.customer, address: p.address,
      title: (p.customer || "Job") + " — " + (p.productName || "Fence"), total: p.total, lf: p.lf, status: "materials", suppliesOrdered: false, materialEta: "",
      crewId: store.settings && store.settings.activeCrewId, crewHours: null, scheduledStart: "", scheduledEnd: "", notes: "" });
  }
  function createBlank() { setCreating(true); }
  function submitManual(data) {
    const { list: cl, id: custId } = { list: store.customers, id: data.customerId };
    store.addWorkOrder({ id: newId("wo-"), customerId: data.customerId || undefined, customer: data.customer, address: data.address,
      title: (data.customer || "Job") + " — " + (data.kind === "repair" ? "Repair" : "Install"), kind: data.kind,
      total: +data.total || 0, priority: data.priority, status: "materials", suppliesOrdered: false, materialEta: "",
      crewId: store.settings && store.settings.activeCrewId, crewHours: null, scheduledStart: "", scheduledEnd: "", notes: data.notes || "" });
    store.addActivity(`New ${data.kind === "repair" ? "repair" : "install"} work order — ${data.customer || "customer"}`);
    setCreating(false);
  }

  return (
    <div className={"page fade-up" + (detailWo ? " wo-detail-open" : "")}>
      {/* pipeline header — on a phone it folds down to the title while a job is open */}
      <div className="row between wo-page-head" style={{ marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div><h1 className="display" style={{ fontSize: 28, margin: 0 }}>Work Orders</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>Your job pipeline — schedule crews, push to Google Calendar, and run each job to completion.</p></div>
        <div className="row gap-8" style={{ flexWrap: "wrap" }}>
          <div className="seg">
            {[["pipeline", "Jobs"], ["sheet", "Job costing"], ["map", "Map"]].map(([v, lb]) => (
              <button key={v} className={"seg-btn" + (view === v ? " on" : "")} onClick={() => setView(v)}>{lb}</button>
            ))}
          </div>
          {orders.some(o => o.status === "unscheduled") && (
            <button className="btn btn-ghost" onClick={() => { autoSchedule(store); }} title="Auto-schedule jobs awaiting a date"><Icon name="bolt" size={15} />Auto-schedule</button>
          )}
          <button className="btn btn-ghost" onClick={() => go("calendar")}><Icon name="calendar" size={15} />Calendar</button>
          {orphanProjects.length > 0 && (
            <select className="select" style={{ width: 190 }} value="" onChange={e => e.target.value && createFromProject(e.target.value)}>
              <option value="">From won job…</option>
              {orphanProjects.map(p => <option key={p.id} value={p.id}>{p.job} · {p.customer || "Job"}</option>)}
            </select>
          )}
          <button className="btn btn-primary" onClick={createBlank}><Icon name="plus" size={15} />New</button>
        </div>
      </div>

      {/* WO-01: the four questions actually asked of this screen, before the
          eight-stage pipeline. "Active" excludes completed work, so the list a
          crew opens is the work that is left. */}
      {!detailWo && <div className="est-toolbar" style={{ marginTop: 14 }}>
        <div className="cat-tabs" role="tablist" aria-label="Job views">
          {WO_VIEWS.map(v => (
            <button key={v.id} role="tab" aria-selected={filter === v.id} title={v.hint}
              className={"filter-chip" + (filter === v.id ? " on" : "")}
              onClick={() => { setFilter(v.id); setView("pipeline"); }}>{v.label} · {woViewCount(v.id)}</button>
          ))}
        </div>
        <label className="est-search">
          <Icon name="search" size={14} />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search customer, address or crew" aria-label="Search jobs" />
          {query && <button className="est-search-x" aria-label="Clear search" onClick={() => setQuery("")}><Icon name="x" size={13} /></button>}
        </label>
      </div>}

      {!detailWo && <details className="wo-stages">
        <summary>Pipeline by stage</summary>
      <div className="wo-pipeline">
        <button className={"wo-pipe-all" + (filter === "all" ? " on" : "")} onClick={() => { setFilter("all"); setView("pipeline"); }}>
          <span className="display tnum" style={{ fontSize: 20 }}>{orders.length}</span><span>All jobs</span>
        </button>
        {/* Sold: won quotes whose deposit hasn't cleared — no job exists yet */}
        <Icon name="chevright" size={15} className="faint wo-pipe-arrow" />
        <button className={"wo-pipe-stage" + (filter === "sold" ? " on" : "")} onClick={() => { setFilter("sold"); setView("pipeline"); }} style={filter === "sold" ? { borderColor: "var(--teal)" } : null} title="Signed quotes awaiting the deposit — the job auto-creates when it clears">
          <span className="wo-pipe-dot" style={{ background: "var(--teal)" }} />
          <span className="display tnum" style={{ fontSize: 20 }}>{soldQuotes.length}</span>
          <span>Sold</span>
        </button>
        {WO_STAGES.map((s, i) => (
          <React.Fragment key={s.id}>
            <Icon name="chevright" size={15} className="faint wo-pipe-arrow" />
            <button className={"wo-pipe-stage" + (filter === s.id ? " on" : "")} onClick={() => { setFilter(s.id); setView("pipeline"); }} style={filter === s.id ? { borderColor: s.color } : null}>
              <span className="wo-pipe-dot" style={{ background: s.color }} />
              <span className="display tnum" style={{ fontSize: 20 }}>{counts[s.id] || 0}</span>
              <span>{s.short}</span>
            </button>
          </React.Fragment>
        ))}
        {/* Unpaid: complete but the money isn't — the pipeline isn't done until it is */}
        <Icon name="chevright" size={15} className="faint wo-pipe-arrow" />
        <button className={"wo-pipe-stage" + (filter === "unpaid" ? " on" : "")} onClick={() => { setFilter("unpaid"); setView("pipeline"); }} style={filter === "unpaid" ? { borderColor: "var(--red)" } : null} title="Completed jobs with a balance still due">
          <span className="wo-pipe-dot" style={{ background: "var(--red)" }} />
          <span className="display tnum" style={{ fontSize: 20 }}>{unpaidOrders.length}</span>
          <span>Unpaid</span>
        </button>
      </div>
      </details>}

      {detailWo && (
        <div className="wo-detail">
          <button className="btn btn-ghost btn-sm wo-back" onClick={backToJobs}><Icon name="arrow" size={14} style={{ transform: "rotate(180deg)" }} />Back to jobs</button>
          <WorkOrderCard wo={detailWo} store={store} crews={crews} go={go} focus userName={userName} />
        </div>
      )}
      {!detailWo && view === "sheet" && <JobsSheet orders={orders} store={store} crews={crews} onOpen={openJob} query={query} />}
      {!detailWo && view === "map" && <JobsMap orders={orders} store={store} crews={crews} onOpen={openJob} />}
      {!detailWo && view === "pipeline" && (
      <div className="settings-grid wo-layout" style={{ marginTop: 18 }}>
        {/* left rail */}
        <aside className="set-side">
          <div className="card pad">
            {/* WO-06: this said "Gross" over revenue minus labor — materials
                were never subtracted, so it read as profit and was not. The
                sheet's own "Gross margin" on the same jobs DID subtract them,
                and the two numbers disagreed on screen. */}
            <div className="kicker" style={{ marginBottom: 12 }}>This Pipeline</div>
            <div className="row between" style={{ padding: "5px 0", fontSize: 13 }}><span className="muted">Contract total</span><span className="tnum" style={{ fontWeight: 700, color: "var(--teal)" }}>{WO_money0(totalValue)}</span></div>
            <div className="row between" style={{ padding: "5px 0", fontSize: 13 }}><span className="muted">Scheduled labor cost</span><span className="tnum">{WO_money0(totalLabor)}</span></div>
            <div className="row between" style={{ borderTop: "1px solid var(--line)", marginTop: 6, paddingTop: 10, fontWeight: 700 }}>
              <span>After labor</span><span className="tnum">{WO_money0(totalValue - totalLabor)}</span></div>
            <p className="faint" style={{ fontSize: 10.5, margin: "8px 0 0", lineHeight: 1.45 }}>
              Not profit — materials, permits and subs are not taken out here. Open <b style={{ color: "var(--fg-3)" }}>Job costing</b> for margin against the full cost basis.
            </p>
          </div>
          <div className="card pad">
            <div className="kicker" style={{ marginBottom: 10 }}>Upcoming Schedule</div>
            {upcoming.length === 0
              ? <p className="muted" style={{ fontSize: 12.5, textAlign: "center", margin: 0 }}>Nothing scheduled yet</p>
              : <div style={{ display: "grid", gap: 9 }}>
                  {upcoming.map(o => { const cr = crews.find(c => c.id === o.crewId); return (
                    <div key={o.id} className="row gap-8" style={{ fontSize: 12.5 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 3, background: (cr && cr.color) || "var(--teal)", flexShrink: 0 }} />
                      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.customer || o.title}</span>
                      <span className="mono faint">{fmtDate(o.scheduledStart)}</span></div>); })}
                </div>}
          </div>
          <div className="card pad">
            <div className="kicker" style={{ marginBottom: 8 }}>Crews</div>
            {crews.length === 0
              ? <p className="muted" style={{ fontSize: 12, margin: 0 }}>Add crews in <a onClick={() => go("settings")} style={{ color: "var(--teal)", cursor: "pointer" }}>Settings</a>.</p>
              : crews.map(c => <div key={c.id} className="row between" style={{ padding: "6px 0", fontSize: 12.5 }}>
                  <span className="row gap-8"><span style={{ width: 8, height: 8, borderRadius: 3, background: c.color || "var(--teal)" }} />{c.name}</span><span className="mono faint">{WO_money0(crewRate(c))}/hr</span></div>)}
          </div>
        </aside>

        {/* main list */}
        <main>
          {actionItems.length > 0 && (filter === "active" || filter === "all" || filter === "attention") && <ActionItemsPanel items={actionItems} go={go} onOpen={openJob} store={store} />}
          {filter === "sold"
            ? (soldQuotes.length === 0
                ? <div className="card pad"><EmptyState icon="dollar" title="Nothing awaiting deposit" sub="Signed quotes wait here until the deposit clears — then the job auto-creates in Order Supplies." /></div>
                : <div style={{ display: "grid", gap: 12 }}>{soldQuotes.map(q => <SoldQuoteCard key={q.id} q={q} store={store} go={go} />)}</div>)
            : filter === "unpaid"
            ? (unpaidOrders.length === 0
                ? <div className="card pad"><EmptyState icon="check" title="No unpaid completions" sub="Completed jobs with a balance still due show here until it's collected." /></div>
                : <div style={{ display: "grid", gap: 12 }}>{unpaidOrders.slice().reverse().map(wo => <WorkOrderCard key={wo.id} wo={wo} store={store} crews={crews} go={go} onOpen={openJob} userName={userName} />)}</div>)
            : orders.length === 0
            ? <div className="card pad"><EmptyState icon="clipboard" title="No work orders yet" sub="When a customer signs & pays a deposit on a quote, the job lands here ready to schedule — or add one from a won job above." />
                <div className="row gap-8" style={{ justifyContent: "center", marginTop: 4 }}><button className="btn btn-outline btn-sm" onClick={() => go("estimator")}>New estimate</button></div></div>
            : (filter === "all" || filter === "active")
              // grouped by stage, but from the SAME filtered list as every other
              // view — this branch read straight from `orders`, so the search box
              // did nothing on the two views it is most needed on
              ? (list.length === 0
                  ? <div className="card pad"><EmptyState icon="search" title={woNeedle ? `Nothing matches "${query}"` : "Nothing here"} sub={woNeedle ? "Searched customer, address and crew." : "Jobs appear as they are created."}
                      action={woNeedle ? { label: "Clear search", icon: "undo", onClick: () => setQuery("") } : undefined} /></div>
                  : WO_STAGES.map(st => ({ st, rows: list.filter(o => o.status === st.id) })).filter(g => g.rows.length > 0).map(({ st, rows }) => (
                    <div key={st.id} style={{ marginBottom: 22 }}>
                      <div className="wo-group-head"><span className="wo-pipe-dot" style={{ background: st.color }} />{st.label}<span className="muted">· {rows.length}</span></div>
                      <div style={{ display: "grid", gap: 12 }}>
                        {rows.slice().reverse().map(wo => <WorkOrderCard key={wo.id} wo={wo} store={store} crews={crews} go={go} onOpen={openJob} userName={userName} />)}
                      </div>
                    </div>)))
              : <div style={{ display: "grid", gap: 12 }}>
                  {list.length === 0 ? <div className="card pad"><EmptyState icon={STAGE(filter).icon} title={"Nothing " + STAGE(filter).label.toLowerCase()} sub="Jobs move through the pipeline as you schedule and complete them." /></div>
                    : list.slice().reverse().map(wo => <WorkOrderCard key={wo.id} wo={wo} store={store} crews={crews} go={go} onOpen={openJob} userName={userName} />)}
                </div>}
        </main>

        {/* right rail */}
        <aside className="set-side">
          <details className="card pad wo-flow-help">
            <summary className="kicker">How jobs move · the flow</summary>
            <div className="wo-flow" style={{ marginTop: 10 }}>
              {[["dollar", "Quote paid", "Deposit collected on the quote"], ["box", "Order supplies", "Mark ordered · get delivery ETA"], ["calendar", "Needs scheduling", "Tentatively after materials arrive"], ["calendar", "Scheduled", "Pushed to Google Calendar"], ["bolt", "In progress", "Crew on site"], ["check", "Completed", "Job done & closed"]].map(([ic, t, d], i) => (
                <div key={t} className="wo-flow-step">
                  <span className="wo-flow-ic"><Icon name={ic} size={13} /></span>
                  <div><div style={{ fontWeight: 700, fontSize: 12.5 }}>{t}</div><div className="faint" style={{ fontSize: 11 }}>{d}</div></div>
                </div>
              ))}
            </div>
          </details>
        </aside>
      </div>
      )}
      {creating && <ManualWorkOrder store={store} onClose={() => setCreating(false)} onSubmit={submitManual} />}
    </div>
  );
}

/* ---- action items: the jobs that are quietly stalling, with one-tap jumps ---- */
function ActionItemsPanel({ items, go, onOpen, store }: any) {
  const [open, setOpen] = React.useState<any>(true);
  const crit = items.filter(i => i.sev === "crit").length;
  return (
    <div className="card pad" style={{ marginBottom: 16, borderColor: crit ? "var(--red-tint)" : "var(--amber-tint)" }}>
      <button className="row between" style={{ width: "100%", background: "none", border: 0, padding: 0, cursor: "pointer", color: "inherit" }} onClick={() => setOpen(o => !o)}>
        <span className="row gap-8" style={{ fontWeight: 800, fontSize: 14 }}>
          <Icon name="bolt" size={15} style={{ color: crit ? "var(--red)" : "var(--amber)" }} />
          Action items · {items.length}
          {crit > 0 && <span className="chip" style={{ fontSize: 10.5, color: "var(--red)", background: "var(--red-tint)", borderColor: "transparent" }}>{crit} urgent</span>}
        </span>
        <Icon name={open ? "chevdown" : "chevright"} size={15} className="muted" />
      </button>
      {open && (
        <div style={{ display: "grid", gap: 7, marginTop: 12 }}>
          {items.map(it => (
            <div key={it.key} className="ready-row">
              <Icon name={it.icon} size={14} style={{ color: it.sev === "crit" ? "var(--red)" : "var(--amber)", flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 0, fontSize: 13 }}>{it.msg}</span>
              {it.remindQ && <button className="btn btn-outline btn-sm" style={{ flexShrink: 0 }} title="Email the balance reminder with the pay link (logged on the quote)" onClick={() => sendBalanceReminder(store, it.remindQ)}><Icon name="send" size={12} />Send reminder</button>}
              <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }} onClick={() => it.go ? go(it.go[0], it.go[1]) : onOpen(it.wo)}>{it.btn}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- a won quote whose deposit hasn't cleared — the Sold stage card ---- */
function SoldQuoteCard({ q, store, go }: any) {
  const paid = quotePayments(q).paid;
  const age = daysSince(q.lastSentAt || q.createdAt);
  return (
    <div className="card" style={{ overflow: "hidden" }}>
      <div className="wo-head" style={{ cursor: "default" }}>
        <span className="wo-status-dot" style={{ background: "var(--teal)" }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{q.customer || "Customer"} — {q.productName || "Fence"}</div>
          <div className="muted" style={{ fontSize: 12.5 }}>{q.address || "No address"} · signed{age > 0 ? ` ${age}d ago` : " today"}{paid > 0 ? ` · ${WO_money0(paid)} received` : (q.depositPct != null && Number(q.depositPct) === 0 ? " · paid on completion" : " · deposit pending")}</div>
        </div>
        <div className="display tnum" style={{ fontSize: 16, color: "var(--teal)" }}>{WO_money0(q.total || 0)}</div>
        <button className="btn btn-ghost btn-sm" onClick={() => go("quote", { quoteId: q.id })}><Icon name="clipboard" size={13} />Quote</button>
        <button className="btn btn-outline btn-sm" title="Create the work order now without waiting on the deposit" onClick={() => { store.addProjectFromQuote(q); store.addActivity((q.customer || "Job") + " started without deposit — work order created"); }}>Start job →</button>
      </div>
    </div>
  );
}

function ManualWorkOrder({ store, onClose, onSubmit }: any) {
  const customers = store.customers || [];
  const [f, setF] = React.useState<any>({ kind: "repair", customerId: "", customer: "", address: "", total: "", priority: false, notes: "" });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  function pickCustomer(id) {
    const c = customers.find(x => x.id === id);
    setF(s => ({ ...s, customerId: id, customer: c ? c.name : s.customer, address: c ? (c.address || s.address) : s.address }));
  }
  return ReactDOM.createPortal(
    <div className="hub-preview-overlay" data-dialog onClick={onClose}>
      <div className="hub-quote-modal" onClick={e => e.stopPropagation()}>
        <div className="hub-preview-bar">
          <span className="row gap-8" style={{ fontSize: 13, fontWeight: 700 }}><Icon name="plus" size={14} className="muted" />New work order</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>
        <div className="hub-quote-body" style={{ display: "grid", gap: 12 }}>
          <div>
            <label className="field-label">Job type</label>
            <div className="row gap-8">
              {[["repair", "Repair"], ["install", "Install"]].map(([v, lb]) =>
                <button key={v} className={"filter-chip" + (f.kind === v ? " on" : "")} onClick={() => set("kind", v)}>{lb}</button>)}
            </div>
          </div>
          {customers.length > 0 && (
            <div><label className="field-label">Existing customer (optional)</label>
              <select className="select" value={f.customerId} onChange={e => pickCustomer(e.target.value)}>
                <option value="">New / one-off customer…</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name}{c.address ? " · " + c.address : ""}</option>)}
              </select></div>
          )}
          <div className="row gap-10">
            <div style={{ flex: 1 }}><label className="field-label">Customer name</label><input className="input" value={f.customer} onChange={e => set("customer", e.target.value)} placeholder="Name" /></div>
            <div style={{ width: 120 }}><label className="field-label">Job price</label><input className="input tnum" type="number" value={f.total} onChange={e => set("total", e.target.value)} placeholder="0" /></div>
          </div>
          <div><label className="field-label">Address</label><input className="input" value={f.address} onChange={e => set("address", e.target.value)} placeholder="Property address" /></div>
          <div><label className="field-label">Notes</label><textarea className="textarea" value={f.notes} onChange={e => set("notes", e.target.value)} placeholder="What needs repair / scope of work…" /></div>
          <label className="toggle-row" onClick={() => set("priority", !f.priority)}>
            <span className={"sw" + (f.priority ? " on" : "")}><span className="knob" /></span>
            <span style={{ fontSize: 13.5, fontWeight: 600 }}>Priority order</span>
            <span className="muted" style={{ fontSize: 12, marginLeft: "auto" }}>floats to the top</span>
          </label>
          <button className="btn btn-primary btn-block" disabled={!f.customer.trim()} onClick={() => onSubmit(f)}><Icon name="check" size={15} />Create work order</button>
          <p className="faint" style={{ fontSize: 11.5, textAlign: "center", margin: 0 }}>Starts in Order Supplies so you can order materials &amp; set a delivery ETA first.</p>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ---- one shared, polite geocoder for background lookups -------------------
   Weather chips + the Job Map both need coordinates; without a shared gate a
   page full of cards stampedes the free geocoder in parallel and burns its
   rate limit (which then breaks address SEARCH too). One global queue:
   sequential, ≥1.1s apart, per-address cached for the session. */
const geoCache: any = new Map();
const geoQ: any = { chain: Promise.resolve(), last: 0 };
function throttledGeocode(address: any) {
  if (!address || !window.VFP_PARCEL) return Promise.resolve(null);
  if (geoCache.has(address)) return geoCache.get(address);
  const p = (geoQ.chain = geoQ.chain.then(async () => {
    const wait = 1100 - (Date.now() - geoQ.last);
    if (wait > 0) await new Promise(r => setTimeout(r, wait));
    geoQ.last = Date.now();
    try { return await window.VFP_PARCEL.geocode(address); } catch (e) { return null; }
  }));
  geoCache.set(address, p);
  p.then((g: any) => { if (!g) geoCache.delete(address); });   // failure → allow a later retry
  return p;
}

/* install-day weather chip: shows only when a scheduled job inside the 16-day
   forecast window has a rain / hard-frost risk. Geocodes the address once
   (persisted as wo.geo — the Job Map reuses it) and caches forecasts per ~1km. */
function WeatherChip({ wo, store }: any) {
  const [risk, setRisk] = React.useState<any>(null);
  const [good, setGood] = React.useState<any>(null);   // the next clear workday the crew is free (lib/schedulewx.ts)
  const days = React.useMemo(() => {
    if (!wo.scheduledStart || wo.status === "completed") return [];
    const out = [];
    const d0 = new Date(wo.scheduledStart + "T00:00:00");
    const end = new Date((wo.scheduledEnd || wo.scheduledStart) + "T00:00:00");
    const lim = Date.now() + 16 * 86400000;
    for (let d = new Date(d0); d <= end && out.length < 6; d.setDate(d.getDate() + 1)) {
      if (d.getTime() > Date.now() - 86400000 && d.getTime() < lim) out.push(localYmd(d));
    }
    return out;
  }, [wo.scheduledStart, wo.scheduledEnd, wo.status]);
  React.useEffect(() => {
    let dead = false;
    if (!days.length || !window.VFP_WEATHER) { setRisk(null); return; }
    (async () => {
      let geo = wo.geo && wo.geo.lat != null ? wo.geo : null;
      if (!geo && wo.address) {
        const g = await throttledGeocode(wo.address);   // shared queue — never stampedes the geocoder
        if (g && g.lat != null) { geo = { lat: g.lat, lng: g.lng }; store.updateWorkOrder(wo.id, { geo }); }
      }
      if (!geo || dead) return;
      const fc = await window.VFP_WEATHER.forecast(geo.lat, geo.lng);
      if (!fc || dead) return;
      let worst = null;
      // a concrete day is graded by its job types (lib/weatherrules.ts); a fence day as always
      const cxQ = verticalOf(wo) === "concrete" && wo.quoteId ? ((store.quotes || []) as any[]).find((x: any) => x && x.id === wo.quoteId) : null;
      const wxOpts = verticalOf(wo) === "concrete" ? { vertical: "concrete", jobTypes: jobTypesOf(cxQ || wo), cfg: (((store.settings || {}).ai || {}).automationCfg || {}).reschedule_weather && ((((store.settings || {}).ai || {}).automationCfg || {}).reschedule_weather.byVertical || {}).concrete } : undefined;
      const grade = (d: any) => window.VFP_WEATHER.risk(d, wxOpts);
      days.forEach(day => {
        const r = grade(fc[day]);
        if (r && r.level !== "ok" && (!worst || (r.level === "high" && worst.level !== "high"))) worst = { ...r, day };
      });
      setRisk(worst);
      // the next workday the forecast grades clear for the job's whole length, with the crew free (§13 item 13)
      if (worst && wo.scheduledStart) {
        const crew = ((store.settings || {}).crews || []).find((c: any) => c.id === wo.crewId);
        const span = Math.max(1, Math.round((new Date((wo.scheduledEnd || wo.scheduledStart) + "T00:00:00").getTime() - new Date(wo.scheduledStart + "T00:00:00").getTime()) / 86400000) + 1);
        setGood(nextGoodDay(fc, wo.scheduledStart, grade, { span, workdays: crew && Array.isArray(crew.workdays) && crew.workdays.length ? crew.workdays : (store.settings || {}).workdays, busy: (d: string) => crewBusyOn(store.allWorkOrders || store.workOrders, wo.crewId, d, wo.id) }));
      } else setGood(null);
    })();
    return () => { dead = true; };
  }, [days.join(","), wo.geo && wo.geo.lat]);
  // no live forecast yet: the server's overnight verdict (scheduler.js tickForecast → wo.weatherFlag) while its day hasn't passed
  const flag = !risk && weatherFlagLive(wo.weatherFlag, localYmd(new Date())) ? wo.weatherFlag : null;
  if (!risk && !flag) return null;
  const level = risk ? risk.level : (flag.level === "med" ? "med" : "high");
  const col = level === "high" ? "var(--red)" : "var(--amber)";
  const bg = level === "high" ? "var(--red-tint)" : "var(--amber-tint)";
  const label = risk ? risk.label + " " + new Date(risk.day + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" }) : "Not a day for " + (flag.work || "this work") + " · " + wxDayLabel(flag.day);
  const why = (risk ? risk.why : flag.why) || "";
  return (
    <span className="row gap-6" style={{ flexShrink: 0, alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
      <span className="chip" title={why + (good ? "" : " Consider rescheduling or prepping the site.")} style={{ fontSize: 10.5, color: col, background: bg, borderColor: "transparent", flexShrink: 0 }}>
        ⛈ {label}
      </span>
      {good && <button className="btn btn-ghost btn-sm" style={{ padding: "2px 7px", fontSize: 11 }} title={why + " Moves the job to the next workday the forecast grades clear for its whole length, with the crew free."}
        onClick={(e) => { e.stopPropagation(); moveJob(store, wo, good, undefined, { reason: "weather" }); notify(`Moved to ${wxDayLabel(good)} — the next clear day the crew is free.`); }}>
        <Icon name="calendar" size={11} />Move to {wxDayLabel(good)}
      </button>}
    </span>
  );
}

/* Foam back off the truck (plan §13 item 18). The lift log says how many pounds
   went into the slab; converted through the pack the card buys it in, that's
   the sets to draw from stock — once, with the job as the ledger reference —
   so the on-hand count is what's really left. Only when Inventory tracks a
   foam item (its label or name says foam); nothing otherwise. */
function FoamDrawCard({ wo, store }: any) {
  const [items, setItems] = React.useState<any>(null);
  const [busy, setBusy] = React.useState<boolean>(false);
  const ST: any = (window as any).VFP_STOCK;
  const t = liftTotals(wo && wo.liftLog);
  const sup = (wo && wo.cx && wo.cx.est && Array.isArray(wo.cx.est.supplies) ? wo.cx.est.supplies : []).find((x: any) => x && /foam/i.test(String(x.label)));
  const card: any = rateCardsOf(store.settings || {}).lift;
  const comp = card && (card.components || []).find((c: any) => c && c.kind === "material" && c.per === "sqft-inch");
  const packSize = (sup && Number(sup.packSize)) || (comp && comp.pack && Number(comp.pack.size)) || 0;
  const sets = packSize > 0 && t.foamLb > 0 ? Math.ceil(t.foamLb / packSize) : 0;
  const drawn = wo && wo.stockDrawnAt;
  React.useEffect(() => {
    if (!ST || !ST.enabled || !ST.enabled() || !sets || drawn) return;
    let dead = false;
    Promise.resolve().then(() => ST.items()).then((rows: any[]) => { if (!dead) setItems((rows || []).filter((it: any) => it && it.active !== false && /foam/i.test(String(it.engine_label || "") + " " + String(it.name || "")))); }).catch(() => { if (!dead) setItems([]); });
    return () => { dead = true; };
  }, [sets, drawn]);
  if (!wo || verticalOf(wo) !== "concrete" || !(t.foamLb > 0)) return null;
  if (drawn) {
    const d = wo.stockDrawn || {};
    return <p className="muted" style={{ fontSize: 12, margin: "6px 0 0" }}><Icon name="check" size={11} /> {d.sets ? `${d.sets} foam set${d.sets === 1 ? "" : "s"} drawn from ${d.item || "stock"}` : "Foam drawn from stock"} · {new Date(drawn).toLocaleDateString()}</p>;
  }
  if (!sets || !items || !items.length) return null;
  async function draw(it: any) {
    setBusy(true);
    try {
      await ST.move(it, -sets, "consume", { ref: "wo:" + wo.id + ":foam", note: `Lift log: ${t.foamLb} lb over ${t.entries} section${t.entries === 1 ? "" : "s"}` });
      store.updateWorkOrder(wo.id, { stockDrawnAt: Date.now(), stockDrawn: { sets, itemId: it.id, item: it.location ? `${it.name} (${it.location})` : it.name } });
      store.addActivity(`${sets} foam set${sets === 1 ? "" : "s"} drawn from ${it.location || "stock"} for ${wo.customer || "the job"}`);
    } catch (e: any) { notify("Couldn't draw the foam — " + String((e && e.message) || e).slice(0, 120), { kind: "error" }); }
    setBusy(false);
  }
  return (
    <div className="row gap-8" data-tour="foam-draw" style={{ flexWrap: "wrap", alignItems: "center", marginTop: 6, fontSize: 12.5 }}>
      <span className="muted">Lift log: <b style={{ color: "var(--fg)" }}>{t.foamLb} lb</b> ≈ {sets} × {packSize} lb set{sets === 1 ? "" : "s"}.</span>
      {items.map((it: any) => (
        <button key={it.id} className="btn btn-outline btn-sm" disabled={busy} onClick={() => draw(it)} title={`On hand: ${Number(it.on_hand) || 0}${it.location ? " at " + it.location : ""}`}>
          <Icon name="box" size={12} />Draw {sets} from {it.location || it.name}{(Number(it.on_hand) || 0) < sets ? " (short)" : ""}
        </button>
      ))}
    </div>
  );
}

function WorkOrderCard({ wo, store, crews, go, focus, userName, onOpen }: any) {
  // as a list row the body stays shut (the row opens the detail destination);
  // as the destination itself it is always open
  const [open, setOpen] = React.useState<any>(!!focus);
  const [crewSheet, setCrewSheet] = React.useState<any>(false);  // printable crew build sheet
  const [poModal, setPoModal] = React.useState<any>(null);       // purchase-order editor (draft or existing)
  // On-my-way text status. "sent" used to go up before the send was even
  // attempted, on a helper that swallowed its own errors — so a blocked
  // messaging service still read green. The states are what actually happened:
  // sending -> queued -> sent -> delivered, or blocked / failed with a reason.
  const [enroute, setEnroute] = React.useState<any>(null);
  const enrTimers = React.useRef<any[]>([]);
  React.useEffect(() => () => { enrTimers.current.forEach(clearTimeout); }, []);
  const csQuote = wo.quoteId ? (store.quotes || []).find((x: any) => x.id === wo.quoteId) : null;
  const cardRef = React.useRef<any>(null);
  React.useEffect(() => { if (focus) setOpen(true); }, [focus]);
  const st = STAGE(wo.status);
  const crew = crews.find(c => c.id === wo.crewId);
  const labor = woLaborCost(wo, crews);
  const set = (patch) => store.updateWorkOrder(wo.id, patch);
  const days = crewDays(wo, crew);
  // a pending change order holds production: scheduling / starting asks once
  const hold = coHold(wo, store);
  /* Is there anywhere to push to? Only a connected Google calendar with a live
     API counts — otherwise "add to calendar" means opening a template tab, and
     that is an action the contractor should choose, not a side effect of
     booking a crew. */
  const gcalConn: any = (store.settings && store.settings.integrations && store.settings.integrations.gcal) || {};
  const gcalReady = !!gcalConn.connected;
  const gcalName = gcalConn.calendarName || gcalConn.calendarId || "your connected calendar";
  const [pushCal, setPushCal] = React.useState<any>(true);

  // what stands between this job and its next move, for the hint by the button
  const nextTarget: any = wo.status === "in_progress" ? null : wo.status === "scheduled" ? "in_progress" : "scheduled";
  const nextBlockers = nextTarget ? blockersFor(wo, store, nextTarget) : [];
  // materials: derived from this job's purchase orders when it has any, since
  // those already record what was ordered and what has actually been received
  const matState = materialsState(wo, poList(store.settings).filter((x: any) => x.workOrderId === wo.id), poDerivedStatus);

  // Deleting a job throws away work nobody can get back — the hours the crew
  // logged, the receipts, the site photos. A plain "are you sure?" gets clicked
  // through, so this lists exactly what is about to go and makes you type the
  // customer's name. Typing is the part a slip can't do by accident.
  async function confirmDelete() {
    const lost: string[] = [];
    const hrs = actualHours(wo);
    if (hrs > 0) lost.push(hrs.toFixed(1).replace(/\.0$/, "") + " logged hours");
    const exp = woExpenses(wo).length;
    if (exp > 0) lost.push(exp + " expense" + (exp > 1 ? "s" : "") + " (" + WO_money0(allExpensesTotal(wo)) + ")");
    const photos = Array.isArray(wo.photos) ? wo.photos.length : 0;
    if (photos > 0) lost.push(photos + " site photo" + (photos > 1 ? "s" : ""));
    if (wo.notes) lost.push("the job notes");
    const name = wo.customer || "";
    const body = [
      lost.length ? "This deletes " + listWords(lost) + ". None of it can be recovered." : "This job has nothing logged against it yet, so nothing is lost but the job itself.",
      wo.quoteId ? "The quote it came from is kept — you can build a new job from it." : "",
      wo.calendarAdded ? "Remove it from Google Calendar separately; deleting here doesn’t." : "",
    ].filter(Boolean).join("\n");
    const ok = await confirmDialog({
      title: name ? "Delete the " + name + " job?" : "Delete this job?",
      body,
      danger: true,
      confirmLabel: "Delete this job",
      requireText: name || "DELETE",
      requireHint: name ? <>Type the customer’s name — <b style={{ color: "var(--fg)" }}>{name}</b> — to delete</> : undefined,
    });
    if (!ok) return;
    store.deleteWorkOrder(wo.id);
    store.addActivity((name || "A job") + " work order deleted");
    notify("Job deleted", { kind: "success" });
  }

  // customer payload for the appointment / on-my-way automations — enrich with
  // phone/email from the customer record so the texts have somewhere to send.
  const custOf = () => { const c: any = (store.customers || []).find((x: any) => x.id === wo.customerId) || {}; return { id: wo.customerId, name: wo.customer || c.name, phone: c.phone, email: c.email, _address: wo.address || c.address }; };

  /* The one scheduling path. The stage bar, the primary button and the
     customer's schedule request all land here, so none of them can book a job
     with no date or past a permit hold the owner switched on. `req` carries the
     dates and extra patch fields from the customer-request flow. */
  async function schedule(req?: any) {
    const startAt = (req && req.scheduledStart) || wo.scheduledStart;
    const target = { ...wo, ...(req || {}), scheduledStart: startAt };
    const extra = await gateTransition(target, store, "scheduled", { onHard: () => set({ _needDate: true }) });
    if (!extra) return;
    const end = (req && req.scheduledEnd) || wo.scheduledEnd || addDaysStr(startAt, Math.max(0, (days || 1) - 1));
    const patch: any = { ...(req || {}), ...extra, status: "scheduled", scheduledStart: startAt, scheduledEnd: end, _needDate: false };
    set(patch);
    // upcoming-appointment reminder/confirmation — backend owns the send-timing
    // (day-before); we just hand it the appointment date. No-op when unconfigured.
    { const cu = custOf(); emitAutomation("appointment.upcoming", { customer: { id: cu.id, name: cu.name, phone: cu.phone, email: cu.email }, job: { id: wo.projectId || wo.id, date: startAt, address: cu._address } }); }
    store.addActivity((wo.customer || "Job") + " scheduled for " + fmtDate(startAt));
    // connected Google Calendar → push the event via the API (store the event id
    // so reschedules update the same one); otherwise fall back to the manual
    // "add to Google Calendar" template link.
    // opted out, or nothing to push to: the job is booked here and that is all
    // that was asked for. Nothing opens a tab the contractor didn't ask for.
    if (!pushCal || !gcalReady) return;
    const DB: any = window.VFP_DB;
    const conn = store.settings && store.settings.integrations && store.settings.integrations.gcal;
    // "On calendar" is only claimed once Google actually confirms the event —
    // the manual template link opens a browser tab we can't see the outcome of.
    const manual = () => window.open(gcalUrl({ ...wo, ...patch }, crew), "_blank", "noopener");
    if (conn && conn.connected && DB && DB.gcalSync && DB.apiBase && DB.apiBase()) {
      DB.gcalSync({ ...wo, ...patch, crew: crew && crew.name }, conn.calendarId)
        .then((r: any) => {
          if (r && r.eventId) set({ gcalEventId: r.eventId, calendarAdded: true });
          else notify("Couldn't reach Google Calendar — add it manually", { kind: "error" });
        })
        .catch(() => { notify("Couldn't reach Google Calendar — add it manually", { kind: "error" }); manual(); });
    } else { manual(); }
  }

  /* Moving a job BACKWARDS is a correction, not a step. Walking a completed job
     back to Needs Scheduling unpicks its completion date and everything keyed
     off it, so it says so once rather than happening on a stray tap. */
  async function stepBack(to: string) {
    const from = WO_STAGES.findIndex((x) => x.id === wo.status);
    const at = WO_STAGES.findIndex((x) => x.id === to);
    if (at >= 0 && from >= 0 && at < from) {
      // moving back BEFORE "Scheduled" un-books the job. The dates used to stay
      // on the record, and the customer's hub reads them — so a job pulled back
      // to Needs Scheduling still told the customer their install was on.
      const unbooks = at < WO_STAGES.findIndex((x) => x.id === "scheduled") && !!wo.scheduledStart;
      const ok = await confirmDialog({
        title: `Move ${wo.customer || "this job"} back to ${STAGE(to).label}?`,
        body: [
          wo.status === "completed"
            ? "This job is marked complete. Moving it back doesn't un-send anything completion already triggered, and the crew sheet and schedule will show it as live work again."
            : "This is a correction, not a step forward — the job will show as earlier in the pipeline than it is.",
          unbooks ? `The install dates (${fmtDate(wo.scheduledStart)}${wo.scheduledEnd && wo.scheduledEnd !== wo.scheduledStart ? "–" + fmtDate(wo.scheduledEnd) : ""}) come off the job, and ${wo.customer || "the customer"}'s hub stops showing them.${wo.calendarAdded ? " Remove the Google Calendar event yourself — this doesn't." : ""}` : "",
        ].filter(Boolean).join("\n"),
        confirmLabel: unbooks ? "Move it back and clear the dates" : "Move it back", cancelLabel: "Leave it", danger: true,
      });
      if (!ok) return;
      store.addActivity(`${wo.customer || "Job"} moved back to ${STAGE(to).label}${unbooks ? " — install dates cleared" : ""}`);
      if (unbooks) {
        set({ status: to, scheduledStart: "", scheduledEnd: "", calendarAdded: false, gcalEventId: null, calendarStale: false,
              scheduleRequest: wo.scheduleRequest ? { ...wo.scheduleRequest, confirmedAt: null } : undefined });
        return;
      }
    }
    set({ status: to });
  }

  /* Starting work digs. It answers to the same permit and change-order holds as
     scheduling — the stage bar used to skip both. */
  async function start() {
    const extra = await gateTransition(wo, store, "in_progress");
    if (!extra) return;
    set({ ...extra, status: "in_progress" });
    store.addActivity((wo.customer || "Job") + " started");
  }

  /* Completion fires review requests, the balance chase and the social post, and
     starts the warranty clock. It is not a display filter, so it shows what is
     still open, what is still owed, and what it is about to send. */
  async function complete() {
    if (!(await confirmCompletion(wo, store))) return;
    set({ status: "completed" });
    store.addActivity((wo.customer || "Job") + " marked complete");
    // photo-first wrap-up: the after photos and the balance link go to the
    // customer in one text, while the crew is still in the driveway
    const cu = custOf();
    const photos = Array.isArray(wo.photos) ? wo.photos.length : 0;
    const c: any = (store.customers || []).find((x: any) => x.id === wo.customerId);
    if (cu.phone && c && c.hubToken) {
      const q: any = (store.quotes || []).find((x: any) => x.id === wo.quoteId);
      const bal = q ? Math.max(0, (Number(q.total) || 0) - quotePayments(q).paid) : 0;
      const ok = await confirmDialog({
        title: "Text " + (cu.name || "the customer") + " the wrap-up?",
        body: (photos ? photos + " photo" + (photos === 1 ? "" : "s") + " from this job" : "No photos on this job yet — add a couple of after shots first if you can. The link") + (photos ? " and " : " ") + (bal > 0 ? "the " + WO_money0(bal) + " balance link" : "their paid-in-full receipt") + " are on their hub. One text sends it.",
        confirmLabel: "Open the text",
      });
      if (ok) {
        // the job's own words and brand (plan §5.6 / §5.7): a concrete customer never reads "fence"
        const brand = brandOf({ company: store.company, settings: store.settings }, verticalOf(wo));
        const co = brand.name || (store.company && store.company.name) || (store.settings && store.settings.companyName) || "us";
        const done = verticalOf(wo) === "concrete" ? "your concrete work is done" : "your fence is done";
        const link = window.location.origin + "/hub/" + c.hubToken;
        const msg = "Hi " + String(cu.name || "there").split(/\s+/)[0] + ", " + done + "! " + (photos ? "Photos are up" : "Everything's") + " on your page" + (bal > 0 ? " along with the balance (" + WO_money0(bal) + ") — card or bank, whenever you're ready" : "") + ": " + link + " — thanks for choosing " + co + "!";
        window.open("sms:" + String(cu.phone).replace(/[^+\d]/g, "") + "?&body=" + encodeURIComponent(msg), "_blank");
      }
    }
  }

  // "On my way" — fire the crew-enroute automation (the on-my-way text) and
  // flash a brief Sent confirmation. No-op when the automation service is off.
  async function onMyWay() {
    const cu = custOf();
    const phone = (cu.phone || "").trim();
    // can't text without a number — guide the user to add one on the customer record
    if (!phone) { notify("Add " + (cu.name || "the customer") + "’s phone number first so we can text them."); go("customers", { focus: wo.customerId }); return; }
    // user-initiated SMS → gate on usage credits (window.VFP_CREDITS; feature-detected, allows when absent)
    const cr: any = (window as any).VFP_CREDITS;
    if (cr && cr.guard && !cr.guard(store, "sms", { go })) return;
    enrTimers.current.forEach(clearTimeout); enrTimers.current = [];
    setEnroute({ state: "sending", phone });
    if (outboxConfigured()) {
      // FW-007: the stamp and the text go as one queued op — once, with the
      // time it was tapped. No signal: it waits on this device and goes when
      // the signal is back (a text that would be over half an hour late isn't
      // sent; the stamp still lands).
      const h: any = store.fieldOp({ kind: "job.enroute", jobId: wo.id });
      const r: any = await h.sent(8000);
      if (r.outcome === "waiting") { setEnroute({ state: "saved", phone }); enrTimers.current.push(setTimeout(() => setEnroute((e: any) => (e && e.state === "saved" ? null : e)), 15000)); return; }
      if (r.outcome === "permanent") { setEnroute({ state: "blocked", phone, why: (r.body && r.body.message) || "The service refused it." }); return; }
      const d = r.body && r.body.dispatch;
      if (!d || !d.sent) { setEnroute({ state: "blocked", phone, why: d && d.why === "late" ? "too late to text; the time is saved on the job" : "The message service didn't accept it." }); return; }
    } else {
      set({ enrouteAt: Date.now() });   // the customer's portal timeline: "Crew on the way"
      const r: any = await emitAutomation("crew.enroute", { customer: { id: cu.id, name: cu.name, phone, email: cu.email }, job: { id: wo.projectId || wo.id, address: cu._address } });
      if (!r || !r.ok) {
        // the service never took it. Say why, and leave it on screen — this is the
        // one the crew has to act on, so it doesn't time out like a success does.
        setEnroute({ state: "blocked", phone, why: (r && r.detail) || "The message service didn't accept it." });
        return;
      }
    }
    // accepted by the service. That is "queued", not "delivered" — the receipt
    // comes back from the carrier callback, so poll briefly and rest at queued
    // if nothing comes. Resting at queued is honest; claiming delivered is not.
    setEnroute({ state: "queued", phone });
    const DB: any = window.VFP_DB;
    if (DB && DB.smsLastStatus) {
      const poll = (n: number) => enrTimers.current.push(setTimeout(() => {
        DB.smsLastStatus({ customerId: wo.customerId, kind: "enroute" }).then((res: any) => {
          const st = res && res.status;
          if (st === "delivered") setEnroute({ state: "delivered", phone });
          else if (st === "failed" || st === "undelivered") setEnroute({ state: "failed", phone, why: (res && res.error) || "The carrier rejected it." });
          else { if (st === "sent") setEnroute({ state: "sent", phone }); if (n < 4) poll(n + 1); }
        }).catch(() => {});
      }, 2500));
      poll(1);
    }
    // only a settled success tidies itself away; blocked/failed stay put
    enrTimers.current.push(setTimeout(() => setEnroute((e: any) => (e && (e.state === "blocked" || e.state === "failed")) ? e : null), 12000));
  }

  return (
    <div className="card" ref={cardRef} data-wo={wo.id} style={{ overflow: "hidden", borderColor: open ? st.color : "var(--line)", boxShadow: open ? "0 0 0 1px " + st.color + "22" : "none" }}>
      <div className="wo-head" {...clickable(() => onOpen ? onOpen(wo) : setOpen((o: any) => !o), { label: `Open the ${wo.customer || wo.title || "job"} job` })}>
        <span className="wo-status-dot" style={{ background: st.color }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row gap-8" style={{ minWidth: 0 }}>
            {wo.priority && <span className="prio-badge" title="Priority order"><Icon name="bolt" size={11} />Priority</span>}
            {wo.kind === "repair" && <span className="chip" style={{ fontSize: 10, padding: "1px 7px", color: "var(--violet)", borderColor: "var(--violet-tint)" }}>Repair</span>}
            {wo.kind === "warranty" && <span className="chip" style={{ fontSize: 10, padding: "1px 7px", color: "var(--amber)", borderColor: "var(--amber-tint)" }} title={wo.parentWoId ? "A warranty re-visit on an earlier job" : "Warranty re-visit"}>Warranty re-visit</span>}
            <span className="clamp2" style={{ fontWeight: 700, fontSize: 14.5, lineHeight: 1.25 }}>{wo.title || wo.customer || "Work order"}</span>
            <VerticalChip record={wo} />
          </div>
          {/* the lesson from this job, on the card, the day it closes: hours
              against the estimate and profit against the quote */}
          {wo.status === "completed" && (() => {
            const pl = woPL(wo, store, (store.settings && store.settings.crews) || []);
            if (!pl.hasActual || !(pl.est && pl.est.hours > 0)) return null;
            const dh = Math.round((pl.actualHrs - pl.est.hours) * 10) / 10;
            const dp = pl.quotedProfit != null ? Math.round(pl.actualProfit - pl.quotedProfit) : null;
            return (
              <div className="wo-costline" title="From the crew's clocked hours and logged expenses — the Job P&L card has the breakdown">
                <span style={{ color: dh > 0.5 ? "var(--amber)" : "var(--green)" }}>{dh > 0 ? "Ran " + dh + " hrs over" : dh < 0 ? "Came in " + Math.abs(dh) + " hrs under" : "On the hours"}</span>
                {dp != null && <span style={{ color: dp < 0 ? "var(--red)" : "var(--green)" }}> · {dp < 0 ? WO_money0(-dp) + " under margin target" : WO_money0(dp) + " over margin target"}</span>}
              </div>
            );
          })()}
          <div className="muted" style={{ fontSize: 12.5 }}>
            {wo.address || "No address"}{wo.materialEta && wo.status === "materials" ? " · ETA " + fmtDate(wo.materialEta) : ""}{wo.scheduledStart ? " · " + fmtDate(wo.scheduledStart) + (wo.scheduledEnd && wo.scheduledEnd !== wo.scheduledStart ? "–" + fmtDate(wo.scheduledEnd) : "") : ""}{crew ? " · " + crew.name : ""}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="display tnum" style={{ fontSize: 16, color: "var(--teal)" }}>{WO_money0(wo.total || 0)}</div>
          {labor > 0 && <div className="muted" style={{ fontSize: 11.5 }}>{WO_money0(labor)} labor</div>}
        </div>
        {hold.pending.length > 0 && <span className={"chip co-hold-chip" + (hold.overridden ? " ok" : "")} title={hold.overridden ? "Proceeding without approval (confirmed)" : "Change order awaiting the customer's approval — production on hold"}><Icon name="edit" size={11} />CO {hold.overridden ? "unapproved" : "pending"}</span>}
        <WeatherChip wo={wo} store={store} />
        <span className="chip" style={{ color: st.color, borderColor: "var(--line-2)" }}><span className="wo-pipe-dot" style={{ background: st.color }} />{st.short}</span>
        <Icon name={onOpen ? "chevright" : (open ? "chevdown" : "chevright")} size={16} className="muted" />
      </div>

      {open && !onOpen && (
        <div className="wo-body">
          {hold.pending.length > 0 && (
            <div className={"co-hold" + (hold.overridden ? " ok" : "")} data-tour="co-hold">
              <Icon name="edit" size={15} style={{ color: hold.overridden ? "var(--fg-3)" : "var(--amber)", marginTop: 2 }} />
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{hold.overridden ? "Proceeding with a change order still unapproved" : "On hold — change order awaiting " + (wo.customer ? wo.customer.split(" ")[0] + "'s" : "the customer's") + " approval"}</div>
                <div className="muted" style={{ fontSize: 12 }}>{hold.pending.map((c: any) => `${c.label} (${(Number(c.amount) || 0) >= 0 ? "+" : "−"}${WO_money0(Math.abs(Number(c.amount) || 0))})`).join(" · ")}{hold.overridden ? ` · confirmed ${timeAgo(hold.override.at)}` : " · price and scope aren't settled until it's signed on the portal, so don't schedule production yet."}</div>
              </div>
              <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                {wo.customerId && <HubLinkButton store={store} customerId={wo.customerId} label="Send approval link" />}
                <button className="btn btn-ghost btn-sm" onClick={() => go && go("quote", { quoteId: wo.quoteId })}><Icon name="clipboard" size={12} />Open the invoice</button>
                {!hold.overridden && <button className="btn btn-ghost btn-sm" style={{ color: "var(--amber)" }} onClick={() => confirmCoHold(wo, store, "Proceed")}><Icon name="bolt" size={12} />Proceed anyway…</button>}
              </div>
            </div>
          )}
          {/* stage progress */}
          <div className="wo-statusbar">
            {WO_STAGES.map((s, i) => {
              const idx = WO_STAGES.findIndex(x => x.id === wo.status);
              const done = i < idx, cur = i === idx;
              return <button key={s.id} className={"wo-stbtn" + (cur ? " on" : "") + (done ? " done" : "")}
                onClick={() => s.id === "scheduled" ? schedule() : s.id === "in_progress" ? start() : s.id === "completed" ? complete() : stepBack(s.id)}
                style={cur ? { borderColor: s.color, color: s.color } : done ? { color: s.color } : null}>
                {done && <Icon name="check" size={12} />}{s.short}</button>;
            })}
          </div>

          {/* job-level priority (the whole job, not just supplies) */}
          <div className="row" style={{ margin: "0 0 12px" }}>
            <button className={"prio-btn" + (wo.priority ? " on" : "")} onClick={() => set({ priority: !wo.priority })} title="Priority job — floats to the top of its stage in the pipeline">
              <Icon name="bolt" size={12} />{wo.priority ? "Priority job" : "Mark as priority"}
            </button>
          </div>

          {/* procurement spotlight when ordering materials */}
          {wo.status === "materials" && (
            <div className="wo-schedule" data-tour="supplies" style={{ background: "rgba(240,163,94,0.12)", borderColor: "rgba(240,163,94,0.3)" }}>
              <div className="row" style={{ marginBottom: 10 }}>
                <span className="row gap-8"><Icon name="box" size={15} style={{ color: "#f0a35e" }} /><b style={{ fontSize: 13.5 }}>Order supplies from your suppliers</b></span>
              </div>
              <label className="toggle-row" style={{ marginBottom: 10 }} onClick={() => set({ suppliesOrdered: !wo.suppliesOrdered })}>
                <span className={"sw" + (wo.suppliesOrdered ? " on" : "")}><span className="knob" /></span>
                <span style={{ fontSize: 13.5, fontWeight: 600 }}>Supplies ordered</span>
                {wo.suppliesOrdered && <span className="chip green" style={{ fontSize: 10.5, marginLeft: "auto" }}><Icon name="check" size={11} />Ordered</span>}
              </label>
              {(() => {
                const bom = woBOM(wo, store);
                if (!bom.length) return null;
                const supplierConn = (store.settings && store.settings.integrations && store.settings.integrations.supplier) || {};
                const supplierName = wo.supplier || supplierConn.name || (store.settings && store.settings.defaultSupplier) || "your supplier";
                // this job's purchase orders (numbered docs, receiving tracked)
                const jobPOs = poList(store.settings).filter((p: any) => p.workOrderId === wo.id);
                return (
                  <div className="wo-bom">
                    {(() => {
                      const lines = supplyLines(bom, wo.bomOverrides);
                      const sum = supplySummary(lines);
                      const setOrder = (label: string, v: any) => {
                        const next = { ...(wo.bomOverrides || {}), [label]: v };
                        set({ bomOverrides: tidyOverrides(bom, next) });
                      };
                      return (<>
                        <div className="row between" style={{ marginBottom: 4, flexWrap: "wrap", gap: 8 }}>
                          <span className="kicker">Supplies · what the job needs, what you'll order</span>
                          <span className="mono faint" style={{ fontSize: 11 }}>{bom.length} items</span>
                        </div>
                        <p className="faint" style={{ fontSize: 11.5, margin: "0 0 9px" }}>
                          Change any order quantity — a couple of spare panels, an extra bag or two, or short because you already have some on the truck. The gap against the job is worked out below each line.
                        </p>
                        <div className="wo-bom-head"><span>Item</span><span>Job needs</span><span>Order</span><span>Difference</span></div>
                        <div className="wo-bom-list">
                          {lines.map((l) => (
                            <div key={l.label} className={"wo-bom-row editable " + l.state}>
                              <Icon name="box" size={13} className="muted" />
                              <span className="wo-bom-label">{l.label}</span>
                              <span className="tnum wo-bom-need" title="From the estimate's bill of materials">{l.need}</span>
                              <input className="wo-bom-qty tnum" type="number" min="0" inputMode="numeric"
                                aria-label={`Quantity to order of ${l.label}`}
                                value={l.order} onChange={(e: any) => setOrder(l.label, e.target.value)} />
                              <span className={"wo-bom-delta " + l.state} title={l.math}>
                                {l.state === "exact" ? "exact" : `${l.delta > 0 ? "+" : ""}${l.delta}${l.need > 0 ? ` · ${l.delta > 0 ? "+" : ""}${l.pct.toFixed(0)}%` : ""}`}
                              </span>
                              <span className="wo-bom-math">{l.math}</span>
                            </div>
                          ))}
                        </div>
                        <div className={"wo-bom-sum" + (sum.covered ? "" : " short")}>
                          <Icon name={sum.covered ? "check" : "bolt"} size={13} />
                          <b>{sum.headline}</b>
                          <span className="faint">{sum.totalOrder} units ordered against {sum.totalNeed} the job consumes</span>
                          <span className="row gap-8" style={{ marginLeft: "auto", flexWrap: "wrap" }}>
                            {[5, 10].map(pc => (
                              <button key={pc} className="btn btn-ghost btn-sm" title={`Order ${pc}% over on every line, rounded up — the usual allowance for breakage and offcuts`}
                                onClick={() => set({ bomOverrides: tidyOverrides(bom, withWaste(bom, pc)) })}>+{pc}% spare</button>
                            ))}
                            {Object.keys(wo.bomOverrides || {}).length > 0 &&
                              <button className="btn btn-ghost btn-sm" onClick={() => set({ bomOverrides: {} })}><Icon name="undo" size={12} />Back to exact</button>}
                          </span>
                        </div>
                      </>);
                    })()}
                    {jobPOs.length > 0 && (
                      <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
                        {jobPOs.map((p: any) => {
                          const st = poDerivedStatus(p);
                          const meta = PO_STATUS[st] || PO_STATUS.draft;
                          const out = (p.lines || []).reduce((s: number, l: any) => s + Math.max(0, (Number(l.qty) || 0) - (Number(l.received) || 0)), 0);
                          return (
                            <button key={p.id} className="row between" onClick={() => setPoModal(p)}
                              style={{ width: "100%", background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 9, padding: "7px 11px", cursor: "pointer", fontSize: 12.5, color: "var(--fg)" }}>
                              <span className="mono" style={{ fontWeight: 700 }}>{p.number}<span className="muted" style={{ fontWeight: 500, fontFamily: "inherit" }}> · {p.supplier || "no supplier"}{st !== "received" && out > 0 && st !== "draft" ? ` · ${out} outstanding` : ""}</span></span>
                              <span className="chip" style={{ fontSize: 10, color: meta.color, borderColor: "var(--line-2)" }}>{meta.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {(() => {
                      // a recipe that names more than one supplier (the foam distributor, the coating house) gets one PO per supplier (§13 item 18)
                      const sups = poSuppliersOf(bom);
                      if (sups.length < 2) return (
                        <button className="btn btn-primary btn-block" style={{ marginTop: 10, background: "linear-gradient(180deg,#f5b870,#e0902f)", color: "#2a1602" }} onClick={() => setPoModal(poFromWO(wo, store))}>
                          <Icon name="send" size={14} />{jobPOs.length ? "New purchase order" : "Create purchase order for " + (sups[0] || supplierName)}
                        </button>);
                      return (
                        <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
                          {sups.map((sName: string) => { const n = bom.filter((b: any) => String((b && b.supplier) || "").trim() === sName).length; return (
                            <button key={sName || "_"} className="btn btn-primary btn-block" style={{ background: "linear-gradient(180deg,#f5b870,#e0902f)", color: "#2a1602" }} onClick={() => setPoModal(poFromWO(wo, store, { supplier: sName }))}>
                              <Icon name="send" size={14} />PO for {sName || "the items with no supplier"} · {n} item{n === 1 ? "" : "s"}
                            </button>); })}
                        </div>);
                    })()}
                    <p className="faint" style={{ fontSize: 11, textAlign: "center", margin: "6px 0 0" }}>A numbered PO you can print or email — then check materials in as they arrive; receiving updates Inventory and moves the job forward automatically.</p>
                  </div>
                );
              })()}
              <div className="wo-grid" style={{ gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)" }}>
                <div><label className="field-label">Supplier</label>
                  <input className="input" value={wo.supplier || ""} placeholder="e.g. Midwest Fence Supply" onChange={e => set({ supplier: e.target.value })} /></div>
                <div><label className="field-label">Estimated delivery date</label>
                  <input className="input" type="date" value={wo.materialEta || ""} onChange={e => set({ materialEta: e.target.value })} /></div>
              </div>
              {/* WO-04: an ETA is the supplier's promise, not a receipt. The
                  button used to say "Materials in" off an ordered flag plus a
                  date; it now says what it does — books a tentative slot after
                  the ETA — and receiving is its own, separate fact. */}
              {(() => {
                const m = matState;
                return (<>
                  <div className={"mat-state " + m.state} style={{ marginTop: 12 }}>
                    <span className="mat-dot" /><b>{m.label}</b>
                    <span className="muted">{m.detail}</span>
                    {m.pos === 0 && m.state !== "received" && wo.suppliesOrdered &&
                      <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }}
                        onClick={() => { set({ materialsReceived: true }); store.addActivity((wo.customer || "Job") + " materials received"); }}>
                        <Icon name="check" size={12} />Mark received
                      </button>}
                    {m.pos === 0 && m.state === "received" &&
                      <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }} onClick={() => set({ materialsReceived: false })}>Undo</button>}
                    {m.pos > 0 && <span className="faint" style={{ marginLeft: "auto" }}>from purchase orders</span>}
                  </div>
                  {wo.suppliesOrdered && wo.materialEta
                    ? <button className="btn btn-primary btn-block" style={{ marginTop: 10 }} onClick={() => { const earliest = addDaysStr(wo.materialEta, 1); set({ status: "unscheduled", scheduledStart: earliest, scheduledEnd: addDaysStr(earliest, Math.max(0, (days || 1) - 1)) }); store.addActivity((wo.customer || "Job") + " supplies ordered · ETA " + fmtDate(wo.materialEta)); }}>
                        <Icon name="arrow" size={15} />Book a tentative slot after {fmtDate(wo.materialEta)}
                      </button>
                    : <div className="pe-callout info" style={{ marginTop: 10 }}><p className="muted" style={{ margin: 0, fontSize: 12.5 }}>Mark supplies ordered and enter the supplier's estimated delivery date to unlock scheduling. Jobs book tentatively for after the ETA — confirm once the materials actually land.</p></div>}
                </>);
              })()}
            </div>
          )}

          {/* scheduling spotlight when unscheduled */}
          {wo.status === "unscheduled" && (
            <div className="wo-schedule">
              <div className="row gap-8" style={{ marginBottom: 10 }}><Icon name="calendar" size={15} style={{ color: "var(--amber)" }} /><b style={{ fontSize: 13.5 }}>Schedule this job</b>
                {wo.materialEta && <span className="chip" style={{ fontSize: 10.5, marginLeft: "auto", color: "#f0a35e", borderColor: "rgba(240,163,94,0.3)" }}>Materials ETA {fmtDate(wo.materialEta)}</span>}</div>
              {/* customer picked a window on their hub → one tap locks it in */}
              {wo.scheduleRequest && !wo.scheduledStart && (
                <div className="pe-callout" style={{ marginBottom: 12, borderColor: "var(--green)", background: "var(--green-tint)" }}>
                  <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
                    <Icon name="calendar" size={14} style={{ color: "var(--green)" }} />
                    <div style={{ flex: 1, minWidth: 200, fontSize: 13 }}>
                      <b>{wo.customer || "Customer"} asked for the week of {fmtDate(wo.scheduleRequest.start)}</b>
                      <span className="muted" style={{ fontSize: 12 }}> · {requestVia(wo.scheduleRequest)}{wo.scheduleRequest.at ? ", " + new Date(wo.scheduleRequest.at).toLocaleDateString() : ""}</span>
                      {/* what the caller actually said, when the AI line took it —
                          nothing checked availability, so the words matter */}
                      {wo.scheduleRequest.raw && <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>They said: “{wo.scheduleRequest.raw}”</div>}
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={() => {
                      const start = wo.scheduleRequest.start;
                      const end = wo.scheduleRequest.end && wo.scheduleRequest.end >= start ? wo.scheduleRequest.end : addDaysStr(start, Math.max(0, (days || 1) - 1));
                      schedule({ scheduledStart: start, scheduledEnd: end, scheduleRequest: { ...wo.scheduleRequest, confirmedAt: Date.now() }, calendarAdded: false });
                      store.addActivity((wo.customer || "Job") + " confirmed for " + fmtDate(start) + " — customer-picked window");
                    }}><Icon name="check" size={13} />Confirm &amp; schedule</button>
                    <button className="btn btn-ghost btn-sm" title="Clear the request (message them a different plan)" onClick={() => set({ scheduleRequest: null })}><Icon name="x" size={12} /></button>
                  </div>
                </div>
              )}
              <div className="wo-grid">
                <div><label className="field-label">Assigned crew</label>
                  <select className="select" value={wo.crewId || ""} onChange={e => set({ crewId: e.target.value })}>
                    <option value="">Unassigned</option>
                    {crews.map(c => <option key={c.id} value={c.id}>{c.name} — {WO_money0(crewRate(c))}/hr</option>)}
                  </select></div>
                <div><label className="field-label">Crew hours (est.)</label>
                  <input className="input tnum" type="number" min="0" step="0.5" value={wo.crewHours || ""} placeholder="0" onChange={e => set({ crewHours: e.target.value === "" ? null : +e.target.value })} /></div>
                <div><label className="field-label">Start date {wo._needDate && <span style={{ color: "var(--red)" }}>· required</span>}</label>
                  <input className="input" type="date" value={wo.scheduledStart || ""} onChange={e => set({ scheduledStart: e.target.value, scheduledEnd: addDaysStr(e.target.value, Math.max(0, (days || 1) - 1)), _needDate: false })} style={wo._needDate ? { borderColor: "var(--red)" } : null} /></div>
                <div><label className="field-label">End date {days ? `· ~${days}d` : ""}</label>
                  <input className="input" type="date" value={wo.scheduledEnd || ""} onChange={e => set({ scheduledEnd: e.target.value })} /></div>
              </div>
              {/* Scheduling the crew and pushing to Google are separate acts, and
                  the button did both under one label — so a contractor with no
                  Google connection got a template tab opened at them every time
                  they booked a job, and had no way to just schedule it. */}
              <button className="btn btn-primary btn-block" style={{ marginTop: 12 }} onClick={() => schedule()}>
                <Icon name="calendar" size={15} />Schedule this job
              </button>
              {gcalReady
                ? <label className="wo-cal-opt">
                    <input type="checkbox" checked={pushCal} onChange={e => setPushCal(e.target.checked)} />
                    <span>Also add it to Google Calendar<span className="faint"> — {gcalName}</span></span>
                  </label>
                : <p className="faint wo-cal-opt-note">
                    Google Calendar isn't connected, so this just books the crew here. You can still open it in Google or download a .ics once it's scheduled, or <button className="pe-add-link" onClick={() => go("integrations")}>connect it</button> to push automatically.
                  </p>}
              {/* or let the CUSTOMER pick: offer capacity-checked windows on their hub */}
              {!wo.scheduleRequest && (
                <div className="row gap-8" style={{ marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => {
                    const wins = offerWindows(store, wo, days);
                    if (!wins.length) { notify("No open weeks found in the next 10 — clear some capacity first."); return; }
                    set({ offeredWindows: wins });
                    store.addActivity((wo.customer || "Job") + ": " + wins.length + " install windows offered on the customer hub");
                  }}><Icon name="send" size={13} />{(wo.offeredWindows || []).length ? "Refresh offered windows" : "Let the customer pick — offer windows on their hub"}</button>
                  {(wo.offeredWindows || []).map((w: any, i: number) => (
                    <span key={i} className="chip" style={{ fontSize: 10.5 }}>wk of {fmtDate(w.start)}</span>
                  ))}
                  {(wo.offeredWindows || []).length > 0 && <span className="faint" style={{ fontSize: 11 }}>· live on their hub — their pick lands here to confirm</span>}
                </div>
              )}
            </div>
          )}

          {/* assignment grid for already-scheduled jobs */}
          {wo.status !== "unscheduled" && (
            <div className="wo-grid">
              <div><label className="field-label">Assigned crew</label>
                <select className="select" value={wo.crewId || ""} onChange={e => set({ crewId: e.target.value })}>
                  <option value="">Unassigned</option>
                  {crews.map(c => <option key={c.id} value={c.id}>{c.name} — {WO_money0(crewRate(c))}/hr</option>)}
                </select></div>
              <div><label className="field-label">Crew hours</label>
                <input className="input tnum" type="number" min="0" step="0.5" value={wo.crewHours || ""} placeholder="0" onChange={e => set({ crewHours: e.target.value === "" ? null : +e.target.value })} /></div>
              <div><label className="field-label">Start date</label>
                <input className="input" type="date" value={wo.scheduledStart || ""} onChange={e => {
                  // moving the start slides the end with it so the job keeps its span (never ends before it starts)
                  const v = e.target.value; const patch: any = { scheduledStart: v };
                  if (v && wo.scheduledStart && wo.scheduledEnd) {
                    const span = Math.round((new Date(wo.scheduledEnd + "T00:00:00").getTime() - new Date(wo.scheduledStart + "T00:00:00").getTime()) / 86400000);
                    patch.scheduledEnd = addDaysStr(v, Math.max(0, span));
                  } else if (v && wo.scheduledEnd && wo.scheduledEnd < v) patch.scheduledEnd = v;
                  set(patch);
                }} /></div>
              <div><label className="field-label">End date</label>
                <input className="input" type="date" min={wo.scheduledStart || undefined} value={wo.scheduledEnd || ""} onChange={e => { const v = e.target.value; set({ scheduledEnd: v && wo.scheduledStart && v < wo.scheduledStart ? wo.scheduledStart : v }); }} /></div>
            </div>
          )}

          {crew && (
            <div className="wo-crew-strip">
              <span className="wo-crew-dot" style={{ background: crew.color }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{crew.name}{crew.foreman ? " · " + crew.foreman : ""}</div>
                <div className="muted" style={{ fontSize: 11.5 }}>
                  {window.VFP.crewHeadcount(crew)} ppl · {WO_money0(crewRate(crew))}/hr loaded{crew.phone ? " · " + crew.phone : ""}
                  {crew.members && crew.members.length ? " · " + crew.members.map(m => m.name).filter(Boolean).join(", ") : ""}
                </div>
              </div>
              {wo.crewHours ? <div className="wo-days"><span className="display tnum" style={{ fontSize: 17 }}>{days}</span><span className="muted" style={{ fontSize: 11 }}>{days === 1 ? "day" : "days"} on site</span></div> : null}
            </div>
          )}
          {labor > 0 && (
            <div className="wo-labor-note">
              {wo.crewHours} crew-hrs × {WO_money0(crewRate(crew))}/hr = <b style={{ color: "var(--fg)" }}>{WO_money(labor)}</b> labor
              {wo.total ? <> · <span className="muted">job value {WO_money0(wo.total)} → gross {WO_money0(wo.total - labor)}</span></> : null}
            </div>
          )}

          {/* Site readiness sits ABOVE the money. It is pre-work — the permit,
              the locates, the site visit — and it gates starting; logging hours
              and expenses is what happens after. It used to render below the
              expense and P&L cards, so the thing that has to happen first was
              the thing you scrolled past last. */}
          <SiteReadiness wo={wo} set={set} store={store} />

          {/* time tracking / job-costing actuals — log real hours vs the estimate.
              Shown once a job is live (scheduled → in progress → completed). */}
          {(wo.status === "scheduled" || wo.status === "in_progress" || wo.status === "completed" || woTimeLogs(wo).length > 0 || woExpenses(wo).length > 0) && (
            <>
              {/* hours are the crew's own work, so everyone logs them; the money
                  beside them (rates, expenses, P&L) is cost data — roles.ts */}
              <TimeTracking wo={wo} set={set} store={store} crews={crews} />
              {storeCan(store, "money.cost") && <>
                <JobExpenses wo={wo} set={set} store={store} userName={userName} />
                <JobPL wo={wo} store={store} crews={crews} />
              </>}
            </>
          )}

          {/* crew notes, the on-site checklist, and crew updates held for review */}
          <FieldFromCrew wo={wo} store={store} />

          {/* every stamped moment on this job — quote, payments, change orders, crew, photos */}
          <JobTimeline q={(store.quotes || []).find((x: any) => x.id === wo.quoteId) || null} wo={wo} store={store} limit={8} defaultOpen={false} />

          {/* post-completion follow-up: review + referral sequence */}
          {wo.status === "completed" && (
            <div data-tour="followup"><FollowUp wo={wo} store={store} set={set} go={go} /></div>
          )}
          {/* a concrete job's warranty: the terms it carries, claims, a re-visit (lib/concrete.actuals.ts) */}
          {verticalOf(wo) === "concrete" && wo.kind !== "warranty" && (wo.status === "completed" || (Array.isArray(wo.warrantyClaims) && wo.warrantyClaims.length > 0)) && <WarrantyCard wo={wo} store={store} set={set} />}

          {/* calendar controls for scheduled jobs */}
          {wo.status !== "unscheduled" && wo.scheduledStart && (
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              <a className="btn btn-ghost btn-sm" href={gcalUrl(wo, crew)} target="_blank" rel="noopener"><Icon name="calendar" size={13} />{wo.calendarAdded ? "Re-open in Google Calendar" : "Add to Google Calendar"}</a>
              <button className="btn btn-ghost btn-sm" onClick={() => downloadIcs(wo, crew)}><Icon name="download" size={13} />.ics</button>
              {/* Scheduled in Fence Works is not the same fact as synced to Google.
                  The green badge is only claimed once Google confirmed an event id;
                  otherwise the job is booked here and the calendar is not. */}
              {wo.calendarAdded
                ? <span className="chip green" style={{ fontSize: 11 }}><Icon name="check" size={11} />Synced to Google Calendar</span>
                : <span className="chip" style={{ fontSize: 11, color: "var(--fg-3)" }} title="The job is booked in Fence Works. Nothing has confirmed it reached Google Calendar.">Scheduled here · not on Google</span>}
              {/* "On my way" → texts the homeowner the crew is rolling. The badge
                  tracks what actually happened, not that a call was attempted. */}
              {enroute
                ? <span className={"chip enroute-chip " + enroute.state} title={enroute.why || undefined}>
                    <Icon name={enroute.state === "blocked" || enroute.state === "failed" ? "x" : enroute.state === "delivered" ? "check" : enroute.state === "saved" ? "clock" : "send"} size={11} />
                    {enroute.state === "sending" ? "Sending…"
                      : enroute.state === "saved" ? "Saved — sends when you're back online"
                      : enroute.state === "queued" ? "Queued for " + enroute.phone
                      : enroute.state === "sent" ? "Sent to " + enroute.phone
                      : enroute.state === "delivered" ? "Delivered ✓"
                      : enroute.state === "blocked" ? "Not sent — " + (enroute.why || "messaging unavailable")
                      : "Couldn't deliver — " + (enroute.why || "check the number")}
                  </span>
                : <button className="btn btn-ghost btn-sm" style={!custOf().phone ? { opacity: 0.6 } : undefined} onClick={onMyWay} title={custOf().phone ? "Text the customer that the crew is on the way" : "Add the customer's phone to text them"}><Icon name="phone" size={13} />On my way</button>}
              {/* a definite failure gets a way out: call them, or try once more.
                  An ambiguous queued/sent never offers replay — that double-texts. */}
              {enroute && (enroute.state === "blocked" || enroute.state === "failed") && <>
                <a className="btn btn-ghost btn-sm" href={"tel:" + enroute.phone}><Icon name="phone" size={13} />Call instead</a>
                <button className="btn btn-ghost btn-sm" onClick={() => { setEnroute(null); onMyWay(); }}><Icon name="undo" size={13} />Try again</button>
              </>}
              <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto", color: "var(--amber)" }} onClick={() => {
                const DB: any = window.VFP_DB;
                const evId = wo.gcalEventId;
                set({ status: "unscheduled", scheduledStart: "", scheduledEnd: "", calendarAdded: false, gcalEventId: null, calendarStale: false });
                // if Google won't take the delete, keep the event id + flag it so the stale event isn't silently orphaned
                if (evId && DB && DB.gcalUnsync) DB.gcalUnsync(evId).catch(() => { set({ gcalEventId: evId, calendarStale: true }); notify("This job is still on Google Calendar — remove it manually", { kind: "error" }); });
                store.addActivity((wo.customer || "Job") + " schedule cleared");
              }}><Icon name="undo" size={13} />Clear schedule</button>
            </div>
          )}

          <WorkOrderPhotos wo={wo} set={set} />

          {/* inventory tie-in: extra rail, damaged picket, returns — the crew
              logs it here and the stock ledger references this job */}
          <LogMaterialUse wo={wo} store={store} />

          <div><label className="field-label">Notes</label>
            <textarea className="textarea" value={wo.notes || ""} placeholder="Site access, materials staged, customer requests…" onChange={e => set({ notes: e.target.value })} /></div>

          <div className="row between" style={{ marginTop: 4, flexWrap: "wrap", gap: 8 }}>
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              {wo.quoteId && <button className="btn btn-ghost btn-sm" onClick={() => go("quote", { quoteId: wo.quoteId })}><Icon name="clipboard" size={13} />Quote</button>}
              {csQuote && <button className="btn btn-ghost btn-sm" title="Printable build sheet for the install crew — specs, materials & build order" onClick={() => setCrewSheet(true)}><Icon name="printer" size={13} />Crew sheet</button>}
              {wo.customerId && <button className="btn btn-ghost btn-sm" onClick={() => go("customers", { focus: wo.customerId })}><Icon name="user" size={13} />Customer</button>}
              {wo.customerId && <HubLinkButton store={store} customerId={wo.customerId} />}
              {wo.status === "scheduled" && <button className="btn btn-outline btn-sm" onClick={start}>Start job →</button>}
              {wo.status === "in_progress" && <button className="btn btn-outline btn-sm" onClick={complete}>Mark complete →</button>}
              {/* WO-03: the prerequisites read BEFORE the button, not as a
                  dialog after it. A hard blocker is stated plainly; an
                  overridable hold says so, because it is overridable. */}
              {nextBlockers.length > 0 && (() => {
                const hard = nextBlockers.some((b: any) => b.kind === "hard");
                const gate = nextBlockers.some((b: any) => b.kind === "confirm");
                return (
                  <span className={"wo-prereq" + (!hard && !gate ? " warn-only" : "")} title={nextBlockers.map((b: any) => b.body).join("\n\n")}>
                    <Icon name={hard ? "x" : gate ? "bolt" : "clipboard"} size={12} />
                    {nextBlockers.map((b: any) => b.label).join(" · ")}
                    {!hard && <span className="faint">{gate ? " — you'll be asked to confirm" : " — won't stop you booking"}</span>}
                  </span>
                );
              })()}
            </div>
            <button className="btn btn-sm" style={{ background: "var(--red-tint)", color: "var(--red)" }} onClick={confirmDelete}><Icon name="trash" size={13} />Delete</button>
          </div>
        </div>
      )}
      {crewSheet && csQuote && <CrewBuildSheet quote={csQuote} store={store} settings={store.settings} onClose={() => setCrewSheet(false)} />}
      {poModal && <PurchaseOrderModal initial={poModal} store={store} onClose={() => setPoModal(null)} />}
    </div>
  );
}

function timeAgo(ts) {
  if (!ts) return "";
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return m + "m ago";
  const h = Math.floor(m / 60); if (h < 24) return h + "h ago";
  const d = Math.floor(h / 24); return d + "d ago";
}
function FollowUp({ wo, store, set, go }: any) {
  // the review ask goes out under the job's service brand: its review link and its name (plan §5.7)
  const brand = brandOf({ company: store.company, settings: store.settings }, verticalOf(wo));
  const reviewUrl = brand.reviewUrl || (store.settings && store.settings.googleReviewUrl) || "";
  const cust = (store.customers || []).find(c => c.id === wo.customerId);
  const email = cust && cust.email;
  // the workspace's sequences, or the app defaults the Sequences page shows
  // until they're customized (so "not set up" means deleted or paused, not "never opened")
  const seqs = (store.settings && Array.isArray(store.settings.sequences) && store.settings.sequences.length) ? store.settings.sequences : ((window.VFP && window.VFP.DEFAULT_SETTINGS && window.VFP.DEFAULT_SETTINGS.sequences) || []);
  const company = brand.name || (store.settings && store.settings.companyName) || (store.company && store.company.name) || "our team";
  const ctx = { customer: wo.customer, company, reviewLink: reviewUrl, address: wo.address, reward: "$100 Visa gift card" };
  const firstStep = (id) => { const s = seqs.find(x => x.id === id); return s && s.steps && s.steps[0]; };
  const mailto = (step) => {
    const r = window.renderTemplate || ((t) => t);
    const subj = encodeURIComponent(r(step ? step.subject : "How did we do?", ctx));
    const body = encodeURIComponent(r(step ? step.body : "", ctx));
    return "mailto:" + (email || "") + "?subject=" + subj + "&body=" + body;
  };
  function openReview() {
    set({ reviewRequested: true, reviewRequestedAt: Date.now() });
    store.addActivity("Requested a Google review from " + (wo.customer || "customer"));
    if (email) window.open(mailto(firstStep("seq-review")), "_blank");
    else if (reviewUrl) window.open(reviewUrl, "_blank", "noopener");
  }
  function requestReferral() {
    set({ referralRequested: true, referralRequestedAt: Date.now() });
    store.addActivity("Asked " + (wo.customer || "customer") + " for a referral");
    if (email) window.open(mailto(firstStep("seq-referral")), "_blank");
  }
  // warranty + the checkup: registers the workspace's warranty term (Settings →
  // warrantyYears, default 1) on the job and drops a dated follow-up task on
  // the customer a month before it lapses — the stain/seal & gate tune-up
  // visit is repeat revenue AND a built-in referral ask.
  // a concrete job's term is the longest its rate cards gave it (wo.warranty, dated at completion); a fence job's is the workspace's
  const isCx = verticalOf(wo) === "concrete";
  const cxYears = isCx && Array.isArray(wo.warranty) ? Math.max(0, ...wo.warranty.map((t: any) => Number(t.years) || 0)) : 0;
  const wYears = cxYears > 0 ? cxYears : Math.max(0.5, Number(store.settings && store.settings.warrantyYears) || 1);
  const wLabel = wYears + "-yr";
  function registerWarranty() {
    set({ warrantyRegisteredAt: Date.now(), warrantyYears: wYears });
    if (cust) {
      const d = new Date(); d.setDate(d.getDate() + Math.max(14, Math.round(wYears * 365) - 31));   // ~a month before the term ends
      const due = localYmd(d);
      const text = isCx
        ? `${wLabel} concrete check-up — ${wo.customer || "customer"}: look over the lifted sections, offer a re-seal / re-caulk (warranty visit)`
        : `${wLabel} fence checkup — ${wo.customer || "customer"}: offer stain/seal + gate tune-up (warranty visit)`;
      store.updateCustomer(cust.id, { tasks: [...(cust.tasks || []), { id: "t-" + Date.now(), text, done: false, due, createdAt: Date.now(), doneAt: null }] });
    }
    store.addActivity(`Warranty registered for ${wo.customer || "job"} — ${wLabel} checkup scheduled`);
  }
  // the care plan (§13 item 15): completing a concrete job starts a plan per
  // care type on it (app.tsx updateWorkOrder); here the owner sees it, or
  // offers the re-seal a lift didn't carry — repeat revenue every two years
  const carePlans: any[] = isCx && cust && Array.isArray(cust.carePlans) ? cust.carePlans.filter((p: any) => p && p.sourceWoId === wo.id) : [];
  const careTypes: string[] = isCx ? ((wo.cx && Array.isArray(wo.cx.blocks) ? wo.cx.blocks : []).map((b: any) => b && b.jobType).filter((t: any, i: number, a: any[]) => isCareType(t) && a.indexOf(t) === i)) : [];
  function addCarePlan(extra?: string) {
    if (!cust) return;
    const done = localYmd(new Date(wo.completedAt || Date.now()));
    const fresh = plansFromJob({ ...wo, vertical: "concrete" }, done, extra ? { extra: [extra as any] } : {});
    if (!fresh.length) return;
    store.updateCustomer(cust.id, { carePlans: mergePlans(cust.carePlans, fresh) });
    store.addActivity(`Care plan set for ${wo.customer || "customer"} — ${fresh.map((p: any) => CARE_LABEL[p.jobType] + " " + everyLabel(p.everyMonths)).join(", ")}`);
  }
  // "what actually goes out": the sequence's emails, rendered for this customer
  const [preview, setPreview] = React.useState<any>(null);   // "seq-review" | "seq-referral"
  const seqOf = (id) => seqs.find(x => x.id === id);
  const renderT = (t) => { const r = window.renderTemplate || ((x) => x); return r(t || "", ctx); };
  const editSeq = (id) => { if (go) go("sequences", { seq: id }); };
  const seqState = (id) => { const sq = seqOf(id); return !sq ? "missing" : sq.on === false ? "off" : "on"; };
  // the warning next to the button when the sequence won't actually send
  const seqWarn = (id) => { const st = seqState(id); if (st === "on") return null;
    return <button className="fu-mark fu-warn" onClick={() => editSeq(id)} title={st === "off" ? "This sequence is paused — nothing will send. Click to open it." : "This sequence was removed — click to set it up."}><Icon name="bolt" size={11} />{st === "off" ? "Paused — turn on" : "Not set up"}</button>; };
  const previewBtn = (id) => <>{seqWarn(id)}<button className="fu-mark" onClick={() => setPreview(p => p === id ? null : id)} title="See the emails this sends">{preview === id ? "Hide emails" : "Preview emails"}</button></>;
  const seqPreview = (id) => { const sq = seqOf(id); if (!sq) return <div className="fu-hint" style={{ marginTop: 6 }}><Icon name="bolt" size={12} style={{ color: "var(--amber)" }} /> This sequence isn't set up, so nothing sends. <a style={{ color: "var(--teal)", cursor: "pointer" }} onClick={() => editSeq(id)}>Set it up in Email Sequences →</a></div>;
    return (
      <div className="fu-preview" data-tour="fu-preview">
        {(sq.steps || []).map((st, i) => (
          <div key={i} className="seq-preview-box" style={{ marginTop: i ? 8 : 0 }}>
            <div className="row between" style={{ marginBottom: 6, gap: 8 }}><b style={{ fontSize: 13 }}>{renderT(st.subject)}</b><span className="faint mono" style={{ fontSize: 10.5, flexShrink: 0 }}>{st.delayDays ? "day " + st.delayDays : "day 0 · on completion"}{email ? " · to " + email : ""}</span></div>
            <div style={{ whiteSpace: "pre-wrap", color: "var(--fg-2)", fontSize: 12.5, lineHeight: 1.5 }}>{renderT(st.body)}</div>
          </div>
        ))}
        <div className="faint" style={{ fontSize: 11, marginTop: 6 }}>{sq.on === false ? <span style={{ color: "var(--amber)" }}>This sequence is paused — nothing sends until it's turned on. </span> : <>Sent automatically by the {sq.name} sequence when the job completes. </>}<a style={{ color: "var(--teal)", cursor: "pointer" }} onClick={() => editSeq(id)}>{sq.on === false ? "Turn it on / edit →" : "Edit this sequence →"}</a></div>
      </div>);
  };
  const steps = [
    { key: "warranty", done: !!wo.warrantyRegisteredAt, label: `Register warranty · book the ${wLabel} checkup`,
      sub: wo.warrantyRegisteredAt ? `Registered ${timeAgo(wo.warrantyRegisteredAt)} · checkup task waits on the customer's To-Do` : `${wLabel} workmanship warranty + care visit = repeat revenue & another referral ask`,
      action: <button className="btn btn-ghost btn-sm" onClick={registerWarranty} disabled={!!wo.warrantyRegisteredAt}><Icon name="check" size={13} />{wo.warrantyRegisteredAt ? "Registered" : "Register + schedule"}</button>,
      extra: null },
    ...(isCx ? [{ key: "care", done: carePlans.length > 0,
      label: carePlans.length ? "Care plan · " + carePlans.map((p: any) => planLabel(p)).join(" · ") : (careTypes.length ? "Start the care plan · " + careTypes.map((t: string) => CARE_LABEL[t as any]).join(", ") : "Offer a re-seal plan · keeps the lifted slab dry"),
      sub: carePlans.length ? `Next due ${carePlans.map((p: any) => p.nextDue).sort()[0]} · on the To-Do 45 days out · one tap books it from the customer's page` : (careTypes.length ? "A reminder on the To-Do and a one-tap estimate off today's prices, every 1–2 years" : "Water under a slab is what sank it — a seal coat every two years is the follow-up sale"),
      action: carePlans.length
        ? <button className="btn btn-ghost btn-sm" onClick={() => { if (go) go("customers", { focus: cust && cust.id }); }}><Icon name="calendar" size={13} />See plan</button>
        : <button className="btn btn-ghost btn-sm" disabled={!cust} onClick={() => addCarePlan(careTypes.length ? undefined : "seal")}><Icon name="calendar" size={13} />{careTypes.length ? "Start the plan" : "Add a re-seal plan"}</button>,
      extra: null }] : []),
    { key: "review", done: wo.reviewRequested, label: "Request Google review",
      sub: wo.reviewReceived ? "Review received ★" : (wo.reviewRequested ? "Requested " + timeAgo(wo.reviewRequestedAt) : "Day 0 · right after completion"),
      action: <button className="btn btn-primary btn-sm" data-tour="request-review" onClick={openReview}><Icon name="send" size={13} />{wo.reviewRequested ? "Send again" : "Request review"}</button>,
      extra: <>{previewBtn("seq-review")}{wo.reviewRequested && !wo.reviewReceived ? <button className="fu-mark" onClick={() => { set({ reviewReceived: true, reviewReceivedAt: Date.now() }); store.addActivity((wo.customer || "Customer") + " left a review ★"); }}>Mark received</button> : null}</>,
      preview: preview === "seq-review" ? seqPreview("seq-review") : null },
    { key: "referral", done: wo.referralRequested, label: "Ask for a referral · $100 Visa bonus",
      sub: wo.referralConverted ? "Referral converted 🎉 — send the $100 Visa card" : (wo.referralRequested ? "Requested " + timeAgo(wo.referralRequestedAt) + " · 4-email sequence over 90 days" : "Day 0 → 2 → 30 → 90 · $100 per referral"),
      action: <button className="btn btn-ghost btn-sm" data-tour="request-referral" onClick={requestReferral}><Icon name="user" size={13} />{wo.referralRequested ? "Ask again" : "Ask for referral"}</button>,
      extra: <>{previewBtn("seq-referral")}{wo.referralRequested && !wo.referralConverted ? <button className="fu-mark" onClick={() => { set({ referralConverted: true }); store.addActivity("Referral from " + (wo.customer || "customer") + " converted 🎉"); }}>Mark converted</button> : null}</>,
      preview: preview === "seq-referral" ? seqPreview("seq-referral") : null },
  ];
  return (
    <div className="fu-panel">
      <div className="row between" style={{ marginBottom: 10 }}>
        <span className="row gap-8"><Icon name="check" size={15} style={{ color: "var(--green)" }} /><b style={{ fontSize: 13.5 }}>Job complete — review, referral, warranty</b></span>
        <span className="faint" style={{ fontSize: 11 }}>Also on the customer's profile</span>
      </div>
      {!reviewUrl && <div className="fu-hint"><Icon name="bolt" size={12} style={{ color: "var(--amber)" }} /> No Google review link yet — the review email has nowhere to send them. <a style={{ color: "var(--teal)", cursor: "pointer" }} onClick={() => { if (go) go("settings", { section: "social" }); }}>Add it under Settings → Customer-facing →</a></div>}
      <div style={{ display: "grid", gap: 8 }}>
        {steps.map(s => (
          <div key={s.key} className={"fu-step" + (s.done ? " done" : "")}>
            <span className="fu-dot">{s.done ? <Icon name="check" size={12} /> : null}</span>
            <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontWeight: 600, fontSize: 13 }}>{s.label}</div><div className="muted" style={{ fontSize: 11.5 }}>{s.sub}</div></div>
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>{s.extra}{s.action}</div>
            {(s as any).preview && <div style={{ flexBasis: "100%" }}>{(s as any).preview}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* FieldFromCrew — what the crew sent from the site (Crew Mode, lib/outbox.ts):
   their notes, the on-site checklist, "on my way", and anything the service
   held for the office to review (entered more than 72 hours before it synced,
   or a clock-in outside the site fence without an override). Anything that
   arrived late says when it was entered and when it synced. */
/* A concrete job's warranty (roadmap §13 item 19): the terms the job carries
   (from the rate cards, dated the day it finished), a claim log, and a
   re-visit — a work order of its own, kind "warranty", linked back here, never
   sharing the quote (so it costs nothing twice and chases no balance). */
function WarrantyCard({ wo, store, set }: any) {
  const terms: any[] = Array.isArray(wo.warranty) ? wo.warranty : (wo.cx && Array.isArray(wo.cx.warranty) ? wo.cx.warranty : []);
  const claims: any[] = Array.isArray(wo.warrantyClaims) ? wo.warrantyClaims : [];
  const [issue, setIssue] = React.useState<any>("");
  const [open, setOpen] = React.useState<any>(false);
  const canEdit = storeCan(store, "jobs");
  const revisits = (store.workOrders || []).filter((w: any) => w && w.parentWoId === wo.id);
  const today = localYmd(new Date());
  function logClaim() {
    const text = String(issue || "").trim();
    if (!text) return;
    const c = { id: "wc-" + Date.now().toString(36), at: Date.now(), issue: text.slice(0, 600), outcome: "open", revisitWoId: null };
    set({ warrantyClaims: [...claims, c] });
    store.addActivity && store.addActivity(`Warranty claim on ${wo.customer || "job"}: ${text.slice(0, 80)}`);
    setIssue(""); setOpen(false);
  }
  const patchClaim = (id: string, p: any) => set({ warrantyClaims: claims.map((c) => (c.id === id ? { ...c, ...p } : c)) });
  function scheduleRevisit(c: any) {
    const id = newId("wo-");
    const types = Array.isArray(wo.jobTypes) ? wo.jobTypes : [];
    store.addWorkOrder({
      id, kind: "warranty", parentWoId: wo.id, vertical: "concrete", customerId: wo.customerId, customer: wo.customer, address: wo.address,
      title: `Warranty re-visit — ${wo.customer || "job"}`, jobTypes: types, crewId: wo.crewId || null, crewHours: 1.5,
      notes: `Warranty claim: ${c.issue}`, status: "unscheduled", total: 0, cx: wo.cx ? { blocks: wo.cx.blocks, slabs: wo.cx.slabs } : undefined,
    });
    patchClaim(c.id, { outcome: "revisit", revisitWoId: id });
    store.addActivity && store.addActivity(`Warranty re-visit scheduled for ${wo.customer || "job"}`);
    notify("Re-visit added to the jobs list — schedule it from there. It won't ask the customer for another review.");
  }
  if (!terms.length && !claims.length && !canEdit) return null;
  return (
    <div className="wo-ready wo-warranty" data-testid="wo-warranty">
      <div className="row between" style={{ gap: 8, flexWrap: "wrap" }}>
        <span className="row gap-8 kicker"><Icon name="check" size={13} style={{ color: "var(--teal)" }} />Warranty</span>
        {canEdit && !open && <button className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}><Icon name="plus" size={12} />Log a claim</button>}
      </div>
      {terms.length ? terms.map((t: any, i: number) => (
        <div key={i} className="row gap-8" style={{ fontSize: 12.5, flexWrap: "wrap" }}>
          <b>{CX_JOB_SHORT[t.jobType] || t.jobType}</b>
          {t.years ? <span className={"chip" + (t.until && t.until < today ? "" : " green")} style={{ fontSize: 10.5 }}>{t.years} yr{t.years === 1 ? "" : "s"}{t.until ? (t.until < today ? ` · ended ${t.until}` : ` · through ${t.until}`) : ""}</span> : <span className="faint" style={{ fontSize: 11.5 }}>no term set on the card</span>}
          {t.text && <span className="muted" style={{ flexBasis: "100%" }}>{t.text}</span>}
        </div>
      )) : <p className="faint" style={{ fontSize: 12, margin: 0 }}>No warranty terms on this job — set years and wording on each rate card (Products → Rate cards); new jobs carry them.</p>}
      {open && (
        <div className="row gap-8" style={{ flexWrap: "wrap", marginTop: 4 }}>
          <input className="input" style={{ flex: "1 1 240px" }} placeholder="What the customer reported — e.g. the apron settled ½″ at the joint" value={issue} onChange={(e) => setIssue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") logClaim(); }} />
          <button className="btn btn-primary btn-sm" disabled={!String(issue).trim()} onClick={logClaim}><Icon name="check" size={12} />Log claim</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>Cancel</button>
        </div>
      )}
      {claims.map((c) => {
        const rv = c.revisitWoId ? (store.workOrders || []).find((w: any) => w && w.id === c.revisitWoId) : null;
        return (
          <div key={c.id} className="wo-claim">
            <div className="row between" style={{ gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13 }}><b>{new Date(c.at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</b> · {c.issue}</span>
              {canEdit && <select className="select" style={{ width: "auto", fontSize: 12 }} value={c.outcome || "open"} onChange={(e) => patchClaim(c.id, { outcome: e.target.value, ...(e.target.value === "fixed" || e.target.value === "declined" ? { closedAt: Date.now() } : {}) })}>
                <option value="open">Open</option><option value="revisit">Re-visit scheduled</option><option value="fixed">Fixed</option><option value="declined">Not covered</option>
              </select>}
            </div>
            <div className="row gap-8" style={{ flexWrap: "wrap", marginTop: 4 }}>
              {rv ? <span className="muted" style={{ fontSize: 12 }}>Re-visit: {rv.scheduledStart ? `scheduled ${rv.scheduledStart}` : rv.status === "completed" ? "done" : "unscheduled — on the jobs list"}</span>
                : canEdit && c.outcome !== "fixed" && c.outcome !== "declined" && <button className="btn btn-outline btn-sm" onClick={() => scheduleRevisit(c)}><Icon name="calendar" size={12} />Schedule a re-visit</button>}
            </div>
          </div>
        );
      })}
      {revisits.length > 0 && !claims.some((c) => c.revisitWoId) && <p className="faint" style={{ fontSize: 11.5, margin: 0 }}>{revisits.length} re-visit{revisits.length === 1 ? "" : "s"} on this job.</p>}
    </div>
  );
}

function FieldFromCrew({ wo, store }: any) {
  const notes = Array.isArray(wo.fieldNotes) ? wo.fieldNotes : [];
  const check = wo.fieldChecklist || {};
  const ticked = [...CHECK_LIST, ...CONCRETE_CHECK_LIST, ...CROSS_SELL_CHECKS].filter(([id]) => check[id]);
  const held = Array.isArray(wo.heldOps) ? wo.heldOps : [];
  const lifts = Array.isArray(wo.liftLog) ? wo.liftLog : [];
  const [busy, setBusy] = React.useState<any>(null);
  if (!notes.length && !ticked.length && !held.length && !wo.enrouteOffline && !lifts.length) return null;
  const canDecide = storeCan(store, "jobs");
  async function decide(h: any, action: "approve" | "discard") {
    const DB: any = window.VFP_DB;
    if (!DB || !DB.crewHeld) return;
    setBusy(h.key);
    try {
      const r = await DB.crewHeld(wo.id, h.key, action);
      if (r && r.data) store.adoptWorkOrder({ ...r.data, id: wo.id });
      notify(action === "approve" ? opLabel(h) + " approved — it's on the job at " + hhmm(h.at) + "." : opLabel(h) + " discarded.", { kind: "success" });
    } catch (e: any) { notify(String((e && e.message) || e), { kind: "error" }); }
    finally { setBusy(null); }
  }
  return (
    <div className="wo-ready wo-field" data-testid="wo-field">
      <span className="row gap-8 kicker"><Icon name="clipboard" size={13} style={{ color: "var(--teal)" }} />From the field</span>
      {held.map((h: any) => (
        <div key={h.key} className="wo-held">
          <div><b>Held for review:</b> {opLabel(h)} · {new Date(h.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
          <div className="muted" style={{ fontSize: 12.5 }}>{h.message || "The service held this for the office."}{h.receivedAt ? " Synced " + new Date(h.receivedAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) + "." : ""}</div>
          {canDecide && <div className="row gap-8">
            <button className="btn btn-primary btn-sm" disabled={busy === h.key} onClick={() => decide(h, "approve")}><Icon name="check" size={12} />Approve</button>
            <button className="btn btn-ghost btn-sm" disabled={busy === h.key} onClick={() => decide(h, "discard")}><Icon name="trash" size={12} />Discard</button>
          </div>}
        </div>
      ))}
      {wo.enrouteAt && wo.enrouteOffline && <div className="gps-note"><Icon name="send" size={12} />On the way at {hhmm(wo.enrouteAt)}<span className="gps-badge offline">{offlineText(wo.enrouteOffline)}</span></div>}
      {/* the lift log: each section's before, after, foam and holes, against the estimate the job carries */}
      <LiftLogSummary wo={wo} />
      <FoamDrawCard wo={wo} store={store} />
      {ticked.length > 0 && <div className="row gap-8" style={{ flexWrap: "wrap" }}>
        {ticked.map(([id, label]) => <span key={id} className={"gps-badge" + (check[id].done ? " ok" : "")} title={check[id].offline ? offlineText(check[id].offline) : "Ticked at " + hhmm(check[id].at)}>{check[id].done ? "✓ " : "✗ "}{label}</span>)}
      </div>}
      {notes.slice().reverse().map((n: any) => (
        <div key={n.id} className="wo-field-note">
          <span>{n.text}</span>
          <span className="faint" style={{ fontSize: 11.5 }}>Crew note · {new Date(n.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
          {n.offline && <span className="gps-badge offline">{offlineText(n.offline)}</span>}
        </div>
      ))}
    </div>
  );
}

/* TimeTracking — log actual labor hours against a job and compare to the
   estimate. In-app manual entry only for now; GPS / mobile clock-in arrives
   with the native app. Entries persist as wo.timeLogs via updateWorkOrder. */
function TimeTracking({ wo, set, store, crews }: any) {
  const logs = woTimeLogs(wo);
  const crew = (crews || []).find(c => c.id === wo.crewId);
  const rate = crewRate(crew);
  const est = woEstLabor(wo, store, crews);
  const actual = actualHours(wo);
  const actualCost = actual * rate;

  const today = localYmd();
  const [adding, setAdding] = React.useState<any>(false);
  const [f, setF] = React.useState<any>({ hours: "", date: today, crewId: wo.crewId || "", note: "" });
  const setForm = (k, v) => setF(s => ({ ...s, [k]: v }));
  function startAdd() { setF({ hours: "", date: today, crewId: wo.crewId || "", note: "" }); setAdding(true); }
  function addEntry() {
    const hours = +f.hours;
    if (!hours || hours <= 0) return;
    const entry: any = { id: "tl-" + Date.now(), hours, date: f.date || today, createdAt: Date.now() };
    if (f.crewId) entry.crewId = f.crewId;
    if ((f.note || "").trim()) entry.note = f.note.trim();
    set({ timeLogs: [...logs, entry] });
    store.addActivity(`Logged ${hours} hr${hours === 1 ? "" : "s"} on ${wo.customer || wo.title || "a job"}`);
    setAdding(false);
  }
  function removeEntry(id) { set({ timeLogs: logs.filter(e => e.id !== id) }); }
  // office adjustment: the entry keeps its original hours + who/why, so a
  // GPS-override or a mis-clocked day is corrected without losing the trail
  const [adj, setAdj] = React.useState<any>(null);   // { id, hours, reason }
  function saveAdjust() {
    if (!adj) return;
    const h = Math.round((+adj.hours || 0) * 4) / 4;
    const reason = String(adj.reason || "").trim();
    if (!(h > 0)) { notify("Enter the corrected hours."); return; }
    if (!reason) { notify("Give a reason — it's kept on the entry."); return; }
    set({ timeLogs: logs.map(e => e.id === adj.id ? { ...e, hours: h, adjusted: { from: Number(e.adjusted && e.adjusted.from != null ? e.adjusted.from : e.hours) || 0, at: Date.now(), reason, by: "office" } } : e) });
    store.addActivity(`Adjusted a time entry on ${wo.customer || wo.title || "a job"} to ${h} hr — ${reason}`);
    setAdj(null);
  }

  // over/under vs the estimate (only meaningful once we have an estimate)
  const hasEst = est.hours > 0;
  const deltaH = actual - est.hours;
  const over = hasEst && deltaH > 0.001;
  const pct = hasEst ? Math.round((actual / est.hours) * 100) : 0;
  const crewName = (id) => { const c = (crews || []).find(x => x.id === id); return c ? c.name : ""; };

  return (
    <div className="wo-ready">
      <div className="row between" style={{ marginBottom: 10 }}>
        <span className="row gap-8 kicker"><Icon name="clock" size={13} style={{ color: "var(--teal)" }} />Time tracking · actual hours</span>
        <span className="mono" style={{ fontSize: 11, color: "var(--fg-3)" }}>{actual ? actual + " hr logged" : "none logged"}</span>
      </div>

      {/* estimated vs actual readout */}
      <div className="wo-grid" style={{ gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 10, marginBottom: 10 }}>
        <div className="wo-labor-note" style={{ padding: "9px 11px" }}>
          <div className="faint" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".06em" }}>Estimated labor</div>
          <div className="tnum" style={{ fontWeight: 700, fontSize: 15, color: "var(--fg)" }}>{hasEst ? est.hours.toFixed(1) + " hr" : "—"}</div>
          <div className="muted" style={{ fontSize: 11.5 }}>{hasEst ? WO_money0(est.cost) + (est.source === "quote" ? " · from quote" : "") : "Set an estimate"}</div>
        </div>
        <div className="wo-labor-note" style={{ padding: "9px 11px" }}>
          <div className="faint" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".06em" }}>Actual logged</div>
          <div className="tnum" style={{ fontWeight: 700, fontSize: 15, color: "var(--fg)" }}>{actual ? actual.toFixed(1) + " hr" : "0 hr"}</div>
          <div className="muted" style={{ fontSize: 11.5 }}>{rate > 0 ? WO_money0(actualCost) + " @ " + WO_money0(rate) + "/hr" : "Assign a crew for $"}</div>
        </div>
        <div className="wo-labor-note" style={{ padding: "9px 11px", borderColor: hasEst && actual > 0 ? (over ? "var(--red-tint-2,rgba(248,113,113,0.35))" : "rgba(52,211,153,0.35)") : "var(--line)" }}>
          <div className="faint" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".06em" }}>{over ? "Over estimate" : "Variance"}</div>
          {hasEst && actual > 0
            ? <>
                <div className="tnum" style={{ fontWeight: 700, fontSize: 15, color: over ? "var(--red)" : "var(--green)" }}>
                  {over ? "+" : ""}{deltaH.toFixed(1)} hr
                </div>
                <div className="muted" style={{ fontSize: 11.5, color: over ? "var(--red)" : "var(--green)" }}>{pct}% of estimate{over ? " — ran long" : " — on track"}</div>
              </>
            : <div className="muted" style={{ fontSize: 12, paddingTop: 4 }}>{hasEst ? "Log time to compare" : "No estimate yet"}</div>}
        </div>
      </div>

      {/* on the clock right now (Crew Mode clock-ins that haven't clocked out) */}
      {Object.keys(wo.clockOpen || {}).length > 0 && (
        <div style={{ display: "grid", gap: 7, marginBottom: 10 }} data-testid="wo-clock-open">
          {Object.entries(wo.clockOpen || {}).map(([sid, s]: any) => (
            <div key={sid} className="ready-row">
              <Icon name="clock" size={13} style={{ color: "var(--teal)" }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{s.breakAt ? "On break since " + hhmm(s.breakAt) : "On the clock since " + hhmm(s.at)}{s.crewId && crewName(s.crewId) ? <span className="muted" style={{ fontWeight: 500 }}> · {crewName(s.crewId)}</span> : null}</div>
                <div className="row gap-8" style={{ flexWrap: "wrap", marginTop: 3 }}>
                  {s.offline && <span className="gps-badge offline">{offlineText(s.offline)}</span>}
                  {s.override && <span className="gps-badge override">GPS override · {s.override.reason}</span>}
                  {s.gps && !s.override && s.gps.ok && <span className="gps-badge ok">✓ on site{s.gps.distMi != null ? " · " + s.gps.distMi + " mi" : ""}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* logged entries */}
      {logs.length > 0 && (
        <div style={{ display: "grid", gap: 7, marginBottom: 10 }}>
          {logs.slice().sort((a, b) => (b.date || "").localeCompare(a.date || "")).map(e => (
            <div key={e.id} className="ready-row">
              <Icon name="clock" size={13} className="muted" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  <span className="tnum">{(Number(e.hours) || 0)} hr</span>
                  <span className="muted" style={{ fontWeight: 500 }}> · {fmtDate(e.date)}</span>
                  {e.crewId && crewName(e.crewId) ? <span className="muted" style={{ fontWeight: 500 }}> · {crewName(e.crewId)}</span> : null}
                </div>
                {e.note ? <div className="muted" style={{ fontSize: 11.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.note}</div> : null}
                {(e.gps || e.clockInAt || e.adjusted || e.offline || e.offlineIn) && (
                  <div className="row gap-8" style={{ flexWrap: "wrap", marginTop: 3 }}>
                    {e.clockInAt && <span className="gps-badge" title="Clocked from Crew Mode">{new Date(e.clockInAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} → {e.clockOutAt ? new Date(e.clockOutAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "…"}</span>}
                    {e.offlineIn && <span className="gps-badge offline" title="The clock-in was made with no signal">clock-in {offlineText(e.offlineIn)}</span>}
                    {e.offline && <span className="gps-badge offline" title="The clock-out was made with no signal">{e.offlineIn ? "clock-out " : ""}{offlineText(e.offline)}</span>}
                    {e.gps && e.gps.override && <span className="gps-badge override" title={"Override reason: " + (e.gps.override.reason || "")}>GPS override · {e.gps.override.reason}</span>}
                    {e.gps && !e.gps.override && e.gps.ok && <span className="gps-badge ok">✓ on site{e.gps.distMi != null ? " · " + e.gps.distMi + " mi" : ""}</span>}
                    {e.gps && !e.gps.override && e.gps.ok === false && <span className="gps-badge far">{e.gps.distMi != null ? e.gps.distMi + " mi from site" : "no GPS fix"}</span>}
                    {e.gps && e.gps.noSite && <span className="gps-badge">no site pin</span>}
                    {e.adjusted && <span className="gps-badge" title={"Was " + e.adjusted.from + " hr — " + (e.adjusted.reason || "")}>adjusted from {e.adjusted.from} hr · {e.adjusted.reason}</span>}
                  </div>
                )}
                {adj && adj.id === e.id && (
                  <div className="row gap-8" style={{ flexWrap: "wrap", marginTop: 6 }}>
                    <input className="input tnum" type="number" min="0.25" step="0.25" style={{ width: 90 }} value={adj.hours} onChange={ev => setAdj({ ...adj, hours: ev.target.value })} onKeyDown={ev => { if (ev.key === "Enter") saveAdjust(); if (ev.key === "Escape") setAdj(null); }} autoFocus />
                    <input className="input" style={{ flex: "1 1 180px" }} placeholder="Reason — kept on the entry" value={adj.reason} onChange={ev => setAdj({ ...adj, reason: ev.target.value })} onKeyDown={ev => { if (ev.key === "Enter") saveAdjust(); if (ev.key === "Escape") setAdj(null); }} />
                    <button className="btn btn-primary btn-sm" onClick={saveAdjust}><Icon name="check" size={12} />Save</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setAdj(null)}>Cancel</button>
                  </div>
                )}
              </div>
              <span className="mono faint" style={{ fontSize: 11 }}>{rate > 0 ? WO_money0((Number(e.hours) || 0) * rate) : ""}</span>
              <button className="pe-del" title="Adjust hours (office override — original kept)" onClick={() => setAdj({ id: e.id, hours: String(e.hours), reason: "" })}><Icon name="edit" size={12} /></button>
              <button className="pe-del" title="Delete entry" onClick={() => removeEntry(e.id)}><Icon name="trash" size={12} /></button>
            </div>
          ))}
          <div className="row between" style={{ padding: "2px 10px 0", fontSize: 12.5 }}>
            <span className="muted">Total actual</span>
            <span className="tnum" style={{ fontWeight: 700 }}>{actual.toFixed(1)} hr{rate > 0 ? " · " + WO_money0(actualCost) : ""}</span>
          </div>
        </div>
      )}

      {/* log-time form */}
      {!adding
        ? <button className="btn btn-ghost btn-sm" onClick={startAdd}><Icon name="plus" size={12} />Log time</button>
        : <div className="card pad" style={{ display: "grid", gap: 10, borderColor: "var(--teal-tint-2)" }}>
            <div className="wo-grid" style={{ gridTemplateColumns: "100px minmax(0,1fr) minmax(0,1fr)", gap: 10 }}>
              <div><label className="field-label">Hours</label>
                <input className="input tnum" type="number" min="0" step="0.25" autoFocus value={f.hours} placeholder="0" onChange={e => setForm("hours", e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") addEntry(); if (e.key === "Escape") setAdding(false); }} /></div>
              <div><label className="field-label">Date</label>
                <input className="input" type="date" value={f.date} onChange={e => setForm("date", e.target.value)} /></div>
              <div><label className="field-label">Crew (optional)</label>
                <select className="select" value={f.crewId} onChange={e => setForm("crewId", e.target.value)}>
                  <option value="">Unassigned</option>
                  {(crews || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
            </div>
            <div><label className="field-label">Note (optional)</label>
              <input className="input" value={f.note} placeholder="What was done…" onChange={e => setForm("note", e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") addEntry(); if (e.key === "Escape") setAdding(false); }} /></div>
            <div className="row gap-8">
              <button className="btn btn-primary btn-sm" disabled={!(+f.hours > 0)} onClick={addEntry}><Icon name="check" size={13} />Add entry</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setAdding(false)}>Cancel</button>
              <span className="faint" style={{ fontSize: 11, marginLeft: "auto", alignSelf: "center" }}>Crew Mode clock-ins land here with their GPS check</span>
            </div>
          </div>}
    </div>
  );
}

/* JobExpenses — log real costs against a job (materials, dump fees, rentals,
   subs). Any team member on the shared workspace can add them; they feed the
   actual side of the P&L. Mirrors TimeTracking's shape. */
function JobExpenses({ wo, set, store, userName }: any) {
  const items = woExpenses(wo);
  const total = expensesTotal(wo);          // job cost — the P&L number
  const offJob = offJobTotal(wo);           // tools + overhead, shown but not charged
  const today = localYmd();
  const [adding, setAdding] = React.useState<any>(false);
  const [f, setF] = React.useState<any>({ amount: "", category: EXPENSE_CATS[0], scope: "job", vendor: "", date: today, note: "" });
  const setForm = (k, v) => setF(s => ({ ...s, [k]: v }));
  // choosing a tool or a meal moves the scope for you; picking the scope by
  // hand afterwards sticks, so the guess is never in the way
  const setCategory = (v) => setF(s => ({ ...s, category: v, scope: s._scopeTouched ? s.scope : (CAT_SCOPE[v] || "job") }));
  const setScope = (v) => setF(s => ({ ...s, scope: v, _scopeTouched: true }));
  function startAdd() { setF({ amount: "", category: EXPENSE_CATS[0], scope: "job", vendor: "", date: today, note: "" }); setAdding(true); }
  function addEntry() {
    const amount = +f.amount;
    if (!amount || amount <= 0) return;
    const entry: any = { id: "ex-" + Date.now(), amount, category: f.category, scope: f.scope || "job", date: f.date || today };
    if ((f.vendor || "").trim()) entry.vendor = f.vendor.trim();
    if ((f.note || "").trim()) entry.note = f.note.trim();
    if ((userName || "").trim()) entry.by = userName.trim();
    set({ expenses: [...items, entry] });
    const where = entry.scope === "job" ? `on ${wo.customer || wo.title || "a job"}` : `logged — ${scopeLabel(entry.scope).toLowerCase()}`;
    store.addActivity(`${WO_money0(amount)} expense ${where}${f.vendor ? " · " + f.vendor : ""}`);
    setAdding(false);
  }
  function removeEntry(id) { set({ expenses: items.filter(e => e.id !== id) }); }
  return (
    <div className="wo-ready">
      <div className="row between" style={{ marginBottom: 10 }}>
        <span className="row gap-8 kicker"><Icon name="dollar" size={13} style={{ color: "var(--teal)" }} />Expenses · actual costs</span>
        <span className="mono" style={{ fontSize: 11, color: "var(--fg-3)" }}>{total > 0 ? WO_money0(total) + " on this job" : offJob > 0 ? "none on this job" : "none logged"}</span>
      </div>
      {items.length > 0 && (
        <div style={{ display: "grid", gap: 7, marginBottom: 10 }}>
          {items.slice().sort((a, b) => (b.date || "").localeCompare(a.date || "")).map(e => (
            <div key={e.id} className="ready-row">
              <span className={"wo-exp-cat" + (expScope(e) !== "job" ? " off" : "")}>{e.category || "Other"}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  <span className="tnum">{WO_money0(e.amount)}</span>
                  {expScope(e) !== "job" ? <span className="wo-exp-scope">{scopeLabel(expScope(e))}</span> : null}
                  {e.vendor ? <span className="muted" style={{ fontWeight: 500 }}> · {e.vendor}</span> : null}
                  <span className="muted" style={{ fontWeight: 500 }}> · {fmtDate(e.date)}</span>
                  {e.by ? <span className="muted" style={{ fontWeight: 500 }}> · {e.by}</span> : null}
                </div>
                {e.note ? <div className="muted" style={{ fontSize: 11.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.note}</div> : null}
              </div>
              <button className="pe-del" title="Delete expense" onClick={() => removeEntry(e.id)}><Icon name="trash" size={12} /></button>
            </div>
          ))}
          <div className="row between" style={{ padding: "2px 10px 0", fontSize: 12.5 }}>
            <span className="muted">Charged to this job</span>
            <span className="tnum" style={{ fontWeight: 700 }}>{WO_money0(total)}</span>
          </div>
          {offJob > 0 && (
            <div className="row between" style={{ padding: "0 10px", fontSize: 12.5 }}>
              <span className="muted">Logged here, not this job’s cost</span>
              <span className="tnum muted" style={{ fontWeight: 600 }}>{WO_money0(offJob)}</span>
            </div>
          )}
        </div>
      )}
      {!adding
        ? <button className="btn btn-ghost btn-sm" onClick={startAdd}><Icon name="plus" size={12} />Add expense</button>
        : <div className="card pad" style={{ display: "grid", gap: 10, borderColor: "var(--teal-tint-2)" }}>
            <div className="wo-grid" style={{ gridTemplateColumns: "120px minmax(0,1fr)", gap: 10 }}>
              <div><label className="field-label">Amount</label>
                <input className="input tnum" type="number" min="0" step="0.01" autoFocus value={f.amount} placeholder="0.00" onChange={e => setForm("amount", e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") addEntry(); if (e.key === "Escape") setAdding(false); }} /></div>
              <div><label className="field-label">Category</label>
                <select className="select" value={f.category} onChange={e => setCategory(e.target.value)}>
                  {EXPENSE_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select></div>
            </div>
            <div>
              <label className="field-label">Where does it belong?</label>
              <div className="wo-scope-pick">
                {EXPENSE_SCOPES.map(sc => (
                  <button key={sc.id} type="button" className={"wo-scope-opt" + (f.scope === sc.id ? " on" : "")} onClick={() => setScope(sc.id)} aria-pressed={f.scope === sc.id}>
                    <span className="wo-scope-name">{sc.label}</span>
                    <span className="wo-scope-blurb">{sc.blurb}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="wo-grid" style={{ gridTemplateColumns: "minmax(0,1fr) 150px", gap: 10 }}>
              <div><label className="field-label">Vendor / description</label>
                <input className="input" value={f.vendor} placeholder="e.g. Midwest Fence Supply" onChange={e => setForm("vendor", e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") addEntry(); if (e.key === "Escape") setAdding(false); }} /></div>
              <div><label className="field-label">Date</label>
                <input className="input" type="date" value={f.date} onChange={e => setForm("date", e.target.value)} /></div>
            </div>
            <div className="row gap-8">
              <button className="btn btn-primary btn-sm" disabled={!(+f.amount > 0)} onClick={addEntry}><Icon name="check" size={13} />Add expense</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setAdding(false)}>Cancel</button>
              {userName ? <span className="faint" style={{ fontSize: 11, marginLeft: "auto", alignSelf: "center" }}>Logged as {userName}</span> : null}
            </div>
          </div>}
    </div>
  );
}

/* JobPL — the per-job profit & loss: the quote's engine numbers vs what the job
   actually cost (logged hours × crew rate + logged expenses, or an imported
   job's recorded costs), both after the rep's commission. This is the loop
   that lets estimates get sharper over time. */
function JobPL({ wo, store, crews }: any) {
  const pl = woPL(wo, store, crews);
  const offJob = offJobTotal(wo);
  if (!pl.haveQuote && !pl.hasActual && !(pl.revenue > 0)) return null;
  const col = (v) => (v >= 0 ? "var(--green)" : "var(--red)");
  const profitDelta = pl.quotedProfit != null ? pl.actualProfit - pl.quotedProfit : null;
  const marginDelta = pl.quotedMargin != null ? pl.actualMargin - pl.quotedMargin : null;
  const hoursDelta = pl.actualHrs - pl.est.hours;
  return (
    <div className="wo-ready">
      <div className="row between" style={{ marginBottom: 10 }}>
        <span className="row gap-8 kicker"><Icon name="activity" size={13} style={{ color: "var(--teal)" }} />Job P&amp;L · quoted vs actual</span>
        <span className="mono" style={{ fontSize: 11, color: "var(--fg-3)" }}>{WO_money0(pl.revenue)} revenue</span>
      </div>
      <div className="wo-grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div className="wo-labor-note" style={{ padding: "10px 12px" }}>
          <div className="faint" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".06em" }}>Quoted profit</div>
          <div className="tnum" style={{ fontWeight: 800, fontSize: 18, color: "var(--fg)" }}>{pl.quotedProfit != null ? WO_money0(pl.quotedProfit) : "—"}</div>
          <div className="muted" style={{ fontSize: 11.5 }}>{pl.quotedProfit != null ? pl.quotedMargin.toFixed(0) + "% margin · " + WO_money0(pl.quotedTotalCost) + " cost" : "No linked quote"}</div>
        </div>
        <div className="wo-labor-note" style={{ padding: "10px 12px", borderColor: pl.hasActual ? (pl.actualProfit >= 0 ? "rgba(52,211,153,0.35)" : "var(--red-tint)") : "var(--line)" }}>
          <div className="faint" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".06em" }}>Actual profit to date</div>
          <div className="tnum" style={{ fontWeight: 800, fontSize: 18, color: pl.hasActual ? col(pl.actualProfit) : "var(--fg-3)" }}>{pl.hasActual ? WO_money0(pl.actualProfit) : "—"}</div>
          <div className="muted" style={{ fontSize: 11.5 }}>{pl.hasActual ? pl.actualMargin.toFixed(0) + "% margin · " + WO_money0(pl.actualCost) + " cost" + (pl.actualSource === "imported" ? " · imported" : "") : "Log hours & expenses"}</div>
        </div>
      </div>
      {pl.hasActual && profitDelta != null && (
        <div className="wo-pl-delta" style={{ marginTop: 8 }}>
          <span className="tnum" style={{ fontWeight: 800, color: col(profitDelta) }}>{profitDelta >= 0 ? "+" : ""}{WO_money0(profitDelta)}</span>
          <span className="muted">vs quoted profit</span>
          {marginDelta != null && <span className="muted">· {marginDelta >= 0 ? "+" : ""}{marginDelta.toFixed(0)} pts margin</span>}
          {pl.est.hours > 0 && <span className="muted">· {hoursDelta > 0 ? "+" : ""}{hoursDelta.toFixed(1)} hr {hoursDelta > 0.05 ? "over" : hoursDelta < -0.05 ? "under" : "on"} est</span>}
        </div>
      )}
      <div style={{ display: "grid", gap: 5, marginTop: 10 }}>
        <div className="wo-pl-row"><span>Labor</span><span className="muted">quoted {pl.est.hours > 0 ? pl.est.hours.toFixed(1) + " crew-hr · " : ""}{WO_money0(pl.est.cost)}</span><span className="tnum">{WO_money0(pl.actualLabor)} · {pl.actualHrs.toFixed(1)} crew-hr</span></div>
        <div className="wo-pl-row"><span>Materials &amp; other</span><span className="muted">quoted {pl.haveQuote ? WO_money0(pl.quotedNonLabor) : "—"}</span><span className="tnum" style={pl.materialsShort ? { color: "var(--amber)" } : undefined} title={pl.materialsShort ? "Under half what the quote costed — log the material receipts so actual profit is real" : undefined}>{WO_money0(pl.expT)}</span></div>
        {pl.commission > 0 && <div className="wo-pl-row"><span>Commission</span><span className="muted">{pl.rep} · {pl.commissionPct}%</span><span className="tnum">{WO_money0(pl.commission)}</span></div>}
      </div>
      <p className="faint" style={{ fontSize: 10.5, margin: "8px 0 0", lineHeight: 1.4 }}>
        {pl.actualSource === "imported"
          ? "Actual costs came in with the import — log hours or expenses here and they take over. "
          : "Actual profit = revenue − logged labor − expenses charged to this job (pre-tax)" + (pl.commission > 0 ? " − the rep's commission" : "") + ". "}
        Compare it to the quote to sharpen your next bid.
        {offJob > 0 ? " " + WO_money0(offJob) + " of tool and overhead spend is logged on this ticket and deliberately left out — it isn’t what this job cost." : ""}
      </p>
    </div>
  );
}

function SiteReadiness({ wo, set, store }: any) {
  const r = wo.readiness || {};
  const upd = (patch) => set({ readiness: { ...r, ...patch } });
  const fenceItems = [
    { key: "siteVisit", label: "Site visit confirmed", sub: "Verified clearing, slope & access on-site", icon: "mappin" },
    { key: "permit", label: "Permit", sub: "Needed · Submitted · Approved · Not required", icon: "clipboard", attach: true },
    { key: "diggers", label: "Utilities located & marked", sub: "811 called and the locate marks are on the ground", icon: "phone" },
  // a concrete job drills through the slab and never breaks ground: no locate ticket, no permit (plan §5.4)
  ].filter((i) => (i.key !== "diggers" || locatesRequired(wo)) && (i.key !== "permit" || permitRequired(wo)));
  // a concrete job: the site visit, then its own checks composed from the job
  // types — rig access, the weather window, the customer's prep, the slab
  // checked before drilling, the moisture test before a coating, the cure
  // talk (lib/concrete.steps.ts concreteReadinessFor)
  const cxQuote = wo.quoteId ? ((store.quotes || []) as any[]).find((x: any) => x && x.id === wo.quoteId) : null;
  const items: any[] = verticalOf(wo) === "concrete"
    ? [{ key: "siteVisit", label: "Site visit confirmed", sub: "Measured the drop, counted the sections, walked the access", icon: "mappin" }, ...concreteReadinessFor(jobTypesOf(cxQuote || wo))]
    : fenceItems;
  const extra = r.custom || [];
  const pState = permitState(wo);
  const isDone = (k: string) => k === "permit" ? permitDone(wo) : !!r[k];
  const total = items.length + extra.length;
  const done = items.filter(i => isDone(i.key)).length + extra.filter(x => x.done).length;
  const allDone = done === total;
  /* The document, not its filename — and attaching it approves nothing. The old
     version set permit: true from the file picker and kept only f.name, so a
     job could read "permit pulled" off a file nobody could reopen. */
  const [attachErr, setAttachErr] = React.useState<any>(null);
  const [attaching, setAttaching] = React.useState<any>(false);
  // the job as it is when an upload finishes (the readiness may have changed meanwhile)
  const woRef = React.useRef<any>(wo); woRef.current = wo;
  const setDoc = (doc: any) => set({ readiness: { ...((woRef.current && woRef.current.readiness) || {}), permitDoc: doc } });
  /* Online, the copy goes to private storage (server/src/media.js) and the job
     keeps only its path — a permit carries the owner's name and address, so
     it's opened through a short-lived signed link, never a public URL.
     Offline (or no service), it's kept inside the job as before. */
  function attachPermit() {
    const inp = document.createElement("input"); inp.type = "file"; inp.accept = "image/*,application/pdf";
    inp.onchange = async () => {
      const f = inp.files && inp.files[0];
      if (!f) return;
      setAttachErr(null);
      if (docUploadsConfigured()) {
        if (f.size > 12 * 1024 * 1024) { setAttachErr({ name: f.name, why: "That file is " + Math.round(f.size / 1048576) + " MB — the limit is 12 MB. Photograph the page instead of scanning at full resolution." }); return; }
        setAttaching(true);
        try {
          const saved = await storeDocument(f);
          setAttaching(false);
          setDoc({ name: f.name, type: saved.type || f.type || "", size: f.size, path: saved.path, at: Date.now() });
          return;
        } catch (e: any) {
          setAttaching(false);
          if (e && (e.status === 413 || e.status === 415)) { setAttachErr({ name: f.name, why: e.message }); return; }
          // storage unreachable: fall through and keep it on the job
        }
      }
      if (f.size > 4 * 1024 * 1024) { setAttachErr({ name: f.name, why: "That file is " + Math.round(f.size / 1048576) + " MB. Offline, a permit copy is kept inside the job, so keep it under 4 MB — or attach it again once you're back online." }); return; }
      setAttaching(true);
      const reader = new FileReader();
      reader.onerror = () => { setAttaching(false); setAttachErr({ name: f.name, why: "The file couldn't be read. Try again, or pick it from a different folder." }); };
      reader.onload = () => {
        setAttaching(false);
        setDoc({ name: f.name, type: f.type || "", size: f.size, url: String(reader.result || ""), at: Date.now() });
      };
      reader.readAsDataURL(f);
    };
    inp.click();
  }
  // legacy rows stored the filename as a bare string; read both shapes
  const permitDoc = typeof r.permitDoc === "string" ? { name: r.permitDoc, url: "", legacy: true } : (r.permitDoc || null);
  async function openStoredDoc(download: boolean) {
    // opened inside the click so the browser doesn't block it, then pointed at the signed link
    const w = download ? null : window.open("", "_blank");
    try {
      const url = await documentUrl(permitDoc.path, { download, name: permitDoc.name });
      if (w) w.location.href = url; else window.location.href = url;
    } catch (e: any) {
      if (w) w.close();
      notify((e && e.message) || "Couldn't open the permit copy — try again.", { kind: "error" });
    }
  }
  function openPermitDoc() {
    if (permitDoc && permitDoc.path) { openStoredDoc(false); return; }
    if (!permitDoc || !permitDoc.url) return;
    const w = window.open(); if (!w) return;
    w.document.write(permitDoc.type === "application/pdf"
      ? `<iframe src="${permitDoc.url}" style="border:0;position:fixed;inset:0;width:100%;height:100%"></iframe>`
      : `<img src="${permitDoc.url}" style="max-width:100%">`);
    w.document.title = permitDoc.name || "Permit";
  }
  // Opening the dialer is not a locate. It records that the call was placed;
  // the crew ticks the box when the marks are actually on the ground. Ticking it
  // for them made a site read "ready to dig" from a tap on a phone link.
  function call811() { upd({ diggersAt: Date.now() }); window.open("tel:811"); }
  const [permitOpen, setPermitOpen] = React.useState<any>(false);
  const pi = r.permitInfo || {};
  const detectedCounty = React.useMemo(() => detectCountyForWo(wo, store), [wo.quoteId, wo.projectId]);
  const county = pi.county || detectedCounty || "";
  const [addingStep, setAddingStep] = React.useState<any>(null);   // null | "" | text
  function addCustom() { setAddingStep(""); }
  function commitStep() { const t = (addingStep || "").trim(); if (t) upd({ custom: [...extra, { label: t, done: false }] }); setAddingStep(null); }
  function toggleCustom(i) { upd({ custom: extra.map((x, k) => k === i ? { ...x, done: !x.done } : x) }); }

  return (
    <div data-tour="readiness" className="wo-ready">
      <div className="row between" style={{ marginBottom: 10 }}>
        <span className="row gap-8 kicker"><Icon name="check" size={13} style={{ color: allDone ? "var(--green)" : "var(--amber)" }} />Site readiness</span>
        <span className="mono" style={{ fontSize: 11, color: allDone ? "var(--green)" : "var(--fg-3)" }}>{done}/{total} done</span>
      </div>
      {/* FW-025: say the job is held BEFORE they try to schedule it, and say
          plainly when someone chose to go ahead without the permit. */}
      {permitBlocks(wo) && (
        <div className="pe-callout warn" style={{ marginBottom: 10 }}>
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>
            <b>Scheduling is on hold.</b> {permitBlockMessage(permitBlocks(wo))}
          </p>
        </div>
      )}
      {wo.permitOverride && wo.permitOverride.at && !r.permit && (
        <div className="pe-callout info" style={{ marginBottom: 10 }}>
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>
            Scheduled without an approved permit — {wo.permitOverride.by || "someone"} overrode the hold on {new Date(wo.permitOverride.at).toLocaleDateString()}. Marking the permit approved below clears this.
          </p>
        </div>
      )}
      <div style={{ display: "grid", gap: 7 }}>
        {items.map(it => (
          <div key={it.key} className={"ready-row" + (isDone(it.key) ? " on" : "")}>
            {/* the permit's tick is derived from its state, so it can't be set
                to "done" independently of what the state actually says */}
            <button className={"ready-check" + (isDone(it.key) ? " on" : "")}
              aria-pressed={isDone(it.key)} aria-label={it.label}
              onClick={() => it.key === "permit" ? upd(permitPatch(r, pState === "approved" ? "needed" : "approved")) : upd({ [it.key]: !r[it.key] })}>
              {isDone(it.key) && <Icon name="check" size={12} />}
            </button>
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{it.label}</div>
              <div className="muted" style={{ fontSize: 11.5 }}>
                {it.key === "permit"
                  ? (pState === "not-required" ? "Marked not required for this job"
                    : pState === "approved" ? `✓ Approved ${pi.approvedAt ? new Date(pi.approvedAt).toLocaleDateString() : ""}${pi.number ? " · #" + pi.number : ""}${pi.county ? " · " + pi.county + " Co." : ""}`
                    : pState === "submitted" ? `Submitted ${pi.submittedAt ? new Date(pi.submittedAt).toLocaleDateString() : ""}${pi.number ? " · #" + pi.number : ""} — awaiting approval`
                    : "Nothing filed yet")
                  : it.key === "diggers" && !r.diggers && r.diggersAt
                  ? `Called ${new Date(r.diggersAt).toLocaleDateString()} — tick this once the marks are on the ground`
                  : it.sub}
              </div>
              {/* the four states, explicit. "Not required" is a choice you can
                  record, not a checkbox you have to lie about. */}
              {it.key === "permit" && (
                <div className="permit-states" role="group" aria-label="Permit status">
                  {PERMIT_STATES.map((ps) => (
                    <button key={ps.id} className={"permit-state" + (pState === ps.id ? " on" : "")} title={ps.sub}
                      aria-pressed={pState === ps.id} onClick={() => upd(permitPatch(r, ps.id))}>{ps.label}</button>
                  ))}
                </div>
              )}
              {/* the attachment is evidence you can reopen, and it says so */}
              {it.key === "permit" && permitDoc && (
                <div className="permit-doc">
                  <Icon name="clipboard" size={12} />
                  {permitDoc.legacy
                    ? <span className="muted">{permitDoc.name} — filename only; this was attached before documents were stored. Re-attach it to keep the file.</span>
                    : <><button className="pe-add-link" onClick={openPermitDoc}>{permitDoc.name}</button>
                        {permitDoc.path
                          ? <button className="pe-add-link" onClick={() => openStoredDoc(true)}>Download</button>
                          : <a className="pe-add-link" href={permitDoc.url} download={permitDoc.name}>Download</a>}
                        <span className="faint">{Math.max(1, Math.round((permitDoc.size || 0) / 1024))} KB · attached {new Date(permitDoc.at).toLocaleDateString()} · evidence, not approval</span></>}
                </div>
              )}
              {it.key === "permit" && attachErr && (
                <div className="permit-doc err"><Icon name="x" size={12} />
                  <span>{attachErr.why}</span>
                  <button className="pe-add-link" onClick={attachPermit}>Try again</button>
                </div>
              )}
              {/* the permit's two actions sit UNDER the status chips rather than
                  beside them — squeezed into the same flex row they pushed the
                  four states into a narrow stack that wrapped mid-word */}
              {it.key === "permit" && (
                <div className="permit-actions">
                  <button className="btn btn-ghost btn-sm" aria-expanded={!!permitOpen} onClick={() => setPermitOpen((o: any) => !o)}><Icon name="clipboard" size={12} />{permitOpen ? "Hide details" : "Permit details"}</button>
                  <button className="btn btn-ghost btn-sm" disabled={attaching} onClick={attachPermit}><Icon name="download" size={12} style={{ transform: "rotate(180deg)" }} />{attaching ? "Saving…" : permitDoc ? "Replace copy" : "Attach copy"}</button>
                </div>
              )}
            </div>
            {it.key === "diggers" && !r.diggers && <button className="btn btn-ghost btn-sm" onClick={call811}><Icon name="phone" size={12} />{r.diggersAt ? "Call again" : "Call 811"}</button>}
          </div>
        ))}
        {extra.map((x, i) => (
          <div key={i} className={"ready-row" + (x.done ? " on" : "")}>
            <button className={"ready-check" + (x.done ? " on" : "")} onClick={() => toggleCustom(i)}>{x.done && <Icon name="check" size={12} />}</button>
            <div style={{ flex: 1, minWidth: 140 }}><div style={{ fontWeight: 600, fontSize: 13 }}>{x.label}</div></div>
            <button className="pe-del" onClick={() => upd({ custom: extra.filter((_, k) => k !== i) })}><Icon name="x" size={12} /></button>
          </div>
        ))}
      </div>
      {permitOpen && (
        <div className="card pad" style={{ marginTop: 10, display: "grid", gap: 10, borderColor: "var(--teal-tint-2)" }}>
          <div className="row between" style={{ flexWrap: "wrap", gap: 8 }}>
            <span className="kicker">Fence permit — Wisconsin</span>
            <div className="row gap-8" style={{ alignItems: "center" }}>
              <label className="field-label" style={{ margin: 0 }}>County</label>
              <select className="input" style={{ width: 180 }} value={county} onChange={e => upd({ permitInfo: { ...pi, county: e.target.value } })}>
                <option value="">Select county…</option>
                {WI_COUNTIES.map(c => <option key={c.fips} value={c.name}>{c.name}{detectedCounty === c.name ? " (detected)" : ""}</option>)}
              </select>
            </div>
          </div>
          {detectedCounty && !pi.county && <p className="faint" style={{ fontSize: 11.5, margin: 0 }}>Detected from the estimate's parcel: <b>{detectedCounty} County</b>.</p>}
          <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 4 }}>
            {WI_STATEWIDE.map((x, i) => <li key={i} className="muted" style={{ fontSize: 12 }}>{x}</li>)}
          </ul>
          {county && (
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              <a className="btn btn-ghost btn-sm" href={permitSearchUrl(county)} target="_blank" rel="noopener"><Icon name="search" size={12} />{county} Co. fence permit</a>
              <a className="btn btn-ghost btn-sm" href={countyZoningSearchUrl(county)} target="_blank" rel="noopener"><Icon name="building" size={12} />Zoning department</a>
            </div>
          )}
          <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
            <input className="input" style={{ width: 160 }} placeholder="Permit #" value={pi.number || ""} onChange={e => upd({ permitInfo: { ...pi, number: e.target.value } })} />
            {!pi.submittedAt
              ? <button className="btn btn-outline btn-sm" disabled={!county} onClick={() => upd({ permitInfo: { ...pi, county, submittedAt: Date.now() } })}>Mark submitted</button>
              : !pi.approvedAt
                ? <button className="btn btn-primary btn-sm" onClick={() => upd({ permit: true, permitInfo: { ...pi, county, approvedAt: Date.now() } })}><Icon name="check" size={12} />Mark approved</button>
                : <span className="chip green" style={{ fontSize: 11 }}>Approved {new Date(pi.approvedAt).toLocaleDateString()}</span>}
            {pi.submittedAt && !pi.approvedAt && <span className="muted" style={{ fontSize: 11.5 }}>Submitted {new Date(pi.submittedAt).toLocaleDateString()}</span>}
            {(pi.submittedAt || pi.approvedAt) && <button className="btn btn-ghost btn-sm" onClick={() => upd({ permit: false, permitInfo: { county: pi.county } })}>Reset</button>}
          </div>
          <p className="faint" style={{ fontSize: 11, margin: 0 }}>Inside city/village limits? The municipal zoning office governs — call them first. Marking approved checks this readiness step.</p>
        </div>
      )}
      {addingStep == null
        ? <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={addCustom}><Icon name="plus" size={12} />Add step</button>
        : <div className="row gap-8" style={{ marginTop: 8 }}>
            <input className="input" autoFocus placeholder="e.g. HOA approval, gate code…" value={addingStep}
              onChange={e => setAddingStep(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") commitStep(); if (e.key === "Escape") setAddingStep(null); }} />
            <button className="btn btn-primary btn-sm" style={{ flexShrink: 0 }} onClick={commitStep}>Add</button>
            <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }} onClick={() => setAddingStep(null)}><Icon name="x" size={13} /></button>
          </div>}
      {!allDone && (wo.status === "scheduled" || wo.status === "in_progress") &&
        <div className="pe-callout info" style={{ marginTop: 10 }}><p className="muted" style={{ margin: 0, fontSize: 12 }}>{verticalOf(wo) === "concrete" ? "⚠ Complete site readiness before the crew rolls — access, the weather window, the customer's prep, and the slab checks protect the job." : <>⚠ Complete site readiness before crews go on-site — pulls permits &amp; 811 marking protect the job.</>}</p></div>}
    </div>
  );
}

function WorkOrderPhotos({ wo, set }: any) {
  const photos = wo.photos || [];
  /* Each photo uploads as a real file (lib/media.ts: 2000px, EXIF-rotated) so
     the website can use it; offline it's kept on the job as a small copy and
     uploads later by itself. While one uploads, its tile shows here. */
  const woRef = React.useRef<any>(wo); woRef.current = wo;
  // added but not rendered back yet, so two uploads finishing together both land
  // (and one the user already removed is never brought back)
  const same = (a: any, b: any) => !!(a && b && a.url === b.url && a.at === b.at);
  const addedRef = React.useRef<any[]>([]);
  addedRef.current = addedRef.current.filter((x) => !photos.some((p: any) => same(p, x)));
  const [uploading, setUploading] = React.useState<any[]>([]);
  function commit(p: any) {
    const base = ((woRef.current && woRef.current.photos) || []) as any[];
    const inFlight = addedRef.current.filter((x) => !base.some((b) => same(b, x)));
    addedRef.current = [...inFlight, p];
    set({ photos: [...base, ...inFlight, p] });
  }
  function addPhotos() {
    const inp = document.createElement("input"); inp.type = "file"; inp.accept = "image/*"; inp.multiple = true;
    inp.onchange = () => {
      const files = [...(inp.files || [])];
      files.forEach((f) => {
        const tile = { id: newId("up-"), name: f.name, preview: URL.createObjectURL(f) };
        setUploading((u) => [...u, tile]);
        uploadPhoto(f)
          .then((r) => commit({ name: f.name, url: r.url, at: Date.now(), ...(r.w && r.h ? { w: r.w, h: r.h } : {}), ...(r.pending ? { pending: true } : {}) }))
          .catch(() => notify("Couldn't read " + f.name + " — try another photo.", { kind: "error" }))
          .finally(() => { URL.revokeObjectURL(tile.preview); setUploading((u) => u.filter((x) => x.id !== tile.id)); });
      });
    };
    inp.click();
  }
  // "Don't use on my website" (web: false) — the website and job posts skip it
  function toggleSite(i) { set({ photos: photos.map((p, k) => (k === i ? toggleWeb(p) : p)) }); }
  // before → during → after → untagged. The hub gallery groups by these
  // phases (the transformation story); tagged before/after shots also lead
  // the social auto-post when the job completes.
  function cycleTag(i) {
    const NEXT: any = { before: "during", during: "after", after: undefined };
    set({ photos: photos.map((p, k) => k === i
      ? { ...p, tag: p.tag ? NEXT[p.tag] : "before" }
      : p) });
  }
  return (
    <div>
      <div className="row between" style={{ marginBottom: 8 }} data-tour="photos">
        <label className="field-label" style={{ margin: 0 }}>Job photos {photos.length ? `(${photos.length})` : ""}</label>
        <button className="btn btn-ghost btn-sm" onClick={addPhotos}><Icon name="plus" size={12} />Add photos</button>
      </div>
      {photos.length === 0 && !uploading.length
        ? <p className="faint" style={{ fontSize: 12, margin: 0 }}>Snap site conditions, progress, or the finished fence — visible to you and shareable to the customer hub. Tag shots Before/After and the social auto-post uses them when the job completes.</p>
        : <>
          <div className="wo-photos">
            {photos.map((p, i) => (
              <div key={i} className={"wo-photo" + (p.web === false ? " off-site" : "")} style={{ backgroundImage: `url(${p.url})` }} title={p.name}>
                <button className="wo-photo-del" aria-label="Remove photo" onClick={() => set({ photos: photos.filter((_, k) => k !== i) })}><Icon name="x" size={11} /></button>
                <button className={"wo-photo-site" + (p.web === false ? " off" : "")} aria-pressed={p.web === false} aria-label="Don't use on my website"
                  title={p.web === false ? "Kept off your website — tap to allow it" : "Finished-job photos can appear on your website — tap for Don't use on my website"}
                  onClick={() => toggleSite(i)}>{p.web === false ? "no site" : "site"}</button>
                <button className={"wo-photo-tag" + (p.tag ? " on" : "")} title="Tap to cycle Before / During / After / off — groups the customer-hub gallery; before/after lead the social auto-post" onClick={() => cycleTag(i)}>{p.tag ? p.tag : "tag"}</button>
                {p.pending && <span className="wo-photo-wait" title="Saved on this job. It uploads by itself when you're back online."><Icon name="upload" size={10} /></span>}
              </div>
            ))}
            {uploading.map((u) => (
              <div key={u.id} className="wo-photo uploading" style={{ backgroundImage: `url(${u.preview})` }} title={u.name}>
                <span className="wo-photo-busy">Uploading…</span>
              </div>
            ))}
          </div>
          {photos.length > 0 && <p className="faint" style={{ fontSize: 11, margin: "6px 0 0" }}>Photos from finished jobs can go on your website. Tap “site” to keep one off.</p>}
        </>}
    </div>
  );
}

/* ---- Jobs Sheet: every job's money on one screen, quoted and actual -------
   Per row: contract, then either the QUOTED costs (the linked quote's engine
   breakdown via woPL) or the ACTUAL ones (clocked hours × crew rate + job-scope
   expenses, or an imported job's recorded costs) — labor, materials & other,
   the rep's commission, total cost, profit & margin — plus estimated vs
   clocked hours, crew efficiency, payment status and readiness flags. A job
   with nothing recorded says so; it never shows $0 as if it cost nothing. The
   totals band carries both sides. Export CSV writes the rows and columns on
   screen. Read-only — click a row to open it. */

/* the day a job sits on the sheet: finished → the day it finished; booked →
   its start; otherwise the day it was created */
function jobDateOf(wo) {
  if (wo.status === "completed" && Number(wo.completedAt) > 0) return localYmd(new Date(Number(wo.completedAt)));
  if (wo.scheduledStart) return String(wo.scheduledStart).slice(0, 10);
  return Number(wo.createdAt) > 0 ? localYmd(new Date(Number(wo.createdAt))) : "";
}
/* [first, last] calendar day of this month / quarter / year; null = all time */
function sheetPeriod(range, now) {
  const d = new Date(now), y = d.getFullYear(), m = d.getMonth();
  if (range === "month") return [localYmd(new Date(y, m, 1)), localYmd(new Date(y, m + 1, 0))];
  if (range === "quarter") { const q0 = m - (m % 3); return [localYmd(new Date(y, q0, 1)), localYmd(new Date(y, q0 + 3, 0))]; }
  if (range === "year") return [y + "-01-01", y + "-12-31"];
  return null;
}
function sheetRowOf(wo, store, crews) {
  const pl = woPL(wo, store, crews);
  const est = Number(pl.est.hours) || 0, act = Number(pl.actualHrs) || 0;
  return { wo, pl, pay: woPayStatus(wo, store), date: jobDateOf(wo), lf: Number(wo.lf) || 0,
    // crew efficiency: estimated ÷ clocked hours. 100% = on the estimate, over 100% = faster
    eff: est > 0 && act > 0 ? (est / act) * 100 : null };
}
/* the totals band. Each side totals only the jobs that HAVE that side — quoted
   over jobs with a quote, actual over jobs with something recorded — so each
   margin is honest about its own revenue; `both` is the like-for-like variance. */
function sheetTotals(rows) {
  const side = (list, f) => {
    const o = { jobs: list.length, revenue: 0, labor: 0, other: 0, commission: 0, cost: 0, profit: 0, margin: null };
    list.forEach(r => { const v = f(r.pl); o.revenue += r.pl.revenue; o.labor += v.labor; o.other += v.other; o.commission += r.pl.commission; o.cost += v.cost; o.profit += v.profit; });
    o.margin = o.revenue > 0 ? (o.profit / o.revenue) * 100 : null;
    return o;
  };
  const quoted = side(rows.filter(r => r.pl.haveQuote), pl => ({ labor: Number(pl.est.cost) || 0, other: pl.quotedNonLabor, cost: pl.quotedTotalCost, profit: pl.quotedProfit }));
  const actual = side(rows.filter(r => r.pl.hasActual), pl => ({ labor: pl.actualLabor, other: pl.expT, cost: pl.actualCost, profit: pl.actualProfit }));
  const bothRows = rows.filter(r => r.pl.haveQuote && r.pl.hasActual);
  const both = { jobs: bothRows.length, quotedProfit: bothRows.reduce((a, r) => a + r.pl.quotedProfit, 0), actualProfit: bothRows.reduce((a, r) => a + r.pl.actualProfit, 0) };
  const timed = rows.filter(r => r.eff != null);
  const estH = timed.reduce((a, r) => a + r.pl.est.hours, 0), actH = timed.reduce((a, r) => a + r.pl.actualHrs, 0);
  const custs = new Set(rows.map(r => r.wo.customerId || String(r.wo.customer || r.wo.title || r.wo.id).trim().toLowerCase()));
  return {
    customers: custs.size, jobs: rows.length, completed: rows.filter(r => r.wo.status === "completed").length,
    revenue: rows.reduce((a, r) => a + r.pl.revenue, 0), commission: rows.reduce((a, r) => a + r.pl.commission, 0),
    quoted, actual, both,
    eff: actH > 0 ? (estH / actH) * 100 : null, estHours: estH, actualHours: actH,
    toCollect: rows.reduce((a, r) => a + (r.pay && r.pay.id !== "paid" ? r.pay.balance : 0), 0),
    materialsShort: rows.filter(r => r.pl.materialsShort).length,
  };
}
/* the export's columns — the fields the table shows for this mode (the
   customer cell's crew · rep · date line gets its own columns in a sheet) */
function sheetColumns(mode, money) {
  const side = mode === "quoted" ? "Quoted" : "Actual";
  return [
    { key: "customer", label: "Customer" }, { key: "crew", label: "Crew" }, { key: "rep", label: "Rep" }, { key: "date", label: "Date" },
    { key: "stage", label: "Stage" }, { key: "contract", label: "Contract" }, { key: "perFt", label: "$ / ft" },
    ...(money ? [
      { key: "labor", label: side + " labor" }, { key: "other", label: side + " materials & other" }, { key: "commission", label: "Commission" },
      { key: "cost", label: side + " total cost" }, { key: "profit", label: side + " profit" }, { key: "margin", label: side + " margin %" },
    ] : []),
    { key: "estHours", label: "Est. hours" }, { key: "actualHours", label: "Actual hours" }, { key: "eff", label: "Crew efficiency %" },
    { key: "payment", label: "Payment" }, { key: "balance", label: "Balance due" }, { key: "flags", label: "Flags" },
  ];
}
function sheetFlags(wo) {
  const r = wo.readiness || {};
  return [wo.imported ? "imported" : "", wo.suppliesOrdered ? "ordered" : "",
    r.diggers ? "811 located" : (wo.status === "scheduled" || wo.status === "in_progress") ? "811 not marked" : ""].filter(Boolean);
}
/* one row as numbers — blank (never 0) wherever the job has no figure */
function sheetCsvRow(r, mode, crews) {
  const { wo, pl } = r;
  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const q = mode === "quoted";
  const has = q ? pl.haveQuote : pl.hasActual;
  const crew = (crews || []).find(c => c.id === wo.crewId);
  return {
    customer: wo.customer || wo.title || "Job", crew: crew ? crew.name : "", rep: pl.rep || "", date: r.date, stage: STAGE(wo.status).label,
    contract: r2(pl.revenue), perFt: r.lf > 0 && pl.revenue > 0 ? r2(pl.revenue / r.lf) : "",
    labor: has ? r2(q ? pl.est.cost : pl.actualLabor) : "", other: has ? r2(q ? pl.quotedNonLabor : pl.expT) : "",
    commission: has ? r2(pl.commission) : "", cost: has ? r2(q ? pl.quotedTotalCost : pl.actualCost) : "",
    profit: has ? r2(q ? pl.quotedProfit : pl.actualProfit) : "", margin: has && pl.revenue > 0 ? Math.round(q ? pl.quotedMargin : pl.actualMargin) : "",
    estHours: pl.est.hours > 0 ? Math.round(pl.est.hours * 10) / 10 : "", actualHours: pl.actualHrs > 0 ? Math.round(pl.actualHrs * 10) / 10 : "",
    eff: r.eff != null ? Math.round(r.eff) : "",
    payment: r.pay ? r.pay.label : "", balance: r.pay ? r2(r.pay.balance) : "",
    flags: sheetFlags(wo).join(" · "),
  };
}

// the customer column stays put while the rest of a wide sheet scrolls under it (phones)
const STICKY: any = { position: "sticky", left: 0, zIndex: 1, background: "var(--surface)" };
const SHEET_RANGES = [["all", "All time"], ["month", "This month"], ["quarter", "This quarter"], ["year", "This year"]];
const PAY_COLOR: any = { paid: "var(--green)", deposit: "var(--cyan)", balance: "var(--red)", unpaid: "var(--amber)" };

function JobsSheet({ orders, store, crews, onOpen, query }: any) {
  const [mode, setMode] = useLocalPref("fw_sheet_mode", "actual");      // quoted | actual
  const [status, setStatus] = useLocalPref("fw_sheet_status", "all");   // completed | open | all
  const [range, setRange] = useLocalPref("fw_sheet_range", "all");      // month | quarter | year | all
  const money = storeCan(store, "money.cost");   // margin, materials, labor & commission are cost data
  const all = React.useMemo(() => orders.map(wo => sheetRowOf(wo, store, crews)), [orders, store.quotes, store.settings, crews]);
  if (!orders.length) return <div className="card pad" style={{ marginTop: 18 }}><EmptyState icon="clipboard" title="No jobs yet" sub="Work orders appear on the sheet as soon as they exist." /></div>;
  const quoted = money && mode === "quoted";
  const period = sheetPeriod(range, Date.now());
  const needle = String(query || "").trim().toLowerCase();
  const rows = all
    .filter(r => status === "all" || (status === "completed" ? r.wo.status === "completed" : r.wo.status !== "completed"))
    .filter(r => !period || (!!r.date && r.date >= period[0] && r.date <= period[1]))
    .filter(r => !needle || `${r.wo.customer || ""} ${r.wo.title || ""} ${r.wo.address || ""} ${r.pl.rep || ""}`.toLowerCase().includes(needle))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  const t = sheetTotals(rows);
  const col = (v) => (v >= 0 ? "var(--green)" : "var(--red)");
  const pct = (v) => (v == null ? "—" : Math.round(v) + "%");
  function exportCsv() {
    const m = quoted ? "quoted" : "actual";
    downloadCsv(`jobs-sheet-${m}-${localYmd()}.csv`, toCsv(sheetColumns(m, money), rows.map(r => sheetCsvRow(r, m, crews))));
  }
  const rangeLabel = (SHEET_RANGES.find(x => x[0] === range) || SHEET_RANGES[0])[1];
  const gap = t.both.actualProfit - t.both.quotedProfit;
  return (
    <div className="card pad" style={{ marginTop: 18 }}>
      {/* which side, which jobs, which dates */}
      <div className="row between" style={{ flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        <div className="row gap-8" style={{ flexWrap: "wrap" }}>
          {money && <div className="seg" role="group" aria-label="Quoted or actual numbers">
            {[["quoted", "Quoted"], ["actual", "Actual"]].map(([v, lb]) => <button key={v} className={"seg-btn" + ((v === "quoted") === quoted ? " on" : "")} aria-pressed={(v === "quoted") === quoted} onClick={() => setMode(v)}>{lb}</button>)}
          </div>}
          <div className="seg" role="group" aria-label="Which jobs">
            {[["completed", "Completed"], ["open", "Open"], ["all", "All"]].map(([v, lb]) => <button key={v} className={"seg-btn" + (status === v ? " on" : "")} aria-pressed={status === v} onClick={() => setStatus(v)}>{lb}</button>)}
          </div>
        </div>
        <div className="row gap-8">
          <select className="select" style={{ width: 140 }} value={range} onChange={e => setRange(e.target.value)} aria-label="Date range">
            {SHEET_RANGES.map(([v, lb]) => <option key={v} value={v}>{lb}</option>)}
          </select>
          <button className="btn btn-outline btn-sm" style={{ flexShrink: 0 }} disabled={!rows.length} onClick={exportCsv} title="Download these rows and columns as a spreadsheet"><Icon name="download" size={13} />Export CSV</button>
        </div>
      </div>

      {/* totals band — both sides, each over the jobs that have it */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 24px", padding: "12px 14px", borderRadius: 12, background: "var(--surface-2)", border: "1px solid var(--line)", marginBottom: 14 }}>
        {money && <>
          <div>
            <div className="kicker">Quoted profit</div>
            <div className="display tnum" style={{ fontSize: 22 }}>{t.quoted.jobs ? WO_money0(t.quoted.profit) : "—"}</div>
            <div className="faint" style={{ fontSize: 10.5, marginTop: 2 }}>{t.quoted.jobs ? pct(t.quoted.margin) + " margin · " + t.quoted.jobs + " quoted job" + (t.quoted.jobs === 1 ? "" : "s") : "None of these jobs has a quote"}</div>
          </div>
          <div>
            <div className="kicker">Actual profit</div>
            <div className="display tnum" style={{ fontSize: 22, color: t.actual.jobs ? col(t.actual.profit) : "var(--fg-3)" }}>{t.actual.jobs ? WO_money0(t.actual.profit) : "—"}</div>
            <div className="faint" style={{ fontSize: 10.5, marginTop: 2 }}>{t.actual.jobs ? pct(t.actual.margin) + " margin · " + t.actual.jobs + " job" + (t.actual.jobs === 1 ? "" : "s") + " with actuals" : "Nothing recorded on these jobs yet"}</div>
          </div>
        </>}
        <div style={{ display: "flex", gap: "10px 20px", flexWrap: "wrap", alignItems: "flex-start", flex: "1 1 300px" }}>
          {([
            ["Customers", String(t.customers)],
            ["Jobs", t.jobs + (t.completed ? " · " + t.completed + " done" : "")],
            ["Revenue", WO_money0(t.revenue)],
            ...(money ? [
              [quoted ? "Quoted labor" : "Actual labor", quoted ? WO_money0(t.quoted.labor) : (t.actual.jobs ? WO_money0(t.actual.labor) : "—")],
              [quoted ? "Quoted cost" : "Actual cost", quoted ? WO_money0(t.quoted.cost) : (t.actual.jobs ? WO_money0(t.actual.cost) : "—")],
              ["Commission", WO_money0(quoted ? t.quoted.commission : t.actual.commission)],
            ] : []),
            ["Crew efficiency", pct(t.eff)],
            ["To collect", WO_money0(t.toCollect)],
          ] as any[]).map(([lb, v]) => (
            <div key={lb}><div className="kicker">{lb}</div><div className="tnum" style={{ fontWeight: 800, fontSize: 15 }}>{v}</div></div>
          ))}
        </div>
        {money && !quoted && t.materialsShort > 0 && (
          <div style={{ flexBasis: "100%", fontSize: 11.5, color: "var(--amber)" }}>
            {t.materialsShort} job{t.materialsShort === 1 ? " has" : "s have"} logged under half the materials the quote costed — if receipts are missing, actual profit reads high.
          </div>
        )}
        {money && t.both.jobs > 0 && (
          <div className="faint" style={{ flexBasis: "100%", fontSize: 11.5, borderTop: "1px solid var(--line)", paddingTop: 8 }}>
            On the {t.both.jobs} job{t.both.jobs === 1 ? "" : "s"} with a quote and actuals: <b className="tnum" style={{ color: col(t.both.actualProfit) }}>{WO_money0(t.both.actualProfit)}</b> actual vs <b className="tnum" style={{ color: "var(--fg-2)" }}>{WO_money0(t.both.quotedProfit)}</b> quoted
            {" "}<b className="tnum" style={{ color: col(gap) }}>({gap >= 0 ? "+" : "−"}{WO_money0(Math.abs(gap))})</b>
            {t.eff != null ? <> · crews clocked {Math.round(t.actualHours)} hr against {Math.round(t.estHours)} estimated</> : null}.
          </div>
        )}
      </div>

      {rows.length === 0
        ? <div style={{ textAlign: "center", padding: "26px 0" }}>
            <p className="muted" style={{ fontSize: 13, margin: "0 0 10px" }}>No {status === "all" ? "" : status + " "}jobs{range === "all" ? "" : " " + rangeLabel.toLowerCase()}{needle ? " match your search" : ""}.</p>
            <button className="btn btn-ghost btn-sm" onClick={() => { setStatus("all"); setRange("all"); }}>Show every job</button>
          </div>
        : <div style={{ overflowX: "auto" }}>
        {/* two-line cells (value over its % / status) keep every column on a
            laptop screen; the export gives each its own column */}
        <table className="rep-src-table" style={{ fontSize: 12.5 }}>
          <thead><tr>
            <th style={STICKY}>Customer</th><th>Stage</th><th className="num">Contract</th><th className="num">$ / ft</th>
            {money && <><th className="num" title={quoted ? "The quote's own crew-hours × rate" : "Hours clocked × the crew's rate"}>Labor</th>
              <th className="num" style={{ whiteSpace: "normal", minWidth: 70 }} title={quoted ? "Everything the quote costed that isn't labor — materials, gates, disposal, permit, travel" : "Expenses charged to the job — materials, dump fees, subs"}>Materials &amp; other</th>
              <th className="num" title="The rep's commission — the same % Sales Team pays out">Commission</th>
              <th className="num" style={{ whiteSpace: "normal" }}>Total cost</th><th className="num" style={{ whiteSpace: "normal" }}>{quoted ? "Quoted profit" : "Actual profit"}</th></>}
            <th className="num" title="Estimated crew-hours → hours actually clocked, and crew efficiency: estimated ÷ actual. Over 100% means the crew beat the estimate">Hours</th>
            <th>Payment</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => {
              const { wo, pl, pay } = r;
              const st = STAGE(wo.status);
              const crew = crews.find(c => c.id === wo.crewId);
              const has = quoted ? pl.haveQuote : pl.hasActual;
              const profit = quoted ? pl.quotedProfit : pl.actualProfit, margin = quoted ? pl.quotedMargin : pl.actualMargin;
              const sub = [crew && crew.name, pl.rep, r.date ? fmtDate(r.date) : ""].filter(Boolean).join(" · ");
              const flags = sheetFlags(wo);
              return (
                <tr key={wo.id} style={{ cursor: "pointer" }} onClick={() => onOpen(wo)} title="Open this job">
                  <td style={{ ...STICKY, whiteSpace: "normal", minWidth: 136 }}><div style={{ fontWeight: 700 }}>{wo.customer || wo.title || "Job"}</div>{sub && <div className="faint" style={{ fontSize: 11 }}>{sub}</div>}</td>
                  <td style={{ whiteSpace: "normal", maxWidth: 124 }}><span className="chip" style={{ fontSize: 10.5, color: st.color, borderColor: "var(--line-2)", whiteSpace: "nowrap" }}><span className="wo-pipe-dot" style={{ background: st.color }} />{st.short}</span>
                    {flags.length > 0 && <div style={{ fontSize: 10.5, marginTop: 3, color: flags.includes("811 not marked") ? "var(--red)" : "var(--fg-3)" }}>{flags.join(" · ")}</div>}</td>
                  <td className="num tnum" style={{ fontWeight: 700 }}>{WO_money0(pl.revenue)}</td>
                  <td className="num tnum" style={{ color: "var(--teal)", fontWeight: 700 }}>{r.lf > 0 && pl.revenue > 0 ? WO_money0(pl.revenue / r.lf) : "—"}</td>
                  {money && (has
                    ? <><td className="num tnum">{WO_money0(quoted ? pl.est.cost : pl.actualLabor)}</td>
                        <td className="num tnum">{WO_money0(quoted ? pl.quotedNonLabor : pl.expT)}
                          {!quoted && pl.materialsShort && <div style={{ fontSize: 10, color: "var(--amber)" }} title="Logged expenses are under half what the quote costed for materials and other — if receipts are missing, this job's actual profit reads high">vs {WO_money0(pl.quotedNonLabor)} quoted</div>}</td>
                        <td className="num tnum" title={pl.commission > 0 ? pl.rep + " · " + pl.commissionPct + "%" : "No commission on this job"}>{pl.commission > 0 ? WO_money0(pl.commission) : "—"}</td>
                        <td className="num tnum">{WO_money0(quoted ? pl.quotedTotalCost : pl.actualCost)}</td>
                        <td className="num"><div className="tnum" style={{ fontWeight: 800, color: col(profit) }}>{WO_money0(profit)}</div>
                          <div className="muted tnum" style={{ fontSize: 11 }}>{Math.round(margin)}% margin{!quoted && pl.actualSource === "imported" ? " · imported" : ""}</div></td></>
                    : <td colSpan={5} className="faint" style={{ fontSize: 11.5, textAlign: "center", whiteSpace: "normal" }}>
                        {quoted ? "No quote on this job — nothing quoted to compare" : "No hours clocked or expenses logged yet"}</td>)}
                  <td className="num tnum"><div style={{ color: "var(--fg-2)" }}>{pl.est.hours > 0 ? +pl.est.hours.toFixed(1) : "—"} → {pl.actualHrs > 0 ? +pl.actualHrs.toFixed(1) : "—"}</div>
                    {r.eff != null && <div style={{ fontSize: 11, fontWeight: 700, color: r.eff >= 100 ? "var(--green)" : r.eff >= 90 ? "var(--fg-2)" : "var(--red)" }} title="Crew efficiency: estimated ÷ actual hours">{Math.round(r.eff)}% eff.</div>}</td>
                  <td>{pay
                    ? <div title={WO_money0(pay.paid) + " collected"}><div style={{ fontSize: 12, fontWeight: 700, color: pay.id === "unpaid" && wo.status !== "completed" ? "var(--fg-3)" : PAY_COLOR[pay.id] }}>{pay.label}</div>
                        {pay.id !== "paid" && <div className="muted tnum" style={{ fontSize: 11 }}>{WO_money0(pay.balance)} due</div>}</div>
                    : <span className="faint" title="No quote on this job, so no payment ledger to read">—</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>}
      <p className="faint" style={{ fontSize: 11, margin: "10px 0 0", lineHeight: 1.5 }}>
        {!money ? "" : quoted
          ? "Quoted: the linked quote's own cost breakdown, pre-tax, less the rep's commission. "
          : "Actual: hours clocked × the crew's rate, plus expenses charged to the job (imported jobs: the costs that came in with the import), less the rep's commission. "}
        Crew efficiency is estimated ÷ actual hours. Payment reads the quote's ledger. Click a row to open the job.
      </p>
    </div>
  );
}

/* ---- Job Map: every active install pinned by stage ------------------------
   Addresses geocode lazily (OSM, 1/sec, throttled) and the lat/lng persists on
   the work order (wo.geo) so each job is only ever looked up once. */
function JobsMap({ orders, store, crews, onOpen }: any) {
  const mapEl = React.useRef<any>(null);
  const mapRef = React.useRef<any>(null);
  const pinsRef = React.useRef<any>([]);
  const [ready, setReady] = React.useState<any>(false);
  const [locating, setLocating] = React.useState<any>(0);
  const [sel, setSel] = React.useState<any>(null);
  const jobs = orders.filter(o => o.address);
  const missing = jobs.filter(o => !o.geo);

  React.useEffect(() => {
    let dead = false; let m: any = null;
    ensureLeaflet().then((L: any) => {
      if (dead || !mapEl.current) return;
      m = L.map(mapEl.current, { zoomControl: true, attributionControl: false }).setView([44.5213, -88.0241], 10);
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", { maxZoom: 20 }).addTo(m);
      mapRef.current = m;
      setReady(true);
      setTimeout(() => m.invalidateSize(), 60);
    });
    return () => { dead = true; if (m) m.remove(); mapRef.current = null; };
  }, []);

  // geocode the jobs that don't have coordinates yet — through the SAME shared
  // throttled queue the weather chips use, so background lookups never race
  // each other or the address search for the geocoder's rate limit.
  React.useEffect(() => {
    if (!missing.length) return;
    let stop = false;
    const queue = missing.slice(0, 25);   // stay polite to the free geocoder
    setLocating(queue.length);
    (async () => {
      for (const wo of queue) {
        const g = await throttledGeocode(wo.address);
        if (stop) return;
        if (g && g.lat != null) store.updateWorkOrder(wo.id, { geo: { lat: g.lat, lng: g.lng } });
        else if (g === null) store.updateWorkOrder(wo.id, { geo: { failed: true } });   // don't retry a dud every mount
        setLocating(n => Math.max(0, n - 1));
      }
    })();
    return () => { stop = true; };
  }, [missing.map(o => o.id).join(",")]);

  // (re)draw pins whenever geo data or the job list changes
  React.useEffect(() => {
    const L = (window as any).L, m = mapRef.current;
    if (!ready || !L || !m) return;
    pinsRef.current.forEach(p => m.removeLayer(p)); pinsRef.current = [];
    const located = jobs.filter(o => o.geo && o.geo.lat != null);
    located.forEach(wo => {
      const st = STAGE(wo.status);
      const pin = L.circleMarker([wo.geo.lat, wo.geo.lng], { radius: 9, color: "#06121a", weight: 2, fillColor: st.color, fillOpacity: 0.95 })
        .addTo(m)
        .bindPopup(`<b>${(wo.customer || wo.title || "Job")}</b><br>${wo.address || ""}<br>${WO_money0(wo.total || 0)} · ${st.label}${wo.scheduledStart ? " · " + fmtDate(wo.scheduledStart) : ""}`);
      pin.on("click", () => setSel(wo.id));
      pinsRef.current.push(pin);
    });
    if (located.length) m.fitBounds(located.map(o => [o.geo.lat, o.geo.lng]), { padding: [50, 50], maxZoom: 15 });
  }, [ready, orders]);

  /* ---- daily crew route: shortest nearest-neighbor chain over the day's
     located jobs (tries every start, keeps the shortest). Drive time is a
     flat ~28 mph estimate — good enough to order the day. ---- */
  const [routeDate, setRouteDate] = React.useState<any>(() => localYmd());
  const [routeCrew, setRouteCrew] = React.useState<any>("");
  const route = React.useMemo(() => {
    const day = routeDate;
    const todays = orders.filter(o => o.geo && o.geo.lat != null && o.scheduledStart && o.status !== "completed"
      && o.scheduledStart <= day && (o.scheduledEnd || o.scheduledStart) >= day
      && (!routeCrew || o.crewId === routeCrew));
    const R = 3958.8, rad = Math.PI / 180;
    const dist = (a, b) => { const dLa = (b.geo.lat - a.geo.lat) * rad, dLn = (b.geo.lng - a.geo.lng) * rad, la1 = a.geo.lat * rad, la2 = b.geo.lat * rad; const h = Math.pow(Math.sin(dLa / 2), 2) + Math.cos(la1) * Math.cos(la2) * Math.pow(Math.sin(dLn / 2), 2); return 2 * R * Math.asin(Math.sqrt(h)); };
    if (todays.length < 2) return { jobs: todays, legs: [], totalMi: 0 };
    let best: any = null;
    for (let s = 0; s < todays.length; s++) {
      const left = todays.slice(); const chain = [left.splice(s, 1)[0]]; let total = 0;
      while (left.length) {
        let bi = 0, bd = Infinity;
        left.forEach((j, i) => { const d = dist(chain[chain.length - 1], j); if (d < bd) { bd = d; bi = i; } });
        total += bd; chain.push(left.splice(bi, 1)[0]);
      }
      if (!best || total < best.total) best = { chain, total };
    }
    const legs = best.chain.slice(1).map((j, i) => ({ mi: dist(best.chain[i], j) }));
    return { jobs: best.chain, legs, totalMi: best.total };
  }, [orders, routeDate, routeCrew]);
  const routeLine = React.useRef<any>(null);
  React.useEffect(() => {
    const L = (window as any).L, m = mapRef.current;
    if (!ready || !L || !m) return;
    if (routeLine.current) { m.removeLayer(routeLine.current); routeLine.current = null; }
    if (route.jobs.length >= 2) routeLine.current = L.polyline(route.jobs.map(j => [j.geo.lat, j.geo.lng]), { color: "#f5c451", weight: 3, dashArray: "8 6", opacity: 0.9 }).addTo(m);
    return () => { if (routeLine.current && m) { try { m.removeLayer(routeLine.current); } catch (e) {} routeLine.current = null; } };
  }, [ready, route]);
  const driveMin = Math.round((route.totalMi / 28) * 60);
  const gmaps = route.jobs.length >= 2 ? "https://www.google.com/maps/dir/" + route.jobs.map(j => j.geo.lat + "," + j.geo.lng).join("/") : null;

  const selWo = sel ? orders.find(o => o.id === sel) : null;
  return (
    <div className="card pad" style={{ marginTop: 18 }}>
      <div className="row between" style={{ marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <span className="row gap-8" style={{ flexWrap: "wrap" }}>
          {WO_STAGES.map(s => <span key={s.id} className="row gap-4" style={{ fontSize: 11.5, color: "var(--fg-3)" }}><span className="wo-pipe-dot" style={{ background: s.color }} />{s.short}</span>)}
        </span>
        <span className="mono faint" style={{ fontSize: 11 }}>
          {jobs.filter(o => o.geo && o.geo.lat != null).length}/{jobs.length} jobs located
          {locating > 0 ? ` · locating ${locating}…` : ""}
          {orders.length - jobs.length > 0 ? ` · ${orders.length - jobs.length} missing an address` : ""}
        </span>
      </div>
      <div ref={mapEl} style={{ height: 520, borderRadius: 12, overflow: "hidden", border: "1px solid var(--line)" }} />
      {/* daily crew route — order the day's stops, gold dashed line on the map */}
      <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 10, background: "var(--surface-2)", border: "1px solid var(--line)" }}>
        <div className="row between" style={{ flexWrap: "wrap", gap: 8 }}>
          <span className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
            <b style={{ fontSize: 13 }}>Crew route</b>
            <input className="input" type="date" style={{ width: 140, padding: "5px 8px" }} value={routeDate} onChange={e => setRouteDate(e.target.value)} />
            <select className="select" style={{ width: 150, padding: "5px 8px" }} value={routeCrew} onChange={e => setRouteCrew(e.target.value)}>
              <option value="">All crews</option>
              {crews.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </span>
          {route.jobs.length >= 2
            ? <span className="row gap-8" style={{ alignItems: "center" }}>
                <span className="mono" style={{ fontSize: 11.5, color: "var(--fg-2)" }}>{route.jobs.length} stops · {route.totalMi.toFixed(1)} mi · ~{driveMin} min drive</span>
                {gmaps && <a className="btn btn-outline btn-sm" href={gmaps} target="_blank" rel="noopener"><Icon name="map" size={12} />Open route in Google Maps</a>}
              </span>
            : <span className="faint" style={{ fontSize: 11.5 }}>{route.jobs.length === 1 ? "One stop this day — no routing needed" : "No located jobs scheduled this day"}</span>}
        </div>
        {route.jobs.length >= 2 && (
          <div className="row gap-8" style={{ flexWrap: "wrap", marginTop: 8 }}>
            {route.jobs.map((j, i) => (
              <span key={j.id} className="chip" style={{ fontSize: 11, cursor: "pointer" }} onClick={() => setSel(j.id)} title={j.address}>
                <span style={{ display: "inline-grid", placeItems: "center", width: 15, height: 15, borderRadius: 99, background: "#f5c451", color: "#06121a", fontWeight: 800, fontSize: 9.5 }}>{i + 1}</span>
                {j.customer || j.title}
                {i < route.legs.length && <span className="faint" style={{ fontSize: 10 }}>→ {route.legs[i].mi.toFixed(1)} mi</span>}
              </span>
            ))}
          </div>
        )}
      </div>
      {selWo && (
        <div className="row between" style={{ marginTop: 10, padding: "10px 12px", borderRadius: 10, background: "var(--surface-2)", border: "1px solid var(--line)", flexWrap: "wrap", gap: 8 }}>
          <span className="row gap-8" style={{ minWidth: 0 }}>
            <span className="wo-pipe-dot" style={{ background: STAGE(selWo.status).color }} />
            <b style={{ fontSize: 13.5 }}>{selWo.customer || selWo.title}</b>
            <span className="muted" style={{ fontSize: 12.5 }}>{selWo.address}</span>
          </span>
          <span className="row gap-8">
            <span className="display tnum" style={{ fontSize: 15, color: "var(--teal)" }}>{WO_money0(selWo.total || 0)}</span>
            <button className="btn btn-outline btn-sm" onClick={() => onOpen(selWo)}>Open job</button>
          </span>
        </div>
      )}
    </div>
  );
}

Object.assign(window, { WorkOrders, WorkOrderCard, FollowUp, SiteReadiness, WorkOrderPhotos, TimeTracking, JobsSheet, JobsMap });

export { crewRate, woLaborCost, crewDays, repCommission, woPL, woPayStatus, addDaysStr, fmtDate, woBOM, gcalUrl, downloadIcs, WorkOrders, ManualWorkOrder, WorkOrderCard, timeAgo, FollowUp, SiteReadiness, WorkOrderPhotos, TimeTracking, woTimeLogs, actualHours, woEstLabor, WO_money0, WO_STAGES, STAGE, throttledGeocode };
